import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';
import './App.css';
import './DashboardTheme.css';
import { PROFESSION_CATEGORIES, DIFFICULTY_LEVELS, PE_POINTS_OPTIONS, LANGUAGES, API_BASE_URL } from './constants';
import { Articles, ArticleReader, TestInterface, NewsUpdatesPage } from './Components';
import AddressAutocomplete from './components/AddressAutocomplete';
import PricingPage from './components/PricingPage';
import PasswordReset from './components/PasswordReset';
import { SUBSCRIPTION_PLANS } from './config/stripe';
import SubscriptionRequired from './components/SubscriptionRequired';
import CpdLandingPage from './components/CpdLandingPage';

// Constants
const API_URL = API_BASE_URL + '/api';

// Debounce hook for performance optimization
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Whether a user may use the platform right now.
//
// Access outlives the Stripe status: someone who cancels a yearly plan has paid
// through the end of that year and keeps access until then. The backend decides
// this and sends has_access; the status check is only a fallback for user
// objects cached in localStorage before that field existed.
const hasPlatformAccess = (user) => {
  if (!user) return false;
  if (user.is_admin) return true;
  if (typeof user.has_access === 'boolean') return user.has_access;
  return user.subscription_status === 'active';
};

// List of countries for dropdowns
const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria',
  'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
  'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon',
  'Canada', 'Cape Verde', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica',
  'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'East Timor', 'Ecuador',
  'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon',
  'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana',
  'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel',
  'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'North Korea', 'South Korea', 'Kuwait',
  'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg',
  'Macedonia', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius',
  'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia',
  'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'Norway', 'Oman', 'Pakistan',
  'Palau', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania',
  'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal',
  'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'Spain',
  'Sri Lanka', 'Sudan', 'Suriname', 'Swaziland', 'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania',
  'Thailand', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine',
  'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam', 'Yemen',
  'Zambia', 'Zimbabwe'
];

// Country codes for phone numbers
const COUNTRY_CODES = [
  { country: 'Afghanistan', code: '+93' },
  { country: 'Albania', code: '+355' },
  { country: 'Algeria', code: '+213' },
  { country: 'Andorra', code: '+376' },
  { country: 'Angola', code: '+244' },
  { country: 'Argentina', code: '+54' },
  { country: 'Armenia', code: '+374' },
  { country: 'Australia', code: '+61' },
  { country: 'Austria', code: '+43' },
  { country: 'Azerbaijan', code: '+994' },
  { country: 'Bahrain', code: '+973' },
  { country: 'Bangladesh', code: '+880' },
  { country: 'Belarus', code: '+375' },
  { country: 'Belgium', code: '+32' },
  { country: 'Belize', code: '+501' },
  { country: 'Benin', code: '+229' },
  { country: 'Bhutan', code: '+975' },
  { country: 'Bolivia', code: '+591' },
  { country: 'Bosnia and Herzegovina', code: '+387' },
  { country: 'Botswana', code: '+267' },
  { country: 'Brazil', code: '+55' },
  { country: 'Brunei', code: '+673' },
  { country: 'Bulgaria', code: '+359' },
  { country: 'Burkina Faso', code: '+226' },
  { country: 'Burundi', code: '+257' },
  { country: 'Cambodia', code: '+855' },
  { country: 'Cameroon', code: '+237' },
  { country: 'Canada', code: '+1' },
  { country: 'Cape Verde', code: '+238' },
  { country: 'Central African Republic', code: '+235' },
  { country: 'Chad', code: '+235' },
  { country: 'Chile', code: '+56' },
  { country: 'China', code: '+86' },
  { country: 'Colombia', code: '+57' },
  { country: 'Comoros', code: '+269' },
  { country: 'Congo', code: '+242' },
  { country: 'Costa Rica', code: '+506' },
  { country: 'Croatia', code: '+385' },
  { country: 'Cuba', code: '+53' },
  { country: 'Cyprus', code: '+357' },
  { country: 'Czech Republic', code: '+420' },
  { country: 'Denmark', code: '+45' },
  { country: 'Djibouti', code: '+253' },
  { country: 'Dominican Republic', code: '+1' },
  { country: 'Ecuador', code: '+593' },
  { country: 'Egypt', code: '+20' },
  { country: 'El Salvador', code: '+503' },
  { country: 'Estonia', code: '+372' },
  { country: 'Ethiopia', code: '+251' },
  { country: 'Fiji', code: '+679' },
  { country: 'Finland', code: '+358' },
  { country: 'France', code: '+33' },
  { country: 'Gabon', code: '+241' },
  { country: 'Gambia', code: '+220' },
  { country: 'Georgia', code: '+995' },
  { country: 'Germany', code: '+49' },
  { country: 'Ghana', code: '+233' },
  { country: 'Greece', code: '+30' },
  { country: 'Guatemala', code: '+502' },
  { country: 'Guinea', code: '+224' },
  { country: 'Guyana', code: '+592' },
  { country: 'Haiti', code: '+509' },
  { country: 'Honduras', code: '+504' },
  { country: 'Hungary', code: '+36' },
  { country: 'Iceland', code: '+354' },
  { country: 'India', code: '+91' },
  { country: 'Indonesia', code: '+62' },
  { country: 'Iran', code: '+98' },
  { country: 'Iraq', code: '+964' },
  { country: 'Ireland', code: '+353' },
  { country: 'Israel', code: '+972' },
  { country: 'Italy', code: '+39' },
  { country: 'Jamaica', code: '+1' },
  { country: 'Japan', code: '+81' },
  { country: 'Jordan', code: '+962' },
  { country: 'Kazakhstan', code: '+7' },
  { country: 'Kenya', code: '+254' },
  { country: 'South Korea', code: '+82' },
  { country: 'Kuwait', code: '+965' },
  { country: 'Kyrgyzstan', code: '+996' },
  { country: 'Laos', code: '+856' },
  { country: 'Latvia', code: '+371' },
  { country: 'Lebanon', code: '+961' },
  { country: 'Lesotho', code: '+266' },
  { country: 'Liberia', code: '+231' },
  { country: 'Libya', code: '+218' },
  { country: 'Liechtenstein', code: '+423' },
  { country: 'Lithuania', code: '+370' },
  { country: 'Luxembourg', code: '+352' },
  { country: 'Madagascar', code: '+261' },
  { country: 'Malawi', code: '+265' },
  { country: 'Malaysia', code: '+60' },
  { country: 'Maldives', code: '+960' },
  { country: 'Mali', code: '+223' },
  { country: 'Malta', code: '+356' },
  { country: 'Mauritania', code: '+222' },
  { country: 'Mauritius', code: '+230' },
  { country: 'Mexico', code: '+52' },
  { country: 'Moldova', code: '+373' },
  { country: 'Monaco', code: '+377' },
  { country: 'Mongolia', code: '+976' },
  { country: 'Montenegro', code: '+382' },
  { country: 'Morocco', code: '+212' },
  { country: 'Mozambique', code: '+258' },
  { country: 'Myanmar', code: '+95' },
  { country: 'Namibia', code: '+264' },
  { country: 'Nepal', code: '+977' },
  { country: 'Netherlands', code: '+31' },
  { country: 'New Zealand', code: '+64' },
  { country: 'Nicaragua', code: '+505' },
  { country: 'Niger', code: '+227' },
  { country: 'Nigeria', code: '+234' },
  { country: 'Norway', code: '+47' },
  { country: 'Oman', code: '+968' },
  { country: 'Pakistan', code: '+92' },
  { country: 'Panama', code: '+507' },
  { country: 'Papua New Guinea', code: '+675' },
  { country: 'Paraguay', code: '+595' },
  { country: 'Peru', code: '+51' },
  { country: 'Philippines', code: '+63' },
  { country: 'Poland', code: '+48' },
  { country: 'Portugal', code: '+351' },
  { country: 'Qatar', code: '+974' },
  { country: 'Romania', code: '+40' },
  { country: 'Russia', code: '+7' },
  { country: 'Rwanda', code: '+250' },
  { country: 'Saudi Arabia', code: '+966' },
  { country: 'Senegal', code: '+221' },
  { country: 'Serbia', code: '+381' },
  { country: 'Seychelles', code: '+248' },
  { country: 'Sierra Leone', code: '+232' },
  { country: 'Singapore', code: '+65' },
  { country: 'Slovakia', code: '+421' },
  { country: 'Slovenia', code: '+386' },
  { country: 'Somalia', code: '+252' },
  { country: 'South Africa', code: '+27' },
  { country: 'Spain', code: '+34' },
  { country: 'Sri Lanka', code: '+94' },
  { country: 'Sudan', code: '+249' },
  { country: 'Suriname', code: '+597' },
  { country: 'Sweden', code: '+46' },
  { country: 'Switzerland', code: '+41' },
  { country: 'Syria', code: '+963' },
  { country: 'Taiwan', code: '+886' },
  { country: 'Tajikistan', code: '+992' },
  { country: 'Tanzania', code: '+255' },
  { country: 'Thailand', code: '+66' },
  { country: 'Togo', code: '+228' },
  { country: 'Tunisia', code: '+216' },
  { country: 'Turkey', code: '+90' },
  { country: 'Turkmenistan', code: '+993' },
  { country: 'Uganda', code: '+256' },
  { country: 'Ukraine', code: '+380' },
  { country: 'United Arab Emirates', code: '+971' },
  { country: 'United Kingdom', code: '+44' },
  { country: 'United States', code: '+1' },
  { country: 'Uruguay', code: '+598' },
  { country: 'Uzbekistan', code: '+998' },
  { country: 'Venezuela', code: '+58' },
  { country: 'Vietnam', code: '+84' },
  { country: 'Yemen', code: '+967' },
  { country: 'Zambia', code: '+260' },
  { country: 'Zimbabwe', code: '+263' }
];

// AI Assistant Component - separate from main app to prevent re-render issues
const AIAssistant = ({ user }) => {
  const [aiQuestion, setAiQuestion] = useState('');
  const [aiAnswer, setAiAnswer] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [recentQuestions, setRecentQuestions] = useState([]);

  // Simple markdown to HTML converter for AI responses
  const formatResponse = (text) => {
    if (!text) return '';
    
    return text
      .replace(/###\s(.+)/g, '<h4>$1</h4>')
      .replace(/##\s(.+)/g, '<h3>$1</h3>') 
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>')
      .replace(/^/, '<p>')
      .replace(/$/, '</p>')
      .replace(/(<p><\/p>)/g, '');
  };

  // Handle AI Assistant questions
  const handleAiQuestion = useCallback(async (question) => {
    if (!question.trim()) {
      setAiError('Please enter a question');
      return;
    }

    setAiLoading(true);
    setAiError('');
    setAiAnswer('');

    try {
      const response = await fetch(`${API_URL}/ai-assistant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ question: question.trim() })
      });

      const data = await response.json();

      if (response.ok) {
        setAiAnswer(data.answer);
        // Add to recent questions (keep last 5)
        setRecentQuestions(prev => [
          { question: question.trim(), answer: data.answer, timestamp: Date.now() },
          ...prev.slice(0, 4)
        ]);
        setAiQuestion(''); // Clear the input
      } else {
        setAiError(data.error || 'Failed to get answer');
      }
    } catch (error) {
      console.error('Error asking AI:', error);
      setAiError('Network error. Please try again.');
    } finally {
      setAiLoading(false);
    }
  }, []);

  // Stable onChange handler for AI question input
  const handleAiQuestionChange = useCallback((e) => {
    setAiQuestion(e.target.value);
  }, []);

  // i18n for AI assistant component
  const { t } = useTranslation();

  return (
    <div className="ai-assistant card">
      <div className="ai-header">
        <h4>{t('dashboard.aiAssistant.title', 'Professional AI Assistant')}</h4>
        <p>{t('dashboard.aiAssistant.subtitle', 'Get detailed explanations of concepts, terms, and topics in your field')}</p>
      </div>
      
      <div className="ai-content-layout">
        <div className="ai-input-section">
          <textarea
            value={aiQuestion}
            onChange={handleAiQuestionChange}
            placeholder={t('dashboard.aiAssistant.placeholder', 'Ask about professional topics like: methods, standards, terminology, best practices, regulations, etc.')}
            maxLength={500}
            rows={6}
            className="ai-question-input"
            disabled={aiLoading}
          />
          <div className="ai-input-footer">
            <span className="character-count">{aiQuestion.length}/500</span>
            <button 
              onClick={() => handleAiQuestion(aiQuestion)}
              disabled={aiLoading || !aiQuestion.trim()}
              className="ai-ask-btn"
            >
              {aiLoading ? t('thinking', 'Thinking...') : t('ask', 'Ask')}
            </button>
          </div>

          {aiError && (
            <div className="ai-error">
              ⚠️ {aiError}
            </div>
          )}
        </div>

        <div className="ai-response-section">
          {aiAnswer ? (
            <div className="ai-answer">
              <div className="ai-answer-header">
                <span className="ai-icon">🤖</span>
                <span>AI Assistant</span>
              </div>
              <div 
                className="ai-answer-content"
                dangerouslySetInnerHTML={{ __html: formatResponse(aiAnswer) }}
              />
            </div>
          ) : (
            <div className="ai-placeholder">
              <div className="ai-placeholder-icon">💡</div>
              <p>{t('dashboard.aiAssistant.ask', 'Ask a question to get a professional explanation')}</p>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .ai-assistant {
          background: var(--card-bg);
          border-radius: var(--border-radius);
          box-shadow: var(--card-shadow);
          overflow: hidden;
        }

        .ai-header {
          padding: 1.5rem 1.5rem 1rem 1.5rem;
          border-bottom: 1px solid var(--border-color);
          background: white;
        }

        .ai-header h4 {
          margin: 0 0 0.5rem 0;
          font-size: 1.2rem;
          font-weight: 600;
          color: var(--primary-text);
        }

        .ai-header p {
          margin: 0;
          font-size: 0.9rem;
          color: var(--secondary-text);
          line-height: 1.4;
        }

        .ai-content-layout {
          display: flex;
          gap: 1.5rem;
          padding: 1.5rem;
          min-height: 200px;
        }

        .ai-input-section {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .ai-response-section {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .ai-question-input {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius);
          font-size: 0.9rem;
          line-height: 1.4;
          resize: vertical;
          min-height: 120px;
          flex: 1;
          transition: border-color 0.2s;
          font-family: inherit;
        }

        .ai-question-input:focus {
          outline: none;
          border-color: var(--accent-green);
          box-shadow: 0 0 0 2px rgba(25, 135, 84, 0.1);
        }

        .ai-question-input:disabled {
          background-color: #f8f9fa;
          cursor: not-allowed;
        }

        .ai-input-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 0.75rem;
        }

        .character-count {
          font-size: 0.8rem;
          color: var(--secondary-text);
        }

        .ai-ask-btn {
          background-color: var(--accent-green);
          color: var(--light-text);
          border: none;
          padding: 0.6rem 1.5rem;
          border-radius: var(--border-radius);
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 0.9rem;
        }

        .ai-ask-btn:hover:not(:disabled) {
          background-color: var(--accent-green-hover);
          transform: translateY(-1px);
        }

        .ai-ask-btn:disabled {
          background-color: #9ca3af;
          cursor: not-allowed;
          transform: none;
        }

        .ai-error {
          margin-top: 0.75rem;
          padding: 0.75rem;
          background-color: #fef2f2;
          border: 1px solid #fca5a5;
          border-radius: var(--border-radius);
          color: #dc2626;
          font-size: 0.9rem;
        }

        .ai-answer {
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius);
          overflow: hidden;
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .ai-answer-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          background-color: #f8f9fa;
          border-bottom: 1px solid var(--border-color);
          font-weight: 500;
          color: var(--primary-text);
          font-size: 0.9rem;
        }

        .ai-icon {
          font-size: 1.1rem;
        }

        .ai-answer-content {
          padding: 1rem;
          flex: 1;
          overflow-y: auto;
          line-height: 1.6;
          font-size: 0.95rem;
          color: var(--primary-text);
          background-color: white;
          max-height: 450px;
        }

        .ai-answer-content::-webkit-scrollbar {
          width: 6px;
        }

        .ai-answer-content::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }

        .ai-answer-content::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }

        .ai-answer-content::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }

        .ai-answer-content p {
          margin: 0 0 1rem 0;
          line-height: 1.6;
        }

        .ai-answer-content p:last-child {
          margin-bottom: 0;
        }

        .ai-answer-content h3 {
          margin: 1.5rem 0 0.75rem 0;
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--dark-bg);
          line-height: 1.3;
        }

        .ai-answer-content h3:first-child {
          margin-top: 0;
        }

        .ai-answer-content h4 {
          margin: 1.25rem 0 0.6rem 0;
          font-size: 1rem;
          font-weight: 600;
          color: var(--primary-text);
          line-height: 1.3;
        }

        .ai-answer-content h4:first-child {
          margin-top: 0;
        }

        .ai-answer-content strong {
          color: var(--dark-bg);
          font-weight: 600;
        }

        .ai-answer-content em {
          color: var(--secondary-text);
          font-style: italic;
        }

        .ai-placeholder {
          border: 2px dashed var(--border-color);
          border-radius: var(--border-radius);
          padding: 2rem;
          text-align: center;
          color: var(--secondary-text);
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        .ai-placeholder-icon {
          font-size: 2rem;
          margin-bottom: 0.5rem;
          opacity: 0.7;
        }

        .ai-placeholder p {
          margin: 0;
          font-size: 0.9rem;
        }

        @media (max-width: 768px) {
          .ai-content-layout {
            flex-direction: column;
            gap: 1rem;
            padding: 1rem;
          }

          .ai-header {
            padding: 1rem;
          }

          .ai-question-input {
            font-size: 16px; // Prevents zoom on iOS
            min-height: 100px;
          }

          .ai-answer-content {
            font-size: 0.9rem;
          }
        }
      `}</style>
    </div>
  );
};
function MainApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { t, i18n } = useTranslation();

  // Always scroll to the top of the page when navigating to a new route,
  // so clicking links (e.g. "See how it works") lands at the top of the tab.
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch (_) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);
  
  const [articles, setArticles] = useState([]);
  const [objectives, setObjectives] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('objectives') || '[]'); } catch { return []; }
  });
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedLanguage, setSelectedLanguage] = useState('All');
  const [selectedSource, setSelectedSource] = useState('All');
  const [selectedSort, setSelectedSort] = useState('newest');
  const [loading, setLoading] = useState(false);
  const [currentView, setCurrentView] = useState('home'); // 'home', 'articles', 'article', 'test', 'login', 'register', 'account', 'help'
  const [selectedArticle, setSelectedArticle] = useState(null);
  
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Test state
  const [testQuestions, setTestQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [testResults, setTestResults] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [completedArticles, setCompletedArticles] = useState([]);
  const [completionStatuses, setCompletionStatuses] = useState({});

  // Email Confirmation state
  const [status, setStatus] = useState('confirming'); // confirming, success, error
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [confirmationToken, setConfirmationToken] = useState(null);

  // Add news state
  const [newsItems, setNewsItems] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);

  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [discountValidation, setDiscountValidation] = useState(null);
  const [isValidatingDiscount, setIsValidatingDiscount] = useState(false);
  const [monthlyAllowance, setMonthlyAllowance] = useState({ is_monthly: false, available: null, capacity: null });

  // Memoized debounced values for performance
  const debouncedDiscountCode = useDebounce(discountCode, 300);

  // Memoized auth check function
  const checkAuthStatus = useCallback(async (token) => {
    try {
      const response = await fetch(`${API_URL}/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        console.log('checkAuthStatus profile data:', userData);
        setUser(userData);
        setIsAuthenticated(true);
        
        // Admin users bypass all subscription checks
        if (userData.is_admin) {
          console.log('Admin user detected in checkAuthStatus, bypassing subscription checks');
          return; // Exit early for admin users
        }
        
        // Check if user needs to complete payment after auth check
        const pendingPlan = localStorage.getItem('pendingPlan');
        const hasConfirmedEmail = userData.email_confirmed;
        const hasActiveSubscription = hasPlatformAccess(userData);
        
        // If user has confirmed email but no active subscription, redirect to payment
        if (hasConfirmedEmail && !hasActiveSubscription) {
          // Set default plan if none exists
          if (!pendingPlan) {
            localStorage.setItem('pendingPlan', '');
          }
          
          // Don't redirect immediately here to avoid conflicts with navigation
          // The login form will handle the redirection
        }
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } finally {
      setAuthLoading(false);
    }
  }, []);

  // Memoized fetch news function
  const fetchNews = useCallback(async () => {
    try {
      setNewsLoading(true);
      const response = await fetch(`${API_URL}/news`);
      if (response.ok) {
        const data = await response.json();
        setNewsItems(data);
      } else {
        console.error('Failed to fetch news');
      }
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setNewsLoading(false);
    }
  }, []);

  // Memoized fetch articles function
  const fetchArticles = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/articles`;
      
      // Add pagination parameters (start with reasonable defaults)
      const urlParams = new URLSearchParams();
      urlParams.append("page", "1");
      urlParams.append("per_page", "100"); // Load first 100 articles
      
      if (selectedLanguage && selectedLanguage !== "All") {
        urlParams.append("language", encodeURIComponent(selectedLanguage));
      }
      
      url += `?${urlParams.toString()}`;
      console.log('🔍 Fetching articles from URL:', url);
      
      const response = await fetch(url);
      console.log('📡 API Response status:', response.status, response.ok);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📦 Raw API response:', data);
        
        // Handle the new paginated response format
        const articlesData = data.articles || data; // Backward compatibility
        console.log('📚 Articles data to set:', articlesData, 'Length:', articlesData?.length);
        setArticles(articlesData);
        
        // If user is authenticated, also fetch completion data
        if (isAuthenticated) {
          await fetchCompletionData(articlesData);
        }
      } else {
        console.error('Failed to fetch articles');
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedLanguage, isAuthenticated]);

  // Memoized fetch completion data function
  const fetchCompletionData = useCallback(async (articlesList = articles) => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('token');
      
      // Fetch completed articles for certificates
      const completionsResponse = await fetch(`${API_URL}/users/${user.id}/article-completions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (completionsResponse.ok) {
        const completions = await completionsResponse.json();
        setCompletedArticles(completions);
      }
      
      // OPTIMIZATION: Use bulk endpoint instead of individual requests (N+1 problem fix)
      // Extract article IDs for the bulk request
      const articleIds = articlesList.map(article => article.id);
      const queryParams = articleIds.length > 0 ? `?article_ids=${articleIds.join("&article_ids=")}` : "";
      
      const statusResponse = await fetch(`${API_URL}/users/${user.id}/completion-statuses${queryParams}`, {
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("token")}`
        }
      });
      
      if (statusResponse.ok) {
        const statusesData = await statusResponse.json();
        // Convert to the format expected by the rest of the app
        const statusesObj = {};
        Object.entries(statusesData).forEach(([articleId, status]) => {
          statusesObj[parseInt(articleId)] = status;
        });
        setCompletionStatuses(statusesObj);
      }
      
    } catch (error) {
      console.error('Error fetching completion data:', error);
    }
  }, [user]);

  // Memoized categories list
  const categories = useMemo(() => {
    return ['All', ...new Set(articles.map(article => article.category))];
  }, [articles]);

  // Memoized filtered articles
  const filteredArticles = useMemo(() => {
    return selectedCategory === 'All' 
      ? articles 
      : articles.filter(article => article.category === selectedCategory);
  }, [articles, selectedCategory]);

  // Memoized user test permissions
  const canUserTakeTests = useCallback((user) => {
    if (!user) return false;
    
    // Admin users can always take tests
    if (user.is_admin) return true;
    
    // Check if the user has access (active, or inside a period they paid for)
    if (!hasPlatformAccess(user)) {
      return false;
    }
    
    // Check if user has Professional plan (can take tests)
    const planRaw = (user.subscription_plan ?? '').toString().trim();
    const planLower = planRaw.toLowerCase();
    const professionalPlansLower = new Set([
      'professional',
      'professional monthly',
      'professional_monthly',
      'professionalmonthly',
      'pro_monthly',
      'pro-monthly',
      // Stripe IDs that may be stored instead of names
      'price_1s16m9ikwuvtcv9mkuoqxaua', // Professional yearly price ID
      'price_1s4ljtikwuvtcv9muyhtspin', // Professional monthly price ID
      'prod_sewzwtoox1xoog',            // Professional product ID
    ]);

    return professionalPlansLower.has(planLower);
  }, []);

  useEffect(() => {
    const fetchAllowance = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch(`${API_URL}/monthly-allowance`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setMonthlyAllowance(data);
        }
      } catch {}
    };
    fetchAllowance();
  }, [user?.subscription_plan, user?.subscription_status, user?.has_access]);

  // Handle discount code validation
  const handleDiscountCode = useCallback(async (code) => {
    if (!code.trim()) {
      setDiscountValidation(null);
      return;
    }

    setIsValidatingDiscount(true);
    try {
      const response = await fetch(`${API_URL}/validate-discount-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          code: code.trim(),
          user_id: user?.id,
          plan_id: 'price_1TrMOMIkwUVtcv9M3cZ3qhGX', // Always validate against Professional price
          amount: 19999 // €199.99 in cents
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setDiscountValidation({
          valid: true,
          discount: data.discount_amount,
          finalAmount: data.final_amount,
          percentage: data.discount_percentage
        });
      } else {
        setDiscountValidation({
          valid: false,
          error: data.error
        });
      }
    } catch (error) {
      console.error('Error validating discount code:', error);
      setDiscountValidation({
        valid: false,
        error: 'Failed to validate discount code'
      });
    } finally {
      setIsValidatingDiscount(false);
    }
  }, [user?.id]);

  // Trigger discount code validation when debounced value changes

  const formatPrice = (cents) => {
    return `€${(cents / 100).toFixed(2)}`;
  };

  // Discount code helper functions for UI components
  const handleDiscountCodeChange = (e) => {
    setDiscountCode(e.target.value);
    if (discountValidation) {
      setDiscountValidation(null);
    }
    setError('');
  };

  const validateDiscountCode = async () => {
    if (!discountCode.trim()) {
      setDiscountValidation(null);
      return;
    }

    setIsValidatingDiscount(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/validate-discount-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          code: discountCode.trim(),
          user_id: user?.id,
          plan_id: 'price_1TrMOMIkwUVtcv9M3cZ3qhGX', // Professional plan
          amount: 19999 // €199.99 in cents
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setDiscountValidation({
          valid: true,
          code: discountCode.trim(),
          discount_amount: data.discount_amount,
          final_amount: data.final_amount,
          original_amount: data.original_amount || 19999,
          discount_percentage: data.discount_percentage
        });
        setError('');
      } else {
        setDiscountValidation(null);
        setError(data.error || 'Invalid discount code');
      }
    } catch (error) {
      console.error('Error validating discount code:', error);
      setDiscountValidation(null);
      setError('Failed to validate discount code. Please try again.');
    } finally {
      setIsValidatingDiscount(false);
    }
  };

  const handleUpgradeWithDiscount = async () => {
    setUpgradeLoading(true);
    setError('');

    try {
      // Prepare request body based on validation state
      const requestBody = {
        userId: user.id,
        priceId: 'price_1TrMOMIkwUVtcv9M3cZ3qhGX', // Professional plan price ID
          planType: 'PROFESSIONAL' // Upgrading from Professional Insights to Professional
      };

      // Add discount code if validated
      if (discountValidation && discountValidation.valid) {
        requestBody.discountCode = discountCode;
      }

      const response = await fetch(`${API_URL}/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      
      if (response.ok && data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || 'Failed to initiate payment. Please try again.');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setError('Failed to initiate payment. Please try again.');
    } finally {
      setUpgradeLoading(false);
    }
  };
  // Add missing upgradeLoading state
  const [upgradeLoading, setUpgradeLoading] = useState(false);

  const UserDashboard = React.memo(() => {
    const currentYear = new Date().getFullYear();
    const yearlyRequirement = 10;
    const canTakeTests = canUserTakeTests(user);
    const [showUpgradeInfo, setShowUpgradeInfo] = useState(false);
    const [activeMonth, setActiveMonth] = useState(new Date().getMonth());
    const [activeRec, setActiveRec] = useState(null);

    // Calculate current year progress
    const currentYearCompletions = completedArticles.filter(c => c.completion_year === currentYear);
    const currentYearPoints = currentYearCompletions.reduce((sum, c) => sum + c.pe_points_awarded, 0);
    const progressPercentage = Math.min((currentYearPoints / yearlyRequirement) * 100, 100);

    // Prepare data for points history chart
    const pointsByYear = completedArticles.reduce((acc, curr) => {
      const year = curr.completion_year;
      acc[year] = (acc[year] || 0) + curr.pe_points_awarded;
      return acc;
    }, {});
    const chartData = Object.entries(pointsByYear)
      .sort(([yearA], [yearB]) => parseInt(yearA) - parseInt(yearB))
      .map(([year, points]) => ({ year, points }));
    const maxChartPoints = Math.max(...chartData.map(d => d.points), 1);

    // Enhanced monthly data for current year
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Get monthly breakdown for current year
    const monthlyData = completedArticles
      .filter(article => article.completion_year === currentYear)
      .reduce((acc, curr) => {
        if (curr.completed_at) {
          const month = new Date(curr.completed_at).getMonth();
          acc[month] = (acc[month] || 0) + curr.pe_points_awarded;
        }
        return acc;
      }, {});

    const monthlyChartData = monthNames.map((month, index) => ({
      month,
      points: monthlyData[index] || 0,
      monthIndex: index
    }));

    const maxMonthlyPoints = Math.max(...monthlyChartData.map(d => d.points), 1);
    const totalCurrentYearPoints = monthlyChartData.reduce((sum, d) => sum + d.points, 0);
    const averagePerMonth = totalCurrentYearPoints / (new Date().getMonth() + 1);
    const completionsThisMonth = completedArticles.filter(article => {
      if (!article.completed_at) return false;
      const articleDate = new Date(article.completed_at);
      const now = new Date();
      return articleDate.getMonth() === now.getMonth() && articleDate.getFullYear() === now.getFullYear();
    }).length;

    // Calculate trends
    const lastMonthIndex = new Date().getMonth() - 1;
    const thisMonthPoints = monthlyData[new Date().getMonth()] || 0;
    const lastMonthPoints = lastMonthIndex >= 0 ? (monthlyData[lastMonthIndex] || 0) : 0;
    const trend = thisMonthPoints >= lastMonthPoints ? 'up' : 'down';
    const trendPercentage = lastMonthPoints > 0 ? Math.abs(((thisMonthPoints - lastMonthPoints) / lastMonthPoints) * 100) : 0;

    // Get recommended articles (uncompleted ones), prioritize by user's professions
    const primaryProfession = (user?.profession || '').toLowerCase();
    const secondaryProfession = (user?.secondary_profession || '').toLowerCase();

    const scoreArticle = (article) => {
      const category = (article.category || '').toLowerCase();
      const secondary = (article.secondary_category || '').toLowerCase();
      let score = 0;
      if (primaryProfession && category === primaryProfession) score += 2;
      if (secondaryProfession && category === secondaryProfession) score += 1;
      if (primaryProfession && secondary === primaryProfession) score += 1.5;
      if (secondaryProfession && secondary === secondaryProfession) score += 0.75;
      // Slight preference for more recent publications
      if (article.publication_date) score += 0.1;
      return score;
    };

    const recommendedArticles = articles
      .filter(article => !completionStatuses[article.id]?.completed)
      .sort((a, b) => scoreArticle(b) - scoreArticle(a))
      .slice(0, 3);

    return (
      <div className="dashboard-container">
        <div className="dashboard-main-content">
          {/* Welcome Banner */}
          <div className="welcome-banner">
            <div className="welcome-text">
              <h2>{t('dashboard.welcome', 'Welcome, {{name}}', { name: user?.first_name })}</h2>
              <p>{t('dashboard.welcomeSub', 'Continue your professional development')}</p>
            </div>
            <div className="stats-cards">
              <button type="button" className={`stat-card ${!canTakeTests ? 'grayed-out' : ''}`} onClick={() => navigate('/account')}>
                <span className="stat-number">{canTakeTests ? (user?.pe_points_earned || 0) : 0}</span>
                <span className="stat-label">{t('dashboard.totalPoints', 'TOTAL POINTS')} {!canTakeTests && <span className="pro-badge-mini">PRO</span>}</span>
              </button>
              <button type="button" className={`stat-card ${!canTakeTests ? 'grayed-out' : ''}`} onClick={() => navigate('/articles')}>
                <span className="stat-number">{completedArticles.length}</span>
                <span className="stat-label">{t('dashboard.completed', 'COMPLETED')} {!canTakeTests && <span className="pro-badge-mini">PRO</span>}</span>
              </button>
              <button type="button" className={`stat-card ${!canTakeTests ? 'grayed-out' : ''}`} onClick={() => navigate('/account')}>
                <span className="stat-number">{canTakeTests ? currentYearPoints : 0}</span>
                <span className="stat-label">{currentYear} POINTS {!canTakeTests && <span className="pro-badge-mini">PRO</span>}</span>
              </button>
              <button type="button" className={`stat-card ${!canTakeTests ? 'grayed-out' : ''}`} onClick={() => navigate('/account')}>
                <span className="stat-number">{canTakeTests ? progressPercentage.toFixed(0) : 0}%</span>
                <span className="stat-label">{t('dashboard.progress', 'PROGRESS')} {!canTakeTests && <span className="pro-badge-mini">PRO</span>}</span>
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="dashboard-actions" style={{ display: 'grid', gridTemplateColumns: monthlyAllowance?.is_monthly ? '1fr 1fr 1fr' : '1fr 1fr', gap: '1rem' }}>
            <button className="dashboard-btn" onClick={() => navigate('/articles')}>{t('dashboard.browseArticles', 'BROWSE ARTICLES')}</button>
            <button className="dashboard-btn" onClick={() => navigate('/account')}>{t('dashboard.viewProgress', 'VIEW PROGRESS')}</button>
            {monthlyAllowance?.is_monthly && (
              <div className="dashboard-btn" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {(((monthlyAllowance.capacity ?? 1) - (monthlyAllowance.available ?? 0)) || 0).toFixed(2)} PE USED · {(monthlyAllowance.available ?? 0).toFixed(2)} LEFT
              </div>
            )}
          </div>

          {/* Chrome extension install banner */}
          <div className="ext-banner">
            <div className="ext-banner-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="4" />
                <path d="M12 8h9" /><path d="M5.5 6.5 9.5 12" /><path d="M14.5 15.5 10 21" />
              </svg>
            </div>
            <div className="ext-banner-text">
              <div className="ext-banner-title">{t('dashboard.extBanner.title', 'Earn CPD points anywhere with our Chrome extension')}</div>
              <div className="ext-banner-sub">{t('dashboard.extBanner.sub', 'Read any professional article in your browser and earn points — no need to leave the page. See the full step-by-step guide under Help & Support.')}</div>
            </div>
            <a
              className="ext-banner-btn"
              href="https://chromewebstore.google.com/detail/cpd-pro-%E2%80%93-article-quiz/jahjkbeffflpapclognafdfjpcphcfej"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" />
              </svg>
              {t('dashboard.extBanner.cta', 'Add to Chrome')}
            </a>
          </div>

          {/* Professional Insights Upgrade Notice */}
          {!canTakeTests && (
            <div className="professional-insights-upgrade">
              <div className="upgrade-header">
                <div className="upgrade-icon">📚</div>
                <div className="upgrade-text">
                  <h3>You're on Professional Insights</h3>
                  <p>You can read unlimited articles, but to take tests and earn CPD points, upgrade to Professional.</p>
                </div>
              </div>
              <div className="upgrade-actions">
                <button className="btn-upgrade-primary" onClick={() => navigate('/account')}>
                  Upgrade to Professional
                </button>
                <button className="btn-upgrade-secondary" onClick={() => setShowUpgradeInfo(true)}>
                  Learn More
                </button>
              </div>
            </div>
          )}

          {/* Upgrade Information Modal */}
          {showUpgradeInfo && (
            <div className="upgrade-modal-overlay" onClick={() => setShowUpgradeInfo(false)}>
              <div className="upgrade-modal" onClick={(e) => e.stopPropagation()}>
                <div className="upgrade-modal-header" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '1.5rem 2rem',
                  borderBottom: '1px solid #e9ecef'
                }}>
                  <h3 style={{ margin: 0, color: '#333', fontSize: '1.5rem', fontWeight: 600 }}>Upgrade to Professional</h3>
                  <button className="modal-close" onClick={() => setShowUpgradeInfo(false)} style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: 'var(--secondary-text)',
                    padding: '0.5rem',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }} onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'var(--primary-bg)';
                    e.target.style.color = 'var(--primary-text)';
                  }} onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = 'var(--secondary-text)';
                  }}>×</button>
                </div>
                
                <div className="upgrade-modal-content" style={{ padding: '2rem' }}>
                  <div className="plans-comparison">
                    <div className="plan-column current-plan">
                      <div className="plan-header">
                        <h4>Professional Insights</h4>
                        <div className="plan-price">Free</div>
                        <div className="plan-status">Your Current Plan</div>
                      </div>
                      <div className="plan-features">
                        <h5>What you have now:</h5>
                        <ul>
                          <li className="feature-included">✓ Read unlimited articles</li>
                          <li className="feature-included">✓ Learn across multiple professions</li>
                          <li className="feature-included">✓ All articles in one platform</li>
                          <li className="feature-included">✓ Email support</li>
                          <li className="feature-excluded">✗ No CPD points</li>
                          <li className="feature-excluded">✗ No certificates</li>
                          <li className="feature-excluded">✗ No tests</li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="plan-column upgrade-plan">
                      <div className="plan-header">
                        <h4>Professional</h4>
                        <div className="plan-price">€199.99/year (excl VAT)</div>
                        {/* Removed monthly savings label since we only offer yearly */}
                      </div>
                      <div className="plan-features">
                        <h5>Everything in Professional Insights, plus:</h5>
                        <ul>
                          <li className="feature-included">✓ Earn official CPD points</li>
                          <li className="feature-included">✓ Download accredited certificates</li>
                          <li className="feature-included">✓ Take AI-generated tests</li>
                          <li className="feature-included">✓ Track progress and certification history</li>
                          <li className="feature-included">✓ Annual billing cycle</li>
                          <li className="feature-included">✓ Priority support</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  <div className="upgrade-benefits">
                    <h4>Why upgrade to Professional?</h4>
                    <div className="benefits-grid">
                      <div className="benefit-item">
                        <div className="benefit-icon professional-icon cpd-icon">CP</div>
                        <div className="benefit-text">
                          <h5>Earn CPD Points</h5>
                          <p>Get official continuing professional development points for your learning</p>
                        </div>
                      </div>
                      <div className="benefit-item">
                        <div className="benefit-icon professional-icon cert-icon">✓</div>
                        <div className="benefit-text">
                          <h5>Accredited Certificates</h5>
                          <p>Download official certificates to prove your professional development</p>
                        </div>
                      </div>
                      <div className="benefit-item">
                        <div className="benefit-icon professional-icon test-icon">?</div>
                        <div className="benefit-text">
                          <h5>Knowledge Testing</h5>
                          <p>Take AI-generated tests to validate your understanding</p>
                        </div>
                      </div>
                      <div className="benefit-item">
                        <div className="benefit-icon professional-icon progress-icon">▲</div>
                        <div className="benefit-text">
                          <h5>Progress Tracking</h5>
                          <p>Track your learning journey and certification history</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="upgrade-cta">
                    <h4>Ready to upgrade?</h4>
                    <p>When you upgrade, your current plan will continue until the end of your billing cycle, then you'll automatically switch to Professional.</p>
                    <div className="cta-buttons">
                      <button className="btn-upgrade-cta" onClick={() => {
                        setShowUpgradeInfo(false);
                        navigate('/account');
                      }}>
                        Upgrade Now
                      </button>
                      <button className="btn-cancel-cta" onClick={() => setShowUpgradeInfo(false)}>
                        Maybe Later
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="dashboard-widgets">
            {/* Recommended Articles Section */}
            <div className="recommended-articles card">
              <div className="section-header">
                <h3>{t('dashboard.recommended', 'Recommended Articles')}</h3>
                <button className="view-all-btn" onClick={() => navigate('/articles')}>{t('dashboard.viewAll', 'View All')}</button>
              </div>
              <div className="articles-list">
                {recommendedArticles.length > 0 ? (
                  recommendedArticles.map(article => (
                    <div
                      key={article.id}
                      className={`article-card-small${(activeRec || recommendedArticles[0]?.id) === article.id ? ' is-on' : ''}`}
                      onClick={() => setActiveRec(article.id)}
                      onDoubleClick={() => navigate(`/article/${article.id}`)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/article/${article.id}`); }}
                    >
                      <div className="article-card-content">
                        <h4>{article.title}</h4>
                        <p>{article.category} • {article.pe_points_value} PE Points</p>
                      </div>
                      <button
                        type="button"
                        className="article-card-arrow"
                        onClick={(e) => { e.stopPropagation(); navigate(`/article/${article.id}`); }}
                      >→</button>
                    </div>
                  ))
                ) : (
                  <p className="empty-state">No new articles to recommend. Check back later!</p>
                )}
              </div>
            </div>

            {/* Enhanced Points History Section */}
            <div className={`points-history card ${!canTakeTests ? 'grayed-out-card' : ''}`}>
              <div className="section-header">
                <h3>{t('dashboard.pointsHistory', 'Points History')} {!canTakeTests && <span className="pro-badge-mini">PRO</span>}</h3>
                {!canTakeTests && (
                  <button className="btn-upgrade-small" onClick={() => navigate('/account')}>
                    Upgrade
                  </button>
                )}
              </div>
              
              {canTakeTests ? (
                <>
                  {/* Statistics Cards */}
                  <div className="stats-row">
                    <div className="stat-mini-card">
                      <div className="stat-mini-number">{totalCurrentYearPoints}</div>
                      <div className="stat-mini-label">{t('dashboard.pointsYear', 'Points {{year}}', { year: currentYear })}</div>
                    </div>
                    <div className="stat-mini-card">
                      <div className="stat-mini-number">{averagePerMonth.toFixed(1)}</div>
                      <div className="stat-mini-label">{t('dashboard.avgMonth', 'Avg/Month')}</div>
                    </div>
                    <div className="stat-mini-card">
                      <div className="stat-mini-number">{completionsThisMonth}</div>
                      <div className="stat-mini-label">{t('dashboard.thisMonth', 'This Month')}</div>
                    </div>
                    <div className="stat-mini-card">
                      <div className={`stat-mini-number trend-${trend}`}>
                        {trend === 'up' ? '↗' : '↘'} {trendPercentage.toFixed(0)}%
                      </div>
                      <div className="stat-mini-label">{t('dashboard.trend', 'Trend')}</div>
                    </div>
                  </div>

                  {/* Monthly Chart */}
                  <div className="chart-section">
                    <h4>{t('dashboard.monthlyBreakdown', 'Monthly Breakdown')} ({currentYear})</h4>
                    <div className="monthly-chart-container">
                      {monthlyChartData.map(data => (
                        <div
                          key={data.month}
                          className="monthly-chart-bar-item"
                          onClick={() => setActiveMonth(data.monthIndex)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === 'Enter') setActiveMonth(data.monthIndex); }}
                        >
                          <div className="monthly-chart-bar-wrapper">
                            <div 
                              className={`monthly-chart-bar ${data.monthIndex === activeMonth ? 'active' : data.monthIndex <= new Date().getMonth() ? 'past' : 'future'}`}
                              style={{ 
                                height: `${data.points > 0 ? Math.max((data.points / maxMonthlyPoints) * 100, 8) : 4}%`,
                                backgroundColor: data.monthIndex === activeMonth ? '#c4a35a' : 
                                                data.monthIndex < new Date().getMonth() ? '#08111c' : '#e5e7eb'
                              }}
                            >
                              {data.points > 0 && (
                                <span className="monthly-bar-label">{data.points}</span>
                              )}
                            </div>
                          </div>
                          <div className={`monthly-label ${data.monthIndex === activeMonth ? 'current' : ''}`}>
                            {data.month}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="month-detail">
                      <strong>{monthlyChartData[activeMonth]?.month} {currentYear}:</strong>{' '}
                      {monthlyChartData[activeMonth]?.points || 0} PE {t('dashboard.pointsThisMonth', 'points this month')}
                    </div>
                  </div>

                  {/* Yearly Overview */}
                  {chartData.length > 1 && (
                    <div className="chart-section">
                      <h4>Yearly Overview</h4>
                      <div className="yearly-chart-container">
                        {chartData.map(data => (
                          <div key={data.year} className="yearly-chart-bar-item">
                            <div className="yearly-chart-bar-wrapper">
                              <div 
                                className="yearly-chart-bar"
                                style={{ 
                                  height: `${(data.points / maxChartPoints) * 100}%`,
                                  backgroundColor: data.year == currentYear ? '#3498db' : '#95a5a6'
                                }}
                              >
                                <span className="yearly-bar-label">{data.points}</span>
                              </div>
                            </div>
                            <div className={`yearly-label ${data.year == currentYear ? 'current' : ''}`}>
                              {data.year}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Empty State */}
                  {totalCurrentYearPoints === 0 && (
                    <div className="empty-points-state">
                      <div className="empty-icon">📈</div>
                      <h4>Start Building Your History</h4>
                      <p>Complete articles and pass tests to start tracking your CPD points!</p>
                      <button className="btn-start-learning" onClick={() => navigate('/articles')}>
                        Browse Articles
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="upgrade-chart-placeholder">
                  <div className="lock-icon-large">🔒</div>
                  <p>Upgrade to Professional to track your CPD points and view detailed analytics</p>
                </div>
              )}
            </div>
            {/* AI Assistant - Professional Plan Only */}
            {canUserTakeTests(user) ? (
              <AIAssistant user={user} />
            ) : (
              <div className="ai-assistant card">
                <div className="ai-header">
                  <h4>{t('dashboard.aiAssistant.title', 'Professional AI Assistant')}</h4>
                  <p>{t('dashboard.aiAssistant.subtitle', 'Get detailed explanations of concepts, terms, and topics in your field')}</p>
                </div>
                <div className="upgrade-prompt">
                  <div className="lock-icon-large">🔒</div>
                  <h5>{t('dashboard.aiAssistant.upgradeTitle', 'Professional Plan Required')}</h5>
                  <p>{t('dashboard.aiAssistant.upgradeCopy', 'Upgrade to Professional to access our AI-powered assistant for detailed explanations of professional concepts and terminology.')}</p>
                  <button 
                    onClick={() => window.open("/pricing", "_blank")}
                    className="btn-upgrade"
                  >
                    {t('dashboard.aiAssistant.upgradeCta', 'Upgrade to Professional')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar for Latest Updates */}
        <div className="dashboard-sidebar">
          <div className="latest-updates">
            <h4>{t('dashboard.latestUpdates', 'Latest Updates')}</h4>
            <span className="update-category">{t('news.new', 'New')}</span>
            <div className="updates-list">
              {newsLoading ? (
                <p>{t('loading', 'Loading...')}</p>
              ) : newsItems.length === 0 ? (
                <p className="no-updates">{t('news.noUpdates', 'No updates available')}</p>
              ) : (
                newsItems.slice(0, 3).map(item => {
                  const getTypeInfo = (type) => {
                    switch (type) {
                      case 'news':
                        return { icon: '📰', color: '#6c757d' };
                      case 'feature':
                        return { icon: '🔵', color: '#007bff' };
                      case 'announcement':
                        return { icon: '🟢', color: '#28a745' };
                      case 'update':
                        return { icon: '🟡', color: '#ffc107' };
                      default:
                        return { icon: '📰', color: '#6c757d' };
                    }
                  };
                  const typeInfo = getTypeInfo(item.type);
                  
                  return (
                    <div
                      key={item.id}
                      className="news-item-preview"
                      onClick={() => navigate('/news')}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate('/news');
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="news-item-header">
                        <span className="news-type-icon" style={{ color: typeInfo.color }}>
                          {typeInfo.icon}
                        </span>
                        <h4>{item.title.length > 60 ? `${item.title.substring(0, 60).trim()}...` : item.title}</h4>
                      </div>
                      <p className="news-preview">
                        {item.content.length > 120 
                          ? `${item.content.substring(0, 120).trim()}...` 
                          : item.content
                        }
                      </p>
                      <span className="news-date">
                        {new Date(item.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
            <button className="view-all-updates-btn" onClick={() => navigate('/news')}>{t('dashboard.viewAllUpdates', 'View All Updates →')}</button>
          </div>
        </div>
        <style jsx>{`
          /* Dashboard Layout */
          .dashboard-container {
            /* Re-theme the dashboard to the Help Center blue palette.
               Overriding these variables recolors every accent (buttons,
               borders, hovers) used across the dashboard in one place. */
            --accent-green: #c4a35a;
            --accent-green-hover: #b08f45;
            display: flex;
            gap: 2rem;
            padding: 2rem;
            max-width: 1400px;
            margin: 0 auto;
          }
          .dashboard-main-content {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 2rem;
          }
          .dashboard-sidebar {
            width: 280px;
            flex-shrink: 0;
            display: flex;
            flex-direction: column;
          }

          /* Dashboard Widgets Grid */
          .dashboard-widgets {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1.5rem;
            align-items: stretch;
          }
          
          /* AI Assistant spans full width in the grid */
          .dashboard-widgets .ai-assistant {
            grid-column: 1 / -1;
            margin-top: 0.5rem;
          }

          @media (max-width: 1200px) {
            .dashboard-container {
              flex-direction: column;
              gap: 1.5rem;
            }
            .dashboard-sidebar {
              width: 100%;
            }
            .dashboard-widgets {
              grid-template-columns: 1fr;
              gap: 1.5rem;
            }
            .dashboard-widgets .ai-assistant {
              grid-column: 1;
            }
          }
          
          @media (max-width: 768px) {
            .dashboard-container {
              padding: 1rem;
            }
            .welcome-banner {
              flex-direction: column;
              align-items: flex-start;
              gap: 1.5rem;
            }
            .stats-cards {
              flex-wrap: wrap;
              width: 100%;
            }
            .stat-card {
              width: calc(50% - 0.5rem);
              min-width: 110px;
            }
            .dashboard-widgets {
              grid-template-columns: 1fr;
            }
          }

          /* Welcome Banner */
          .welcome-banner {
            position: relative;
            background:
              radial-gradient(circle at 88% -30%, rgba(255,255,255,0.18), transparent 45%),
              linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
            color: var(--light-text);
            padding: 2.75rem 2.5rem;
            border-radius: 20px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 1.5rem;
            flex-wrap: wrap;
            box-shadow: 0 18px 40px rgba(37, 99, 235, 0.28);
            overflow: hidden;
          }
          .welcome-text h2 {
            font-size: 2.5rem;
            font-weight: 800;
            letter-spacing: -0.02em;
            margin: 0 0 0.5rem 0;
          }
          .welcome-text p {
            font-size: 1.1rem;
            opacity: 0.8;
            margin: 0;
          }

          /* Stats Cards */
          .stats-cards {
            display: flex;
            gap: 0.9rem;
          }
          .stat-card {
            background-color: rgba(255, 255, 255, 0.07);
            border: 1px solid rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(6px);
            padding: 1.1rem 1.5rem;
            border-radius: 14px;
            text-align: center;
            min-width: 120px;
            transition: transform 0.25s ease, background-color 0.25s ease, border-color 0.25s ease;
          }
          .stat-card:hover {
            transform: translateY(-3px);
            background-color: rgba(255, 255, 255, 0.16);
            border-color: rgba(255, 255, 255, 0.45);
          }
          .stat-card.grayed-out {
            opacity: 0.6;
            background-color: rgba(255, 255, 255, 0.05);
          }
          .stat-number {
            font-size: 2.1rem;
            font-weight: 800;
            line-height: 1.1;
            display: block;
            color: #ffffff;
            letter-spacing: -0.02em;
          }
          .stat-label {
            font-size: 0.72rem;
            letter-spacing: 0.04em;
            text-transform: uppercase;
            color: rgba(255, 255, 255, 0.65);
            margin-top: 0.35rem;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
          }
          .pro-badge-mini {
            background-color: var(--accent-green);
            color: var(--light-text);
            padding: 0.2rem 0.4rem;
            border-radius: 0.25rem;
            font-size: 0.6rem;
            font-weight: 600;
          }
          
          /* Professional Insights Notice */
          .professional-insights-notice {
            background: linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%);
            border: 1px solid #ffc107;
            border-radius: var(--border-radius);
            padding: 1.5rem 2rem;
            margin-bottom: 2rem;
            text-align: center;
          }
          .notice-content h3 {
            color: var(--dark-bg);
            margin-bottom: 0.5rem;
          }
          .notice-content p {
            color: var(--secondary-text);
            margin-bottom: 1rem;
          }
          .notice-actions {
            display: flex;
            gap: 1rem;
            justify-content: center;
            align-items: center;
          }
          .btn-upgrade-dashboard {
            background-color: var(--accent-green);
            color: var(--light-text);
            border: none;
            padding: 0.6rem 1.25rem;
            border-radius: var(--border-radius);
            font-weight: 500;
            cursor: pointer;
            transition: background-color 0.2s;
          }
          .btn-upgrade-dashboard:hover {
            background-color: var(--accent-green-hover);
          }
          .btn-learn-more {
            background-color: transparent;
            color: var(--secondary-text);
            border: 1px solid var(--border-color);
            padding: 0.6rem 1.25rem;
            border-radius: var(--border-radius);
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
          }
          .btn-learn-more:hover {
            background-color: var(--border-color);
            color: var(--primary-text);
          }
          
          /* Dashboard Actions */
          .dashboard-actions {
            display: flex;
            gap: 1rem;
          }
          
          /* Professional Insights Upgrade Notice */
          .professional-insights-upgrade {
            padding: 2rem;
            border-left: 4px solid var(--accent-green);
            background-color: var(--card-bg);
            border-radius: var(--border-radius);
            margin-bottom: 2rem;
          }
          .upgrade-header {
            display: flex;
            align-items: flex-start;
            gap: 1rem;
            margin-bottom: 1.5rem;
          }
          .upgrade-icon {
            font-size: 2rem;
            flex-shrink: 0;
          }
          .upgrade-text {
            flex-grow: 1;
          }
          .upgrade-text h3 {
            color: var(--primary-text);
            margin: 0 0 0.5rem 0;
            font-size: 1.25rem;
            font-weight: 600;
          }
          .upgrade-text p {
            color: var(--secondary-text);
            margin: 0;
            line-height: 1.5;
          }
          .upgrade-actions {
            display: flex;
            gap: 1rem;
            justify-content: flex-start;
            align-items: center;
          }
          .btn-upgrade-primary {
            background-color: var(--accent-green);
            color: var(--light-text);
            border: none;
            padding: 0.75rem 1.5rem;
            border-radius: var(--border-radius);
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
          }
          .btn-upgrade-primary:hover {
            background-color: var(--accent-green-hover);
            transform: translateY(-1px);
          }
          .btn-upgrade-secondary {
            background-color: transparent;
            color: var(--secondary-text);
            border: 1px solid var(--border-color);
            padding: 0.75rem 1.5rem;
            border-radius: var(--border-radius);
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
          }
          .btn-upgrade-secondary:hover {
            background-color: var(--border-color);
            color: var(--primary-text);
            transform: translateY(-1px);
          }
          
          .dashboard-btn {
            padding: 0.85rem 1.5rem;
            border: 1px solid var(--border-color);
            border-radius: 12px;
            background-color: var(--card-bg);
            font-weight: 700;
            letter-spacing: 0.02em;
            cursor: pointer;
            box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
            transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;
          }
          .dashboard-btn:hover {
            border-color: #2563eb;
            color: #1d4ed8;
            transform: translateY(-2px);
            box-shadow: 0 10px 24px rgba(37, 99, 235, 0.16);
          }

          /* Chrome extension install banner */
          .ext-banner {
            display: flex;
            align-items: center;
            gap: 1.1rem;
            margin: 1.5rem 0;
            padding: 1.1rem 1.4rem;
            border-radius: 16px;
            border: 1px solid rgba(37, 99, 235, 0.2);
            background: linear-gradient(135deg, #eef4ff 0%, #f8fbff 70%, #ffffff 100%);
            box-shadow: 0 6px 18px rgba(37, 99, 235, 0.08);
          }
          .ext-banner-icon {
            flex: 0 0 auto;
            display: flex; align-items: center; justify-content: center;
            width: 48px; height: 48px; border-radius: 12px;
            background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
            color: #fff; box-shadow: 0 8px 20px rgba(37, 99, 235, 0.28);
          }
          .ext-banner-icon svg { width: 24px; height: 24px; }
          .ext-banner-text { flex: 1 1 auto; min-width: 0; }
          .ext-banner-title { font-weight: 800; color: #0f172a; font-size: 1.02rem; margin-bottom: 0.2rem; }
          .ext-banner-sub { color: #475569; font-size: 0.9rem; line-height: 1.4; }
          .ext-banner-btn {
            flex: 0 0 auto;
            display: inline-flex; align-items: center; gap: 0.45rem;
            background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
            color: #fff; text-decoration: none;
            padding: 0.7rem 1.25rem; border-radius: 999px;
            font-weight: 700; font-size: 0.92rem; white-space: nowrap;
            box-shadow: 0 8px 20px rgba(37, 99, 235, 0.25);
            transition: transform 0.18s ease, box-shadow 0.18s ease;
          }
          .ext-banner-btn svg { width: 16px; height: 16px; }
          .ext-banner-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 26px rgba(37, 99, 235, 0.32); }
          @media (max-width: 720px) {
            .ext-banner { flex-direction: column; align-items: flex-start; text-align: left; }
            .ext-banner-btn { align-self: stretch; justify-content: center; }
          }

          /* Dashboard Widgets Layout */
          .dashboard-widgets {
            display: grid;
            grid-template-columns: 2fr 1fr;
            gap: 2rem;
          }
          .card {
             background-color: var(--card-bg);
             border: 1px solid var(--border-color);
             border-radius: 16px;
             padding: 1.6rem;
             display: flex;
             flex-direction: column;
             box-shadow: 0 4px 14px rgba(15, 23, 42, 0.04);
             transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
          }
          .card:hover {
             transform: translateY(-3px);
             box-shadow: 0 16px 36px rgba(37, 99, 235, 0.12);
             border-color: rgba(37, 99, 235, 0.22);
          }
          .card.grayed-out-card {
            opacity: 0.7;
            background-color: #f8f9fa;
          }
          .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1.5rem;
          }
          .section-header h3 {
            margin: 0;
            font-size: 1.2rem;
            font-weight: 800;
            letter-spacing: -0.01em;
            display: flex;
            align-items: center;
            gap: 0.6rem;
            position: relative;
            padding-left: 0.85rem;
          }
          .section-header h3::before {
            content: "";
            position: absolute;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            width: 4px;
            height: 1.05em;
            border-radius: 3px;
            background: linear-gradient(180deg, #2563eb, #1d4ed8);
          }
          .view-all-btn {
            background: linear-gradient(135deg, #2563eb, #1d4ed8);
            color: var(--light-text);
            border: none;
            padding: 0.55rem 1.1rem;
            border-radius: 10px;
            cursor: pointer;
            font-weight: 700;
            box-shadow: 0 4px 12px rgba(37, 99, 235, 0.22);
            transition: transform 0.2s ease, box-shadow 0.2s ease;
          }
          .view-all-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 24px rgba(37, 99, 235, 0.3);
          }
          .btn-upgrade-small {
            background-color: var(--accent-green);
            color: var(--light-text);
            border: none;
            padding: 0.4rem 0.8rem;
            border-radius: var(--border-radius);
            font-size: 0.8rem;
            cursor: pointer;
            font-weight: 500;
            transition: background-color 0.2s;
          }
          .btn-upgrade-small:hover {
            background-color: var(--accent-green-hover);
          }
          .empty-state {
            color: var(--secondary-text);
            text-align: center;
            padding: 2rem 0;
            flex-grow: 1;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          /* Upgrade Chart Placeholder */
          .upgrade-chart-placeholder {
            text-align: center;
            padding: 3rem 1rem;
            color: var(--secondary-text);
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          /* AI Assistant Upgrade Prompt */
          .upgrade-prompt {
            text-align: center;
            padding: 3rem 2rem;
            color: var(--secondary-text);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 1rem;
          }
          .upgrade-prompt h5 {
            color: var(--primary-text);
            margin: 0.5rem 0;
            font-size: 1.2rem;
          }
          .upgrade-prompt p {
            margin: 0.5rem 0 1.5rem 0;
            max-width: 400px;
            line-height: 1.5;
          }          }
          .lock-icon-large {
            font-size: 3rem;
            margin-bottom: 1rem;
            opacity: 0.7;
          }
          
          /* Recommended Articles */
          .articles-list {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }
          .article-card-small {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 1rem;
            border: 1px solid var(--border-color);
            border-radius: var(--border-radius);
            cursor: pointer;
            transition: all 0.2s;
          }
          .article-card-small:hover {
             border-color: var(--accent-green);
             background-color: #f6f9f6;
          }
          .article-card-content h4 {
             margin: 0 0 0.25rem 0;
             font-weight: 600;
          }
          .article-card-content p {
            margin: 0;
            font-size: 0.9rem;
            color: var(--secondary-text);
          }
          .article-card-arrow {
            font-size: 1.5rem;
            color: var(--secondary-text);
            transition: transform 0.2s;
          }
          .article-card-small:hover .article-card-arrow {
            transform: translateX(5px);
            color: var(--accent-green);
          }

          /* Enhanced Points History Chart */
          .points-history {
            height: 100%;
            display: flex;
            flex-direction: column;
          }

          /* Statistics Row */
          .stats-row {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 1rem;
            margin-bottom: 2rem;
          }
          .stat-mini-card {
            background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
            border: 1px solid #e9ecef;
            border-radius: 8px;
            padding: 1rem;
            text-align: center;
            transition: all 0.3s ease;
          }
          .stat-mini-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          }
          .stat-mini-number {
            font-size: 1.8rem;
            font-weight: 700;
            color: var(--primary-text);
            margin-bottom: 0.25rem;
          }
          .stat-mini-number.trend-up {
            color: #27ae60;
          }
          .stat-mini-number.trend-down {
            color: #e74c3c;
          }
          .stat-mini-label {
            font-size: 0.8rem;
            color: var(--secondary-text);
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          /* Chart Sections */
          .chart-section {
            margin-bottom: 2rem;
          }
          .chart-section h4 {
            margin: 0 0 1rem 0;
            font-size: 1rem;
            font-weight: 600;
            color: var(--primary-text);
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }

          /* Monthly Chart */
          .monthly-chart-container {
            display: flex;
            gap: 0.5rem;
            align-items: flex-end;
            justify-content: space-between;
            height: 120px;
            background: linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%);
            border-radius: 8px;
            padding: 1rem;
            border: 1px solid #e9ecef;
          }
          .monthly-chart-bar-item {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 0.5rem;
            flex: 1;
          }
          .monthly-chart-bar-wrapper {
            width: 100%;
            height: 80px;
            display: flex;
            align-items: flex-end;
            justify-content: center;
          }
          .monthly-chart-bar {
            width: 80%;
            border-radius: 4px 4px 0 0;
            position: relative;
            transition: all 0.5s ease-out;
            display: flex;
            align-items: flex-start;
            justify-content: center;
            padding-top: 4px;
            min-height: 4px;
          }
          .monthly-chart-bar.active {
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
          .monthly-chart-bar.future {
            opacity: 0.3;
            border: 2px dashed #bdc3c7;
            background: transparent !important;
          }
          .monthly-bar-label {
            font-size: 0.7rem;
            font-weight: 600;
            color: white;
            text-shadow: 0 1px 2px rgba(0,0,0,0.3);
          }
          .monthly-label {
            font-size: 0.7rem;
            color: var(--secondary-text);
            font-weight: 500;
            text-align: center;
          }
          .monthly-label.current {
            color: #3498db;
            font-weight: 700;
          }

          /* Yearly Chart */
          .yearly-chart-container {
            display: flex;
            gap: 1rem;
            align-items: flex-end;
            justify-content: center;
            height: 100px;
            background: linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%);
            border-radius: 8px;
            padding: 1rem;
            border: 1px solid #e9ecef;
          }
          .yearly-chart-bar-item {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 0.5rem;
            min-width: 60px;
          }
          .yearly-chart-bar-wrapper {
            width: 100%;
            height: 60px;
            display: flex;
            align-items: flex-end;
            justify-content: center;
          }
          .yearly-chart-bar {
            width: 80%;
            border-radius: 4px 4px 0 0;
            position: relative;
            transition: all 0.5s ease-out;
            display: flex;
            align-items: flex-start;
            justify-content: center;
            padding-top: 4px;
            min-height: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
          .yearly-bar-label {
            font-size: 0.7rem;
            font-weight: 600;
            color: white;
            text-shadow: 0 1px 2px rgba(0,0,0,0.3);
          }
          .yearly-label {
            font-size: 0.8rem;
            color: var(--secondary-text);
            font-weight: 500;
          }
          .yearly-label.current {
            color: #3498db;
            font-weight: 700;
          }

          /* Empty States */
          .empty-points-state {
            text-align: center;
            padding: 2rem;
            background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
            border-radius: 8px;
            border: 1px solid #e9ecef;
          }
          .empty-points-state .empty-icon {
            font-size: 3rem;
            margin-bottom: 1rem;
            opacity: 0.5;
          }
          .empty-points-state h4 {
            margin: 0 0 0.5rem 0;
            color: var(--primary-text);
            font-weight: 600;
          }
          .empty-points-state p {
            margin: 0 0 1.5rem 0;
            color: var(--secondary-text);
          }
          .btn-start-learning {
            background-color: var(--accent-green);
            color: var(--light-text);
            border: none;
            padding: 0.75rem 1.5rem;
            border-radius: var(--border-radius);
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
          }
          .btn-start-learning:hover {
            background-color: var(--accent-green-hover);
            transform: translateY(-2px);
          }

          /* Upgrade Placeholder */
          .upgrade-chart-placeholder {
            text-align: center;
            padding: 3rem 2rem;
            background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
            border-radius: 8px;
            border: 2px dashed #bdc3c7;
          }
          .lock-icon-large {
            font-size: 4rem;
            margin-bottom: 1rem;
            opacity: 0.3;
          }

          /* Responsive Design */
          @media (max-width: 768px) {
            .stats-row {
              grid-template-columns: repeat(2, 1fr);
              gap: 0.75rem;
            }
            .monthly-chart-container {
              height: 100px;
              padding: 0.75rem;
              gap: 0.25rem;
            }
            .monthly-chart-bar-wrapper {
              height: 60px;
            }
            .stat-mini-number {
              font-size: 1.4rem;
            }
            .monthly-label, .yearly-label {
              font-size: 0.6rem;
            }
          }

          /* Sidebar */
          .latest-updates {
            background-color: var(--card-bg);
            border: 1px solid var(--border-color);
            padding: 1.6rem;
            border-radius: 16px;
            display: flex;
            flex-direction: column;
            height: 100%;
            box-shadow: 0 4px 14px rgba(15, 23, 42, 0.04);
          }
          .latest-updates h4 {
            margin: 0 0 0.25rem 0;
            font-weight: 800;
            font-size: 1.15rem;
            letter-spacing: -0.01em;
            position: relative;
            padding-left: 0.85rem;
          }
          .latest-updates h4::before {
            content: "";
            position: absolute;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            width: 4px;
            height: 1.05em;
            border-radius: 3px;
            background: linear-gradient(180deg, #2563eb, #1d4ed8);
          }
          .update-category {
            font-size: 0.8rem;
            color: var(--secondary-text);
            display: block;
            margin-bottom: 1rem;
          }
          .updates-list {
            flex: 1;
            margin-bottom: 1rem;
            min-height: 50px;
          }
          .news-item-preview {
            padding: 1rem;
            border: 1px solid var(--border-color);
            border-radius: var(--border-radius);
            background-color: #f8f9fa;
            transition: all 0.2s;
            overflow: hidden;
            word-wrap: break-word;
          }
          .news-item-preview:hover {
            background-color: #f1f3f5;
            border-color: var(--secondary-text);
          }
          .news-item-header {
            display: flex;
            align-items: flex-start;
            gap: 0.5rem;
            margin-bottom: 0.5rem;
            overflow: hidden;
          }
          .news-type-icon {
            font-size: 1rem;
            flex-shrink: 0;
            margin-top: 0.1rem;
          }
          .news-item-header h4 {
            margin: 0;
            font-size: 0.9rem;
            font-weight: 600;
            line-height: 1.3;
            overflow: hidden;
            word-wrap: break-word;
            word-break: break-word;
            flex: 1;
            min-width: 0;
          }
          .news-preview {
            font-size: 0.8rem;
            color: var(--secondary-text);
            line-height: 1.4;
            margin: 0 0 0.5rem 0;
            overflow: hidden;
            word-wrap: break-word;
            word-break: break-word;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            text-overflow: ellipsis;
          }
          .news-date {
            font-size: 0.7rem;
            color: var(--secondary-text);
            opacity: 0.8;
          }
          .no-updates {
            color: var(--secondary-text);
            font-size: 0.9rem;
          }
          .view-all-updates-btn {
            width: 100%;
            background: none;
            border: 1px solid var(--border-color);
            padding: 0.5rem;
            border-radius: var(--border-radius);
            cursor: pointer;
            color: var(--secondary-text);
            transition: all 0.2s;
          }
          .view-all-updates-btn:hover {
            background-color: #f1f3f5;
            border-color: #adb5bd;
            color: var(--primary-text);
          }
          
          /* Upgrade Modal Styles */
          .upgrade-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
            padding: 1rem;
          }
          .upgrade-modal {
            background-color: var(--card-bg);
            border-radius: var(--border-radius);
            max-width: 900px;
            width: 100%;
            max-height: 90vh;
            overflow-y: auto;
            position: relative;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
          }
          .upgrade-modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 2rem 2rem 1rem 2rem;
            border-bottom: 1px solid var(--border-color);
          }
          .upgrade-modal-header h3 {
            margin: 0;
            color: var(--primary-text);
            font-size: 1.5rem;
            font-weight: 600;
          }
          .modal-close {
            background: none;
            border: none;
            font-size: 2rem;
            cursor: pointer;
            color: var(--secondary-text);
            padding: 0;
            width: 40px;
            height: 40px;
            display: flex;
            alignItems: center;
            justifyContent: center;
            border-radius: 50%;
            transition: all 0.2s;
          }
          .modal-close:hover {
            background-color: var(--border-color);
            color: var(--primary-text);
          }
          .upgrade-modal-content {
            padding: 2rem;
          }
          
          /* Plans Comparison */
          .plans-comparison {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 2rem;
            margin-bottom: 2rem;
          }
          .plan-column {
            border: 2px solid var(--border-color);
            border-radius: var(--border-radius);
            padding: 1.5rem;
            background-color: var(--bg-color);
          }
          .plan-column.current-plan {
            border-color: #ffc107;
            background-color: #fff8e1;
          }
          .plan-column.upgrade-plan {
            border-color: var(--accent-green);
            background-color: #f6f9f6;
          }
          .plan-header {
            text-align: center;
            margin-bottom: 1.5rem;
            padding-bottom: 1rem;
            border-bottom: 1px solid var(--border-color);
          }
          .plan-header h4 {
            margin: 0 0 0.5rem 0;
            font-size: 1.3rem;
            font-weight: 600;
            color: var(--primary-text);
          }
          .plan-price {
            font-size: 1.5rem;
            font-weight: 600;
            color: var(--accent-green);
            margin-bottom: 0.5rem;
          }
          .plan-status {
            font-size: 0.9rem;
            color: #856404;
            font-weight: 500;
            background-color: #ffc107;
            padding: 0.25rem 0.75rem;
            border-radius: 1rem;
            display: inline-block;
          }
          .plan-savings {
            font-size: 0.9rem;
            color: var(--accent-green);
            font-weight: 500;
            background-color: #d4edda;
            padding: 0.25rem 0.75rem;
            border-radius: 1rem;
            display: inline-block;
          }
          .plan-features h5 {
            margin: 0 0 1rem 0;
            font-weight: 600;
            color: var(--primary-text);
          }
          .plan-features ul {
            list-style: none;
            padding: 0;
            margin: 0;
          }
          .plan-features li {
            padding: 0.5rem 0;
            border-bottom: 1px solid rgba(0, 0, 0, 0.1);
          }
          .plan-features li:last-child {
            border-bottom: none;
          }
          .feature-included {
            color: #28a745;
            font-weight: 500;
          }
          .feature-excluded {
            color: #dc3545;
            font-weight: 500;
            opacity: 0.7;
          }
          
          /* Benefits Grid */
          .upgrade-benefits {
            margin-bottom: 2rem;
          }
          .upgrade-benefits h4 {
            margin: 0 0 1.5rem 0;
            font-size: 1.3rem;
            font-weight: 600;
            color: var(--primary-text);
            text-align: center;
          }
          .benefits-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 1.5rem;
          }
          .benefit-item {
            display: flex;
            align-items: flex-start;
            gap: 1rem;
            padding: 1.5rem;
            background-color: var(--bg-color);
            border-radius: var(--border-radius);
            border: 1px solid var(--border-color);
            transition: all 0.3s;
          }
          .benefit-item:hover {
            border-color: var(--accent-green);
            background-color: #f6f9f6;
          }
          .benefit-icon {
            font-size: 2rem;
            flex-shrink: 0;
          }
          .professional-icon {
            width: 50px;
            height: 50px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.2rem;
            font-weight: 600;
            color: white;
            flex-shrink: 0;
          }
          .cpd-icon {
            background-color: #007bff;
            font-size: 0.9rem;
          }
          .cert-icon {
            background-color: #28a745;
            font-size: 1.5rem;
          }
          .test-icon {
            background-color: #6f42c1;
            font-size: 1.3rem;
          }
          .progress-icon {
            background-color: #fd7e14;
            font-size: 1.1rem;
          }
          .benefit-text h5 {
            margin: 0 0 0.5rem 0;
            font-weight: 600;
            color: var(--primary-text);
          }
          .benefit-text p {
            margin: 0;
            color: var(--secondary-text);
            line-height: 1.4;
          }
          
          /* CTA Section */
          .upgrade-cta {
            text-align: center;
            padding: 2rem;
            background-color: var(--bg-color);
            border-radius: var(--border-radius);
            border: 1px solid var(--border-color);
          }
          .upgrade-cta h4 {
            margin: 0 0 1rem 0;
            font-size: 1.3rem;
            font-weight: 600;
            color: var(--primary-text);
          }
          .upgrade-cta p {
            margin: 0 0 1.5rem 0;
            color: var(--secondary-text);
            line-height: 1.5;
          }
          .cta-buttons {
            display: flex;
            gap: 1rem;
            justify-content: center;
          }
          .btn-upgrade-cta {
            background-color: var(--accent-green);
            color: var(--light-text);
            border: none;
            padding: 0.75rem 2rem;
            border-radius: var(--border-radius);
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s;
          }
          .btn-upgrade-cta:hover {
            background-color: var(--accent-green-hover);
            transform: translateY(-2px);
          }
          .btn-cancel-cta {
            background-color: transparent;
            color: var(--secondary-text);
            border: 1px solid var(--border-color);
            padding: 0.75rem 2rem;
            border-radius: var(--border-radius);
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s;
          }
          .btn-cancel-cta:hover {
            background-color: var(--border-color);
            color: var(--primary-text);
          }
          
          /* Responsive Design */
          @media (max-width: 768px) {
            .upgrade-modal {
              margin: 1rem;
              max-height: 95vh;
            }
            .upgrade-modal-header,
            .upgrade-modal-content {
              padding: 1.5rem;
            }
            .plans-comparison {
              grid-template-columns: 1fr;
              gap: 1rem;
            }
            .benefits-grid {
              grid-template-columns: 1fr;
            }
            .cta-buttons {
              flex-direction: column;
            }
          }

          /* Phone Input Styles */
          .phone-input-container {
            display: flex;
            gap: 0.3rem;
            align-items: stretch;
          }
          .phone-code-wrapper {
            position: relative;
            width: 64px;
            flex: 0 0 64px;
          }
          /* Use higher specificity to override .form-group select { width: 100%; } */
          .form-group select.phone-country-code {
            width: 100%;
            padding: 0.5rem 0.25rem;
            border: 1px solid var(--border-color);
            border-radius: var(--border-radius);
            font-size: 0.85rem;
            background-color: var(--card-bg);
            color: var(--primary-text);
            text-align: center;
            /* Visually hide the text in the collapsed state while keeping dropdown functional */
            text-indent: -9999px;
            height: 44px;
          }
          .phone-code-display {
            position: absolute;
            left: 10px;
            top: 50%;
            transform: translateY(-50%);
            font-size: 0.85rem;
            color: var(--primary-text);
            pointer-events: none;
          }
          .phone-number-input {
            flex: 1 1 auto;
            min-width: 0;
            padding: 0.75rem;
            border: 1px solid var(--border-color);
            border-radius: var(--border-radius);
            font-size: 1rem;
            height: 44px;
          }
          .phone-code-wrapper select.readonly-field {
            width: 100%;
            text-align: center;
            padding: 0.5rem 0.25rem;
            text-indent: -9999px; /* hide text, keep dropdown */
            height: 44px;
          }
          .phone-country-code:focus,
          .phone-number-input:focus {
            outline: none;
            border-color: var(--accent-green);
            box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.1);
          }

          /* Table Styles */
          .table-container {
            padding: 2rem;
          }
          .table-container h3 {
            margin: 0 0 1.5rem 0;
            font-weight: 600;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th, td {
            padding: 1rem;
            text-align: left;
            border-bottom: 1px solid var(--border-color);
          }
          th {
            font-weight: 600;
            color: var(--secondary-text);
          }
          td {
            color: var(--primary-text);
          }
          tbody tr:last-child td {
            border-bottom: none;
          }
          .btn-download {
            background-color: var(--dark-bg);
            color: var(--light-text);
            border: none;
            padding: 0.5rem 1rem;
            border-radius: 0.25rem;
            font-size: 0.9rem;
            cursor: pointer;
          }

          /* Plan Selection Styles */
          .plan-selection {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            margin-top: 0.5rem;
          }
          .plan-option {
            border: 2px solid var(--border-color);
            border-radius: var(--border-radius);
            padding: 1.5rem;
            cursor: pointer;
            transition: all 0.3s ease;
            background-color: var(--card-bg);
          }
          .plan-option:hover {
            border-color: var(--accent-green);
            background-color: #f6f9f6;
          }
          .plan-option.selected {
            border-color: var(--accent-green);
            background-color: #f6f9f6;
            box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
          }
          .plan-header {
            display: flex;
            align-items: flex-start;
            gap: 1rem;
            margin-bottom: 1rem;
          }
          .plan-header input[type="radio"] {
            margin-top: 0.25rem;
            width: auto;
          }
          .plan-info h4 {
            margin: 0 0 0.25rem 0;
            font-weight: 600;
            color: var(--primary-text);
          }
          .plan-price {
            font-size: 1.1rem;
            font-weight: 600;
            color: var(--accent-green);
          }
          .plan-features {
            margin: 0;
            padding-left: 1.5rem;
          }
          .plan-features li {
            margin-bottom: 0.5rem;
            color: var(--secondary-text);
          }
          .inactive-notice {
            background-color: #fff3cd;
            border: 1px solid #ffeaa7;
            border-radius: var(--border-radius);
            padding: 1rem;
            margin-top: 1rem;
          }
          .inactive-notice p {
            margin: 0;
            color: #856404;
          }
          .membership-actions {
            display: flex;
            flex-direction: column;
            gap: 2rem;
          }
          .upgrade-section,
          .manage-section,
          .reactivate-section {
            padding: 2rem;
            background-color: #f8f9fa;
            border-radius: var(--border-radius);
            border: 1px solid var(--border-color);
          }
          .upgrade-section h4,
          .manage-section h4,
          .reactivate-section h4 {
            margin: 0 0 1rem 0;
            font-weight: 600;
            color: var(--primary-text);
          }
          .upgrade-offer {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
          }
          .upgrade-benefits h5 {
            margin: 0 0 1rem 0;
            font-weight: 600;
            color: var(--primary-text);
            font-size: 1.2rem;
          }
          .savings-calculation {
            background-color: #e8f5e8;
            border: 1px solid var(--accent-green);
            border-radius: var(--border-radius);
            padding: 1rem;
            margin: 1rem 0;
          }
          .savings-calculation > div {
            display: flex;
            justify-content: space-between;
            margin-bottom: 0.5rem;
          }
          .savings-calculation > div:last-child {
            margin-bottom: 0;
            font-weight: 600;
            border-top: 1px solid var(--accent-green);
            padding-top: 0.5rem;
            margin-top: 0.5rem;
          }
          .savings-calculation .label {
            color: var(--secondary-text);
          }
          .savings-calculation .amount {
            color: var(--primary-text);
          }
          .savings-calculation .savings-amount {
            color: var(--accent-green);
          }
          .upgrade-note {
            background-color: #fff3cd;
            border: 1px solid #ffeaa7;
            border-radius: var(--border-radius);
            padding: 1rem;
            margin: 1rem 0;
            font-size: 0.9rem;
            color: #856404;
          }
          .upgrade-features {
            margin: 1rem 0;
            padding-left: 1.5rem;
          }
          .upgrade-features li {
            margin-bottom: 0.5rem;
            color: var(--secondary-text);
          }
          .btn-upgrade,
          .btn-manage,
          .btn-reactivate {
            background-color: var(--accent-green);
            color: var(--light-text);
            border: none;
            padding: 1rem 2rem;
            border-radius: var(--border-radius);
            font-weight: 600;
            font-size: 1rem;
            cursor: pointer;
            transition: all 0.3s ease;
            align-self: flex-start;
          }
          .btn-upgrade:hover:not(:disabled),
          .btn-manage:hover:not(:disabled),
          .btn-reactivate:hover:not(:disabled) {
            background-color: var(--accent-green-hover);
            transform: translateY(-2px);
          }
          .btn-upgrade:disabled,
          .btn-manage:disabled,
          .btn-reactivate:disabled {
            background-color: #a3d9a5;
            cursor: not-allowed;
            transform: none;
          }
          .manage-section p,
          .reactivate-section p {
            margin: 0 0 1.5rem 0;
            color: var(--secondary-text);
          }

          /* AI Assistant Styles */
          .ai-assistant {
            margin-top: 2rem;
            min-height: 380px;
            display: flex;
            flex-direction: column;
          }

          .ai-header h4 {
            margin: 0 0 0.5rem 0;
            font-size: 1.25rem;
            font-weight: 600;
            color: var(--primary-text);
          }

          .ai-header p {
            margin: 0 0 1rem 0;
            font-size: 0.9rem;
            color: var(--secondary-text);
          }

          .ai-input-section {
            margin-bottom: 1rem;
            flex: 1;
            display: flex;
            flex-direction: column;
          }

          .ai-question-input {
            width: 100%;
            padding: 0.75rem;
            border: 1px solid var(--border-color);
            border-radius: 8px;
            font-family: inherit;
            font-size: 0.9rem;
            resize: vertical;
            min-height: 120px;
            flex: 1;
            transition: border-color 0.2s;
            box-sizing: border-box;
          }

          .ai-question-input:focus {
            outline: none;
            border-color: var(--accent-green);
            box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
          }

          .ai-question-input:disabled {
            background-color: #f8f9fa;
            cursor: not-allowed;
          }

          .ai-input-footer {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 0.5rem;
          }

          .character-count {
            font-size: 0.8rem;
            color: var(--secondary-text);
          }

          .ai-ask-btn {
            background: linear-gradient(135deg, var(--accent-green), #45a049);
            color: white;
            border: none;
            padding: 0.5rem 1rem;
            border-radius: 6px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
            font-size: 0.9rem;
          }

          .ai-ask-btn:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
          }

          .ai-ask-btn:disabled {
            background: #9ca3af;
            cursor: not-allowed;
            transform: none;
            box-shadow: none;
          }

          .ai-error {
            background: #fef2f2;
            color: #991b1b;
            padding: 0.75rem;
            border-radius: 6px;
            font-size: 0.9rem;
            margin-bottom: 1rem;
            border: 1px solid #fecaca;
          }

          .ai-answer {
            background: #f0f9ff;
            border: 1px solid #e0f2fe;
            border-radius: 8px;
            margin-bottom: 1rem;
            overflow: hidden;
          }

          .ai-answer-header {
            background: #0ea5e9;
            color: white;
            padding: 0.75rem;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-size: 0.9rem;
          }

          .ai-answer-content {
            padding: 1rem;
            font-size: 0.9rem;
            line-height: 1.6;
            color: var(--primary-text);
          }

          .recent-questions h5 {
            margin: 0 0 0.5rem 0;
            font-size: 0.9rem;
            font-weight: 600;
            color: var(--primary-text);
          }

          .recent-question-item {
            background: #f8f9fa;
            padding: 0.5rem;
            border-radius: 4px;
            margin-bottom: 0.5rem;
            font-size: 0.8rem;
            color: var(--secondary-text);
            cursor: pointer;
            transition: all 0.2s;
            border: 1px solid transparent;
            font-style: italic;
          }

          .recent-question-item:hover {
            background: #e9ecef;
            border-color: var(--border-color);
            color: var(--primary-text);
          }

          .recent-question-item:last-child {
            margin-bottom: 0;
          }
        `}</style>
      </div>
    );
  });

  // Check for existing token on app load
  useEffect(() => {
    const initializeAuth = async () => {
      // First check for admin authentication from URL
      const urlParams = new URLSearchParams(location.search);
      const adminToken = urlParams.get('admin_token');
      const userData = urlParams.get('user_data');
      
      if (adminToken && userData) {
        try {
          const parsedUser = JSON.parse(decodeURIComponent(userData));
          
          // Verify admin token with backend
          const response = await fetch(`${API_URL}/profile`, {
            headers: {
              'Authorization': `Bearer ${adminToken}`
            }
          });
          
          if (response.ok) {
            const backendUserData = await response.json();
            
            // Verify this is an admin user
            if (backendUserData.is_admin) {
              // Set admin user as authenticated with full access
              localStorage.setItem('token', adminToken);
              setUser(backendUserData);
              setIsAuthenticated(true);
              setAuthLoading(false);
              
              // Clear URL parameters to clean up the URL
              navigate('/', { replace: true });
              
              console.log('Admin user authenticated successfully:', backendUserData);
              return; // Exit early, skip normal token check
            } else {
              console.error('User is not an admin');
              alert('Access denied: Admin privileges required');
            }
          } else {
            console.error('Failed to verify admin token');
            alert('Failed to verify admin credentials');
          }
        } catch (error) {
          console.error('Error processing admin authentication:', error);
          alert('Error processing admin authentication. Please try again.');
        }
      }
      
      // If no admin authentication, check for regular token
    const token = localStorage.getItem('token');
    if (token) {
        await checkAuthStatus(token);
    } else {
      setAuthLoading(false);
      }
    };
    
    initializeAuth();
  }, [checkAuthStatus]);

  // Show landing page at root for unauthenticated users (no redirect)
  useEffect(() => {
    // Intentionally left blank so unauthenticated users see the landing page at '/'
  }, [authLoading, isAuthenticated, navigate, location.pathname]);
  // Check if authenticated user needs to complete payment
  useEffect(() => {
    if (!authLoading && isAuthenticated && user) {
      // DEBUG: Log user data to console
      console.log('=== SUBSCRIPTION CHECK DEBUG ===');
      console.log('User data:', user);
      console.log('User subscription_status:', user.subscription_status);
      console.log('User subscription_plan:', user.subscription_plan);
      console.log('User is_admin:', user.is_admin);
      console.log('Current pathname:', location.pathname);
      console.log('================================');
      
      // Admin users bypass all subscription checks
      if (user.is_admin) {
        console.log('🔧 Admin user detected - bypassing subscription checks');
        return; // Exit early for admin users
      }
      
      // IMPORTANT: Only redirect to subscription if user doesn't have active subscription
      // AND they're not already on a subscription-related page
      const hasActiveSubscription = hasPlatformAccess(user);
      const isOnSubscriptionPage = location.pathname === '/subscription-required' || 
                                  location.pathname === '/subscription-success' || 
                                  location.pathname === '/subscription-cancelled';
      
      console.log('📊 Subscription check results:');
      console.log('  hasActiveSubscription:', hasActiveSubscription);
      console.log('  isOnSubscriptionPage:', isOnSubscriptionPage);
      
      // If user has active subscription, they should NOT be redirected
      if (hasActiveSubscription) {
        console.log('✅ User has active subscription - no redirect needed');
        return; // Exit early - user has active subscription
      }
      
      // If user doesn't have active subscription and is not on subscription page, redirect
      if (!hasActiveSubscription && !isOnSubscriptionPage) {
        console.log('⚠️ User does NOT have active subscription - checking redirect logic');
        // Only redirect if they're trying to access the main dashboard
        if (location.pathname === '/') {
          console.log('🔄 Redirecting to subscription-required page');
          const pendingPlan = localStorage.getItem('pendingPlan');
          
          // Set default plan if none exists
          if (!pendingPlan) {
            localStorage.setItem('pendingPlan', '');
          }
          
          // Redirect to subscription required page instead of directly to Stripe
          navigate('/subscription-required');
        } else {
          console.log('📍 User not on dashboard - not redirecting');
        }
      }
    }
  }, [authLoading, isAuthenticated, user, location.pathname, navigate]);
    
  // Handle token from URL for cross-domain login
  useEffect(() => {
    const handleTokenFromUrl = async () => {
      const token = new URLSearchParams(location.search).get('token');
      if (token) {
        localStorage.setItem('token', token);
        // Wait for auth status to be checked before navigating
        await checkAuthStatus(token); 
        // Clean the URL after authentication is complete
        navigate('/', { replace: true });
      }
    };
    
    handleTokenFromUrl();
  }, [checkAuthStatus, navigate]); // Run only once on component mount

  const handleLogin = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, data };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const handleRegister = async (formData) => {
    try {
      const response = await fetch(`${API_URL}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, data };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
    navigate('/');
  };

  // Memoized article selection handler
  const handleReadArticle = useCallback((article) => {
    setSelectedArticle(article);
    navigate(`/article/${article.id}`);
  }, [navigate]);

  // Memoized navigation handler
  const goBack = useCallback(() => {
    navigate(-1); // Use browser back button instead of hardcoding to home
    setSelectedArticle(null);
    // Reset test state
    setTestQuestions([]);
    setCurrentQuestionIndex(0);
    setSelectedAnswers({});
    setTestResults(null);
  }, [navigate]);

  // Fetch news on component mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchNews();
    }
  }, [isAuthenticated, fetchNews]);

  // Fetch articles when auth status or language changes
  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  // Fetch completion data when user or articles change
  useEffect(() => {
    if (user && articles.length > 0) {
      fetchCompletionData();
    }
  }, [user, articles.length, fetchCompletionData]);

  // Handle route parameters for direct navigation (memoized)
  useEffect(() => {
    const path = location.pathname;
    
    // Handle article routes
    if (path.startsWith('/article/')) {
      const articleId = parseInt(path.split('/')[2]);
      if (articleId && articles.length > 0) {
        const article = articles.find(a => a.id === articleId);
        if (article) {
          setSelectedArticle(article);
        }
      }
    }
    
    // Handle test routes
    if (path.startsWith('/test/')) {
      const articleId = parseInt(path.split('/')[2]);
      if (articleId && articles.length > 0) {
        const article = articles.find(a => a.id === articleId);
        if (article) {
          setSelectedArticle(article);
        }
      }
    }
  }, [location.pathname, articles]);

  // Start test for the selected article
  const startTest = useCallback(async (article) => {
    // Check subscription/plan first
    if (!canUserTakeTests(user)) {
      return;
    }
    // Always check latest status from API to avoid stale cache
    try {
      const latestResp = await fetch(`${API_URL}/users/${user.id}/completion-status/${article.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (latestResp.ok) {
        const st = await latestResp.json();
        if (st.completed) {
          alert('You already passed this test.');
          return;
        }
        if (!st.can_take_test) {
          const left = typeof st.attempts_remaining === 'number' ? st.attempts_remaining : 0;
          alert(`Maximum attempts reached. Attempts remaining: ${left}`);
          return;
        }
      }
    } catch (_) { /* ignore and continue */ }
    
    try {
      setTestLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/users/${user.id}/articles/${article.id}/start-test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTestQuestions(data.questions);
        setCurrentQuestionIndex(0);
        setSelectedAnswers({});
        setTestResults(null);
        navigate(`/test/${article.id}`);
      } else {
        const errorData = await response.json();
        alert(`Error starting test: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error starting test:', error);
      alert('Error starting test');
    } finally {
      setTestLoading(false);
    }
  }, [user, canUserTakeTests, navigate, completionStatuses]);

  // Submit test answers
  const submitTest = async (answers) => {
    try {
      const response = await fetch(`${API_URL}/users/${user.id}/articles/${selectedArticle.id}/submit-test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ answers })
      });

      if (response.ok) {
        const data = await response.json();
        setTestResults(data);
        // Refresh completion data after successful submission
        await fetchCompletionData();
        return data;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error);
      }
    } catch (error) {
      console.error('Error submitting test:', error);
      throw error;
    }
  };

  // Certificate download functions: fetch certificate HTML and generate PDF client-side (no new tab)
  const ensureHtml2Pdf = async () => {
    if (window.html2pdf) return;
    const cdns = [
      'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js',
      'https://cdn.jsdelivr.net/npm/html2pdf.js@0.10.1/dist/html2pdf.bundle.min.js',
      'https://unpkg.com/html2pdf.js@0.10.1/dist/html2pdf.bundle.min.js'
    ];
    await new Promise((resolve) => {
      let i = 0;
      const tryNext = () => {
        if (window.html2pdf || i >= cdns.length) return resolve();
        const s = document.createElement('script');
        s.src = cdns[i++];
        s.async = true;
        s.onload = () => resolve();
        s.onerror = () => tryNext();
        document.head.appendChild(s);
      };
      tryNext();
    });
  };

  const renderHtmlToPdf = async (htmlString, filename) => {
    await ensureHtml2Pdf();

    // Parse the incoming HTML so we can keep its styles in the same document.
    const parsed = new DOMParser().parseFromString(htmlString, 'text/html');
    const target = parsed.querySelector('.certificate') || parsed.querySelector('.wrap') || parsed.body;
    const styleNodes = Array.from(parsed.querySelectorAll('style, link[rel="stylesheet"]'));

    // Offscreen container to host the styled content in our own document (avoids cross-doc style loss)
    const host = document.createElement('div');
    host.style.position = 'fixed';
    host.style.left = '-9999px';
    host.style.top = '-9999px';
    host.style.width = '0';
    host.style.height = '0';
    host.style.overflow = 'hidden';

    // Clone styles into host so html2canvas/html2pdf can see them
    styleNodes.forEach((node) => {
      host.appendChild(node.cloneNode(true));
    });

    // Clone the target element and keep its subtree
    const content = target.cloneNode(true);
    host.appendChild(content);
    document.body.appendChild(host);

    try {
      const waitForFonts = () =>
        new Promise((resolve) => {
          try {
            if (document.fonts && document.fonts.ready) {
              document.fonts.ready.then(() => resolve()).catch(() => setTimeout(resolve, 400));
            } else {
              setTimeout(resolve, 400);
            }
          } catch (_) {
            setTimeout(resolve, 400);
          }
        });

      // Give remote assets a moment to load
      await Promise.all([waitForFonts(), new Promise((r) => setTimeout(r, 700))]);

      const opts = {
        margin: 0.25,
        filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true, backgroundColor: '#ffffff' },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      if (window.html2pdf) {
        await window.html2pdf().set(opts).from(content).save();
      } else {
        try { window.print(); } catch (_) {}
      }
    } finally {
      document.body.removeChild(host);
    }
  };

  const downloadCertificate = async () => {
    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`${API_URL}/certificate/${selectedArticle.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const html = await res.text();
      if (!res.ok) {
        try { const err = JSON.parse(html); alert(err.error || 'Failed to load certificate'); } catch { alert('Failed to load certificate'); }
        return;
      }
      await renderHtmlToPdf(html, `certificate-${selectedArticle.id}.pdf`);
    } catch (error) {
      console.error('Error downloading certificate:', error);
      alert('Error downloading certificate');
    }
  };

  const downloadCertificateFromList = async (certificateId) => {
    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`${API_URL}/certificate/${certificateId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const html = await res.text();
      if (!res.ok) {
        try { const err = JSON.parse(html); alert(err.error || 'Failed to load certificate'); } catch { alert('Failed to load certificate'); }
        return;
      }
      await renderHtmlToPdf(html, `certificate-${certificateId}.pdf`);
    } catch (error) {
      console.error('Error downloading certificate:', error);
      alert('Error downloading certificate');
    }
  };
  const LoginForm = () => {
    const location = useLocation();
    const { i18n } = useTranslation();
    useEffect(() => {
      // Apply ?lang=... from URL and scroll to top on entry
      const params = new URLSearchParams(location.search);
      const lang = params.get('lang');
      if (lang && lang !== i18n.language) {
        try { i18n.changeLanguage(lang); } catch (_) {}
      }
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, [location.search, i18n]);
    const [formData, setFormData] = useState({
      email: '',
      password: ''
    });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showResendOption, setShowResendOption] = useState(false);
    const [resendingEmail, setResendingEmail] = useState(false);
    const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
    const [resetEmailSent, setResetEmailSent] = useState(false);

    const handleCheckoutRedirect = async (userData, pendingPlan) => {
      try {
        // Get user credentials from sessionStorage
        const pendingUser = JSON.parse(sessionStorage.getItem('pendingSubscriptionUser') || '{}');
        const tempPassword = sessionStorage.getItem('tempPassword');
        
        if (!pendingUser.email || !tempPassword) {
          setError('Session expired. Please log in again.');
          navigate('/login');
          return;
        }

        // Get price ID for the selected plan
        const selectedPlan = SUBSCRIPTION_PLANS[pendingPlan];
        if (!selectedPlan) {
          setError('Invalid subscription plan selected.');
          return;
        }
        
        // Create secure subscription session (no authentication token needed)
        const response = await fetch(`${API_URL}/create-subscription-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: pendingUser.email,
            password: tempPassword,
            priceId: selectedPlan.priceId
          }),
        });

        const result = await response.json();
        
        if (response.ok) {
          // Clear temporary credentials now that we've used them
          sessionStorage.removeItem('pendingSubscriptionUser');
          sessionStorage.removeItem('tempPassword');
          
          // Redirect to Stripe checkout
          window.location.href = result.checkout_url;
        } else {
          setError(result.error || 'Failed to create subscription session.');
        }
      } catch (error) {
        console.error('Checkout redirect error:', error);
        setError('Error creating subscription session. Please try again.');
      }
    };

    const handleChange = (e) => {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value
      });
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setError('');
      setShowResendOption(false);
      setIsLoading(true);

      try {
        const loginResponse = await fetch(`${API_URL}/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', },
          body: JSON.stringify(formData),
        });

        const loginData = await loginResponse.json();

        if (loginResponse.ok) {
          // SUCCESS: User has active subscription or is admin
          const token = loginData.access_token;
          localStorage.setItem('token', token);
          
          // Fetch fresh user data from /profile to ensure it's correct
          const profileResponse = await fetch(`${API_URL}/profile`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (profileResponse.ok) {
            const userData = await profileResponse.json();
            localStorage.setItem('user', JSON.stringify(userData));
            setUser(userData);
            setIsAuthenticated(true);
            
            // Navigate to dashboard for users with active subscriptions
              navigate('/');
          } else {
            // Handle profile fetch failure
            setError('Login successful, but failed to retrieve user profile.');
            localStorage.removeItem('token');
          }
        } else {
          // Handle subscription required error (402 status) - NEW SECURE FLOW
          if (loginResponse.status === 402 && loginData.subscription_required) {
            // SECURITY FIX: Do NOT store tokens for users without subscriptions
            // Clear any existing auth data
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
            setIsAuthenticated(false);
            
            // Store user info temporarily in sessionStorage (more secure than localStorage)
            sessionStorage.setItem('pendingSubscriptionUser', JSON.stringify(loginData.user));
            // Also store password temporarily for subscription flow (will be cleared after use)
            sessionStorage.setItem('tempPassword', formData.password);
            // Flag discontinued free-plan users so we can explain the change on the plan page
            if (loginData.plan_discontinued) {
              sessionStorage.setItem('planDiscontinued', 'true');
            } else {
              sessionStorage.removeItem('planDiscontinued');
            }
            
            console.log('User requires subscription - redirecting to subscription flow');
            navigate('/subscription-required');
            return; // Exit early to prevent error message
          } else {
            setError(loginData.error);
            if (loginData.email_confirmation_required) {
              setShowResendOption(true);
            }
          }
        }
      } catch (error) {
        setError('Network error. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    const handleForgotPassword = async (e) => {
      e.preventDefault();
      setError('');
      setIsLoading(true);

      try {
        const response = await fetch(`${API_URL}/request-password-reset`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: formData.email }),
        });

        const data = await response.json();

        if (response.ok) {
          setResetEmailSent(true);
        } else {
          setError(data.error || 'An error occurred. Please try again.');
        }
      } catch (err) {
        setError('An error occurred. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    const handleResendConfirmation = async () => {
      setResendingEmail(true);
      setError('');
      try {
        const response = await fetch(`${API_URL}/resend-confirmation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', },
          body: JSON.stringify({ email: formData.email }),
        });
        const data = await response.json();
        if (response.ok) {
          alert('Confirmation email sent! Please check your inbox and spam folder. Emails may take a few minutes to arrive.');
        } else {
          setError(data.error);
        }
      } catch (error) {
        setError('Network error. Please try again.');
      } finally {
        setResendingEmail(false);
      }
    };

    const resetModal = () => {
      setForgotPasswordMode(false);
      setResetEmailSent(false);
      setError('');
    };

    if (resetEmailSent) {
      return (
        <div className="auth-page-container auth-branded">
          <div className="auth-card card">
            <div className="auth-card-header">
              <h1>Check Your Email</h1>
              <p>We've sent a password reset link to <strong>{formData.email}</strong></p>
            </div>
            <div className="reset-email-success">
              <p>Click the link in your email to reset your password. The link will expire in 1 hour.</p>
              <p className="email-timing-notice">
                <strong>📧 Email may take a few minutes to arrive.</strong> Please check your spam/junk folder if you don't see it in your inbox.
              </p>
              <button 
                className="btn-submit" 
                onClick={() => {
                  setForgotPasswordMode(false);
                  setResetEmailSent(false);
                  setError('');
                }}
                style={{ marginTop: '20px' }}
              >
                Back to Login
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (forgotPasswordMode) {
      return (
        <div className="auth-page-container auth-branded">
          <div className="auth-card card">
            <div className="auth-card-header">
              <h1>Reset Password</h1>
              <p>Enter your email address and we'll send you a link to reset your password.</p>
            </div>
            <form onSubmit={handleForgotPassword}>
              {error && <div className="message error">{error}</div>}
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email address"
                  required 
                />
              </div>
              <button type="submit" disabled={isLoading} className="btn-submit">
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
            <div className="auth-card-footer">
              <button 
                onClick={() => setForgotPasswordMode(false)}
                className="btn-submit"
                style={{ 
                  backgroundColor: '#6b7280',
                  marginTop: '1rem'
                }}
              >
                ← Back to Login
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="auth-page-container auth-branded">
        <div className="auth-card card">
          <div className="auth-card-header">
            <h1>{t('auth.signInTitle', 'Sign In')}</h1>
            <p>{t('auth.signInSubtitle', 'Log in to continue your professional development.')}</p>
            <div className="accredited-strip" style={{ position:'relative', width: '100%', textAlign: 'center', marginTop: '0.75rem' }}>
              <div style={{ color: 'var(--secondary-text)', marginBottom: '0.25rem' }}>Accredited by</div>
              <a href="https://www.nirv.nl" target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center' }}>
                <img src="/nirv-logo.png" alt="NiRV" style={{ height: 44, width: 'auto' }} />
              </a>
            </div>
          </div>
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="message error">
                {error}
                {showResendOption && (
                  <div className="resend-section">
                    <p>Need a new confirmation email?</p>
                    <button type="button" onClick={handleResendConfirmation} disabled={resendingEmail} className="btn-resend">
                      {resendingEmail ? 'Sending...' : 'Resend Confirmation'}
                    </button>
                  </div>
                )}
              </div>
            )}
            <div className="form-group">
              <label htmlFor="email">{t('account.email', 'Email')}</label>
              <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label htmlFor="password">{t('password', 'Password')}</label>
              <input type="password" id="password" name="password" value={formData.password} onChange={handleChange} required />
            </div>
            <button type="submit" disabled={isLoading} className="btn-submit">
              {isLoading ? t('processing', 'Processing...') : t('lp.cta.signIn', 'Sign In')}
            </button>
          </form>
          <div className="auth-card-footer">
            <p>{t('auth.noAccount', "Don't have an account?")}{' '}<button onClick={() => navigate(`/register?lang=${i18n.language}`)} className="btn-link">{t('auth.register', 'Register')}</button></p>
            <p>
              <button 
                onClick={() => setForgotPasswordMode(true)}
                className="btn-link"
                style={{ marginTop: '0.5rem' }}
              >
                {t('auth.forgotPassword', 'Forgot your password?')}
              </button>
            </p>
          </div>
        </div>
        <style jsx>{`
          .auth-page-container {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 1rem;
            min-height: calc(100vh - 80px); /* Adjust for navbar height */
            padding: 2rem;
          }
          .auth-card {
            width: 100%;
            max-width: 450px;
            padding: 2.5rem;
          }
          .auth-card-header {
            text-align: center;
            margin-bottom: 2rem;
          }
          .auth-card-header h1 {
            font-size: 2rem;
            font-weight: 600;
            margin: 0 0 0.5rem 0;
          }
          .auth-card-header p {
            color: var(--secondary-text);
          }
          .form-group {
            margin-bottom: 1.5rem;
          }
          .form-group label {
            display: block;
            margin-bottom: 0.5rem;
            font-weight: 500;
          }
          .form-group input {
            width: 100%;
            padding: 0.75rem;
            border: 1px solid var(--border-color);
            border-radius: var(--border-radius);
            font-size: 1rem;
          }
          .btn-submit {
            width: 100%;
            padding: 0.75rem;
            border: none;
            border-radius: var(--border-radius);
            background-color: #f59e0b;
            color: #0a0a0a;
            font-size: 1.1rem;
            font-weight: 500;
            cursor: pointer;
            transition: background-color 0.2s, box-shadow .2s ease, transform .2s ease;
            box-shadow: 0 10px 28px rgba(245,158,11,.24);
          }
          .btn-submit:hover:not(:disabled) {
            background-color: #d97706;
            box-shadow: 0 12px 30px rgba(245,158,11,.32);
            transform: translateY(-1px);
          }
          .btn-submit:disabled {
            background-color: #a3d9a5;
            cursor: not-allowed;
          }
          .btn-submit[style*="backgroundColor: #6b7280"]:hover:not(:disabled) {
            background-color: #4b5563 !important;
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
          }
          .auth-card-footer {
            text-align: center;
            margin-top: 1.5rem;
            color: var(--secondary-text);
          }
          .btn-link {
            background: none;
            border: none;
            color: var(--accent-green);
            font-weight: 600;
            cursor: pointer;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
            transition: all 0.2s ease;
            text-decoration: none;
            font-size: 1rem;
          }
          .btn-link:hover {
            background-color: var(--accent-green);
            color: var(--light-text);
            transform: translateY(-1px);
          }
          .message.error {
            background-color: #fff0f0;
            color: #d93a3a;
            padding: 1rem;
            border-radius: var(--border-radius);
            margin-bottom: 1.5rem;
            text-align: center;
          }
          .resend-section {
            margin-top: 1rem;
            padding-top: 1rem;
            border-top: 1px solid #d93a3a40;
          }
          .resend-section p {
            margin: 0 0 0.5rem 0;
            font-size: 0.9rem;
          }
          .btn-resend {
            font-size: 0.9rem;
            color: #d93a3a;
            background: none;
            border: 1px solid #d93a3a;
            padding: 0.25rem 0.5rem;
            border-radius: var(--border-radius);
            cursor: pointer;
          }
          .reset-email-success {
            text-align: center;
            padding: 1rem 0;
          }
          .reset-email-success p {
            margin-bottom: 0.75rem;
            line-height: 1.6;
          }
          .email-timing-notice {
            font-size: 0.9rem;
            color: var(--secondary-text);
            margin-top: 1rem;
            padding: 0.75rem;
            background-color: #f8f9fa;
            border-radius: var(--border-radius);
            border-left: 4px solid #17a2b8;
          }
        `}</style>
      </div>
    );
  };
  const RegisterForm = () => {
    const location = useLocation();
    const { i18n } = useTranslation();
    useEffect(() => {
      // Apply ?lang=... from URL and scroll to top on entry
      const params = new URLSearchParams(location.search);
      const lang = params.get('lang');
      if (lang && lang !== i18n.language) {
        try { i18n.changeLanguage(lang); } catch (_) {}
      }
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, [location.search, i18n]);
    const [formData, setFormData] = useState({
      email: '',
      password: '',
      confirmPassword: '',
      first_name: '',
      last_name: '',
      profession: '',
      secondary_profession: '',
      company: '',
      job_title: '',
      country: '',
      address: '',
      city: '',
      state: '',
      postal_code: '',
      phone: '',
      phoneCountryCode: '+31',
      license_number: '',
      acceptPrivacy: false,
      acceptTerms: false
    });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [registrationSuccess, setRegistrationSuccess] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [emailSent, setEmailSent] = useState(false);
    const [resendingEmail, setResendingEmail] = useState(false);

    const professionOptions = PROFESSION_CATEGORIES;
    
    // List of countries for the dropdown
    const countries = [
      'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria',
      'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
      'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon',
      'Canada', 'Cape Verde', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica',
      'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'East Timor', 'Ecuador',
      'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon',
      'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana',
      'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel',
      'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'North Korea', 'South Korea', 'Kuwait',
      'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg',
      'Macedonia', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius',
      'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia',
      'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'Norway', 'Oman', 'Pakistan',
      'Palau', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania',
      'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal',
      'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'Spain',
      'Sri Lanka', 'Sudan', 'Suriname', 'Swaziland', 'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania',
      'Thailand', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine',
      'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam', 'Yemen',
      'Zambia', 'Zimbabwe'
    ];

    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    };

    const handleAddressSelect = (addressData) => {
      setFormData(prevData => ({
        ...prevData,
        address: addressData.address,
        city: addressData.city,
        state: addressData.state,
        country: addressData.country,
        postal_code: addressData.postal_code
      }));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setError('');

      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      if (!formData.acceptPrivacy) {
        setError('You must accept the Privacy Policy to register');
        return;
      }

      if (!formData.acceptTerms) {
        setError('You must accept the Terms of Service to register');
        return;
      }

      setIsLoading(true);

      try {
        const response = await fetch(`${API_URL}/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        const data = await response.json();

        if (response.ok) {
          setRegistrationSuccess(true);
          setUserEmail(data.user_email);
          setEmailSent(data.email_sent);
          
          // Clear any previously selected plan; user will choose on next step
          localStorage.removeItem('pendingPlan');
        } else {
          setError(data.error);
        }
      } catch (error) {
        setError('Network error. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    const handleResendEmail = async () => {
      setResendingEmail(true);
      setError('');

      try {
        const response = await fetch(`${API_URL}/resend-confirmation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: userEmail }),
        });

        const data = await response.json();

        if (response.ok) {
          setEmailSent(true);
          alert('Confirmation email sent! Please check your inbox. Emails may take a few minutes to arrive.');
        } else {
          setError(data.error);
        }
      } catch (error) {
        setError('Network error. Please try again.');
      } finally {
        setResendingEmail(false);
      }
    };

    if (registrationSuccess) {
      return (
        <div className="auth-page-container auth-branded">
          <div className="auth-card card">
            <div className="auth-card-header">
              <h1>Registration Successful!</h1>
              <p>Please check your email to confirm your account.</p>
            </div>
            <div className="confirmation-notice">
              <p>We've sent a confirmation email to <strong>{userEmail}</strong>. Click the link in the email to activate your account.</p>
              <p className="confirmation-instructions">
                After confirming your email, you'll be redirected to complete your subscription and start learning.
              </p>
              <p className="email-timing-notice">
                <strong>📧 Email may take a few minutes to arrive.</strong> Please check your spam/junk folder if you don't see it in your inbox.
              </p>
              
              {error && <p className="message error" style={{marginTop: '1rem'}}>{error}</p>}
            </div>
            <div className="auth-card-footer" style={{display: 'flex', gap: '1rem', justifyContent: 'center'}}>
               <button onClick={handleResendEmail} disabled={resendingEmail} className="btn-secondary-action">
                {resendingEmail ? 'Sending...' : 'Resend Email'}
              </button>
              <button onClick={() => navigate('/login')} className="btn-primary-action">
                Go to Login
              </button>
            </div>
          </div>
          <style jsx>{`
            /* Using the same styles as LoginForm for consistency */
            .auth-page-container {
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: calc(100vh - 80px);
              padding: 2rem;
            }
            .auth-card {
              width: 100%;
              max-width: 450px;
              padding: 2.5rem;
            }
            .auth-card-header {
              text-align: center;
              margin-bottom: 2rem;
            }
            .auth-card-header h1 {
              font-size: 2rem;
              font-weight: 600;
              margin: 0 0 0.5rem 0;
            }
            .auth-card-header p {
              color: var(--secondary-text);
            }
            .confirmation-notice {
              text-align: center;
              line-height: 1.6;
              margin-bottom: 2rem;
            }
            .auth-card-footer {
              text-align: center;
              margin-top: 1.5rem;
            }
            .btn-primary-action, .btn-secondary-action {
              padding: 0.6rem 1.25rem;
              border-radius: 0.25rem;
              font-weight: 500;
              cursor: pointer;
              border: 1px solid transparent;
              transition: all 0.2s;
            }
            .btn-primary-action {
              background-color: var(--accent-green);
              color: var(--light-text);
            }
            .btn-secondary-action {
              background-color: var(--card-bg);
              color: var(--primary-text);
              border: 1px solid var(--border-color);
            }
            .message.error {
              background-color: #fff0f0;
              color: #d93a3a;
              padding: 1rem;
              border-radius: var(--border-radius);
            }
            .selected-plan-info {
              margin: 2rem 0;
              padding: 1.5rem;
              background-color: #f6f9f6;
              border-radius: var(--border-radius);
              border: 1px solid var(--accent-green);
              text-align: center;
            }
            .selected-plan-info h3 {
              margin: 0 0 1rem 0;
              color: var(--primary-text);
            }
            .plan-summary h4 {
              margin: 0 0 0.5rem 0;
              color: var(--primary-text);
              font-size: 1.2rem;
            }
            .plan-price {
              font-size: 1.1rem;
              font-weight: 600;
              color: var(--accent-green);
              margin: 0 0 1rem 0;
            }
            .plan-note {
              font-size: 0.9rem;
              color: var(--secondary-text);
              margin: 0;
            }
          `}</style>
        </div>
      );
    }

    return (
      <div className="auth-page-container auth-branded">
        <div className="auth-card card">
          <div className="auth-card-header">
            <h1>{t('auth.createAccountTitle', 'Create Your Account')}</h1>
            <p>{t('auth.createAccountSubtitle', 'Join Protagon to start your professional development journey.')}</p>
          </div>

          <form onSubmit={handleSubmit}>
            {error && <div className="message error">{error}</div>}

            <div className="form-row">
              <div className="form-group">
                <label>{t('account.firstName', 'First Name')}</label>
                <input type="text" name="first_name" value={formData.first_name} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>{t('account.lastName', 'Last Name')}</label>
                <input type="text" name="last_name" value={formData.last_name} onChange={handleChange} required />
              </div>
            </div>
            
            <div className="form-group">
              <label>{t('account.email', 'Email')}</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} required />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>{t('auth.password', 'Password')}</label>
                <input type="password" name="password" value={formData.password} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>{t('auth.confirmPassword', 'Confirm Password')}</label>
                <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} required />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>{t('account.profession', 'Profession')}</label>
                <select name="profession" value={formData.profession} onChange={handleChange} required>
                  <option value="">{t('account.selectProfession', 'Select Profession')}</option>
                  {professionOptions.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>{t('account.secondaryProfession', 'Secondary Profession (optional)')}</label>
                <select name="secondary_profession" value={formData.secondary_profession || ''} onChange={handleChange}>
                  <option value="">{t('common.none', 'None')}</option>
                  {professionOptions.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('account.company', 'Company')}</label>
                <input type="text" name="company" value={formData.company} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>{t('account.jobTitle', 'Job Title')}</label>
                <input type="text" name="job_title" value={formData.job_title} onChange={handleChange} required />
              </div>
            </div>

            <div className="form-group">
              <label>{t('account.country', 'Country')}</label>
              <select name="country" value={formData.country} onChange={handleChange} required>
                <option value="">{t('account.selectCountry', 'Select Country')}</option>
                {COUNTRIES.map(country => <option key={country} value={country}>{country}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>{t('account.address', 'Address')}</label>
              <AddressAutocomplete
                name="address"
                value={formData.address}
                onChange={handleChange}
                onAddressSelect={handleAddressSelect}
                placeholder={t('account.addressPlaceholder', 'Start typing your address...')}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('account.city', 'City')}</label>
                <input type="text" name="city" value={formData.city} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>{t('account.state', 'State/Province')}</label>
                <input type="text" name="state" value={formData.state} onChange={handleChange} required />
              </div>
            </div>

            <div className="form-group">
              <label>{t('account.postalCode', 'Postal Code')}</label>
              <input type="text" name="postal_code" value={formData.postal_code} onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label>{t('account.phone', 'Phone Number')}</label>
              <div className="phone-input-container">
                <div className="phone-code-wrapper">
                  <select 
                    name="phoneCountryCode" 
                    value={formData.phoneCountryCode} 
                    onChange={handleChange} 
                    className="phone-country-code"
                    required
                  >
                    {COUNTRY_CODES.slice().sort((a, b) => a.country.localeCompare(b.country)).map(item => (
                      <option key={`${item.code}-${item.country}`} value={item.code}>
                        {item.country} ({item.code})
                      </option>
                    ))}
                  </select>
                  <div className="phone-code-display">{formData.phoneCountryCode}</div>
                </div>
                <input 
                  type="tel" 
                  name="phone" 
                  value={formData.phone} 
                  onChange={handleChange} 
                  placeholder={t('account.phonePlaceholder', 'Your phone number')}
                  className="phone-number-input"
                  required 
                />
              </div>
            </div>

            <div className="form-group checkbox-group">
              <input type="checkbox" id="acceptTerms" name="acceptTerms" checked={formData.acceptTerms} onChange={handleChange} required />
              <label htmlFor="acceptTerms">{t('auth.acceptPrefix', 'I agree to the')} <a href="/terms-of-service.html" target="_blank" rel="noopener noreferrer">{t('auth.termsOfService', 'Terms of Service')}</a></label>
            </div>
            <div className="form-group checkbox-group">
              <input type="checkbox" id="acceptPrivacy" name="acceptPrivacy" checked={formData.acceptPrivacy} onChange={handleChange} required />
              <label htmlFor="acceptPrivacy">{t('auth.acceptPrefix', 'I agree to the')} <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer">{t('auth.privacyPolicy', 'Privacy Policy')}</a></label>
            </div>
            
            <button type="submit" className="btn-submit" disabled={isLoading}>
              {isLoading ? t('auth.creatingAccount', 'Creating Account...') : t('auth.register', 'Register')}
            </button>
          </form>

          <div className="auth-card-footer">
            <p>{t('auth.alreadyHaveAccount', 'Already have an account?')}{' '}<button onClick={() => navigate('/login')} className="btn-link">{t('auth.signIn', 'Sign in')}</button></p>
          </div>
        </div>
        <style jsx>{`
          .auth-page-container {
            display: flex;
            justify-content: center;
            align-items: flex-start; /* Changed to flex-start for long forms */
            min-height: calc(100vh - 80px);
            padding: 2rem;
            overflow-y: auto;
          }
          .auth-card {
            width: 100%;
            max-width: 600px;
            padding: 2.5rem;
            margin: 2rem 0;
          }
          .auth-card-header {
            text-align: center;
            margin-bottom: 2rem;
          }
          .auth-card-header h1 {
            font-size: 2rem;
            font-weight: 600;
            margin: 0 0 0.5rem 0;
          }
          .auth-card-header p {
            color: var(--secondary-text);
          }
          .form-group {
            margin-bottom: 1rem;
          }
          .form-group label { display:block; margin-bottom:.5rem; font-weight:500; color:#0b1220; }
          .form-group input, .form-group select {
            width: 100%;
            padding: 0.75rem;
            border: 1px solid rgba(15,23,42,.2);
            border-radius: var(--border-radius);
            font-size: 1rem;
            background: #ffffff;
            color: #0b1220;
          }
          .form-group input::placeholder { color:#0b1220; opacity:0.7; }
          .form-row {
            display: flex;
            gap: 1.5rem;
            margin-bottom: 1rem;
          }
          .form-row .form-group {
            flex-grow: 1;
            margin-bottom: 0;
          }
          .checkbox-group {
            display: flex;
            align-items: center;
            gap: 0.75rem;
          }
          .checkbox-group input {
            width: auto;
          }
          .checkbox-group label {
            margin: 0;
            font-weight: 400;
            color: var(--secondary-text);
          }
          .checkbox-group a {
            color: #2563eb;
            text-decoration: none;
          }
          .checkbox-group a:hover {
            text-decoration: underline;
          }
          .btn-submit {
            padding: 0.75rem;
            border: none;
            border-radius: var(--border-radius);
            background-color: #0f172a;
            color: #ffffff;
            font-size: 1.1rem;
            font-weight: 500;
            cursor: pointer;
            transition: background-color 0.2s, box-shadow .2s ease, transform .2s ease;
            margin-top: 1.5rem;
            width: 100%;
            box-shadow: 0 10px 28px rgba(15,23,42,.18);
          }
          .btn-submit:hover { background:#111827; box-shadow:0 12px 30px rgba(15,23,42,.24); transform: translateY(-1px); }
          .message {
            padding: 1rem;
            border-radius: var(--border-radius);
            margin-bottom: 1.5rem;
            text-align: center;
          }
          .message.success {
            background-color: #e8f5e8;
            color: var(--accent-green);
          }
          .message.error {
            background-color: #fff0f0;
            color: #d93a3a;
          }

          .auth-card-footer {
            text-align: center;
            margin-top: 1.5rem;
            color: var(--secondary-text);
          }
          .btn-link {
            background: none;
            border: none;
            color: #2563eb;
            font-weight: 600;
            cursor: pointer;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
            transition: all 0.2s ease;
            text-decoration: none;
            font-size: 1rem;
          }
          .btn-link:hover {
            background-color: rgba(37,99,235,.12);
            color: #0b1220;
            transform: translateY(-1px);
          }

          /* Phone Input Styles */
          .phone-input-container {
            display: flex;
            gap: 0.3rem;
            align-items: stretch;
          }
          .phone-code-wrapper {
            position: relative;
            width: 64px;
            flex: 0 0 64px;
          }
          /* Use higher specificity to override .form-group select { width: 100%; } */
          .form-group select.phone-country-code {
            width: 64px;
            flex: 0 0 64px;
            padding: 0.5rem 0.25rem;
            border: 1px solid rgba(15,23,42,.2);
            border-radius: var(--border-radius);
            font-size: 0.85rem;
            background-color: #ffffff;
            color: #0b1220;
            text-align: center;
            /* Visually hide the text in the collapsed state while keeping dropdown functional */
            text-indent: -9999px;
            height: 44px;
          }
          .phone-code-display {
            position: absolute;
            left: 10px;
            top: 50%;
            transform: translateY(-50%);
            font-size: 0.85rem;
            color: #0b1220;
            pointer-events: none;
          }
          .phone-number-input {
            flex: 1 1 auto;
            min-width: 0;
            padding: 0.75rem;
            border: 1px solid rgba(15,23,42,.2);
            border-radius: var(--border-radius);
            font-size: 1rem;
            height: 44px;
            background: #ffffff;
            color: #0b1220;
          }
          .phone-code-wrapper select.readonly-field {
            width: 100%;
            text-align: center;
            padding: 0.5rem 0.25rem;
            text-indent: -9999px; /* hide text, keep dropdown */
            height: 44px;
          }
          .phone-country-code:focus,
          .phone-number-input:focus {
            outline: none;
            border-color: rgba(37,99,235,.6);
            box-shadow: 0 0 0 3px rgba(37,99,235,0.18);
          }

          /* Checkbox accent color aligned with blue palette */
          .checkbox-group input { accent-color: #2563eb; }

          /* Table Styles */
          .table-container {
            padding: 2rem;
          }
          .table-container h3 {
            margin: 0 0 1.5rem 0;
            font-weight: 600;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th, td {
            padding: 1rem;
            text-align: left;
            border-bottom: 1px solid var(--border-color);
          }
          th {
            font-weight: 600;
            color: var(--secondary-text);
          }
          td {
            color: var(--primary-text);
          }
          tbody tr:last-child td {
            border-bottom: none;
          }
          .btn-download {
            background-color: var(--dark-bg);
            color: var(--light-text);
            border: none;
            padding: 0.5rem 1rem;
            border-radius: 0.25rem;
            font-size: 0.9rem;
            cursor: pointer;
          }

          /* Plan Selection Styles */
          .plan-selection {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            margin-top: 0.5rem;
          }
          .plan-option {
            border: 2px solid var(--border-color);
            border-radius: var(--border-radius);
            padding: 1.5rem;
            cursor: pointer;
            transition: all 0.3s ease;
            background-color: var(--card-bg);
          }
          .plan-option:hover {
            border-color: var(--accent-green);
            background-color: #f6f9f6;
          }
          .plan-option.selected {
            border-color: var(--accent-green);
            background-color: #f6f9f6;
            box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
          }
          .plan-header {
            display: flex;
            align-items: flex-start;
            gap: 1rem;
            margin-bottom: 1rem;
          }
          .plan-header input[type="radio"] {
            margin-top: 0.25rem;
            width: auto;
          }
          .plan-info h4 {
            margin: 0 0 0.25rem 0;
            font-weight: 600;
            color: var(--primary-text);
          }
          .plan-price {
            font-size: 1.1rem;
            font-weight: 600;
            color: var(--accent-green);
          }
          .plan-features {
            margin: 0;
            padding-left: 1.5rem;
          }
          .plan-features li {
            margin-bottom: 0.5rem;
            color: var(--secondary-text);
          }
          .inactive-notice {
            background-color: #fff3cd;
            border: 1px solid #ffeaa7;
            border-radius: var(--border-radius);
            padding: 1rem;
            margin-top: 1rem;
          }
          .inactive-notice p {
            margin: 0;
            color: #856404;
          }
          .membership-actions {
            display: flex;
            flex-direction: column;
            gap: 2rem;
          }
          .upgrade-section,
          .manage-section,
          .reactivate-section {
            padding: 2rem;
            background-color: #f8f9fa;
            border-radius: var(--border-radius);
            border: 1px solid var(--border-color);
          }
          .upgrade-section h4,
          .manage-section h4,
          .reactivate-section h4 {
            margin: 0 0 1rem 0;
            font-weight: 600;
            color: var(--primary-text);
          }
          .upgrade-offer {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
          }
          .upgrade-benefits h5 {
            margin: 0 0 1rem 0;
            font-weight: 600;
            color: var(--primary-text);
            font-size: 1.2rem;
          }
          .savings-calculation {
            background-color: #e8f5e8;
            border: 1px solid var(--accent-green);
            border-radius: var(--border-radius);
            padding: 1rem;
            margin: 1rem 0;
          }
          .savings-calculation > div {
            display: flex;
            justify-content: space-between;
            margin-bottom: 0.5rem;
          }
          .savings-calculation > div:last-child {
            margin-bottom: 0;
            font-weight: 600;
            border-top: 1px solid var(--accent-green);
            padding-top: 0.5rem;
            margin-top: 0.5rem;
          }
          .savings-calculation .label {
            color: var(--secondary-text);
          }
          .savings-calculation .amount {
            color: var(--primary-text);
          }
          .savings-calculation .savings-amount {
            color: var(--accent-green);
          }
          .upgrade-note {
            background-color: #fff3cd;
            border: 1px solid #ffeaa7;
            border-radius: var(--border-radius);
            padding: 1rem;
            margin: 1rem 0;
            font-size: 0.9rem;
            color: #856404;
          }
          .upgrade-features {
            margin: 1rem 0;
            padding-left: 1.5rem;
          }
          .upgrade-features li {
            margin-bottom: 0.5rem;
            color: var(--secondary-text);
          }
          .btn-upgrade,
          .btn-manage,
          .btn-reactivate {
            background-color: var(--accent-green);
            color: var(--light-text);
            border: none;
            padding: 1rem 2rem;
            border-radius: var(--border-radius);
            font-weight: 600;
            font-size: 1rem;
            cursor: pointer;
            transition: all 0.3s ease;
            align-self: flex-start;
          }
          .btn-upgrade:hover:not(:disabled),
          .btn-manage:hover:not(:disabled),
          .btn-reactivate:hover:not(:disabled) {
            background-color: var(--accent-green-hover);
            transform: translateY(-2px);
          }
          .btn-upgrade:disabled,
          .btn-manage:disabled,
          .btn-reactivate:disabled {
            background-color: #a3d9a5;
            cursor: not-allowed;
            transform: none;
          }
          .manage-section p,
          .reactivate-section p {
            margin: 0 0 1.5rem 0;
            color: var(--secondary-text);
          }
          
          /* Account Details */
          .account-details {
            margin-top: 2rem;
          }
          .account-details h3 {
            margin: 0 0 1.5rem 0;
            font-weight: 600;
            color: var(--primary-text);
          }
          .detail-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 1.5rem;
          }
          .detail-item {
            padding: 1rem;
            background-color: var(--bg-color);
            border-radius: var(--border-radius);
            border: 1px solid var(--border-color);
          }
          .detail-item h4 {
            margin: 0 0 0.5rem 0;
            font-size: 0.9rem;
            font-weight: 500;
            color: var(--secondary-text);
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .detail-item p {
            margin: 0;
            font-size: 1.1rem;
            font-weight: 600;
            color: var(--primary-text);
          }
          .detail-item p.status {
            padding: 0.25rem 0.75rem;
            border-radius: 20px;
            font-size: 0.9rem;
            font-weight: 600;
            text-transform: uppercase;
            display: inline-block;
          }
          .detail-item p.status.active {
            background-color: #d4edda;
            color: #155724;
          }
          .detail-item p.status.inactive {
            background-color: #f8d7da;
            color: #721c24;
          }
          .detail-item p.status.verified {
            background-color: #d4edda;
            color: #155724;
          }
          .detail-item p.status.unverified {
            background-color: #fff3cd;
            color: #856404;
          }

          /* Certificates Tab Styles */
          .certificates-container {
            padding: 0;
            overflow: hidden;
          }
          .certificates-header {
            padding: 2rem;
            text-align: center;
            background-color: #f8f9fa;
            border-bottom: 1px solid var(--border-color);
          }
          .certificates-header h3 {
            margin: 0 0 0.5rem 0;
            font-size: 1.8rem;
            font-weight: 600;
            color: var(--primary-text);
          }
          .certificates-header p {
            margin: 0;
            color: var(--secondary-text);
            font-size: 1rem;
          }
          .loading-container {
            padding: 3rem;
            text-align: center;
            color: var(--secondary-text);
          }
          .loading-spinner {
            width: 40px;
            height: 40px;
            border: 4px solid #f3f3f3;
            border-top: 4px solid var(--accent-green);
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 0 auto 1rem auto;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .table-container {
            padding: 1.5rem;
          }
          .table-container table {
            width: 100%;
            border-collapse: collapse;
            background-color: white;
          }
          .table-container th {
            background-color: var(--dark-bg);
            color: var(--light-text);
            padding: 1rem;
            text-align: left;
            font-weight: 600;
            font-size: 0.9rem;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .table-container td {
            padding: 1rem;
            border-bottom: 1px solid #f1f3f5;
            vertical-align: middle;
          }
          .table-container tbody tr:hover {
            background-color: #f8f9fa;
          }
          .article-title {
            font-weight: 600;
            color: var(--primary-text);
            line-height: 1.4;
          }
          .pe-points {
            background-color: var(--accent-green);
            color: var(--light-text);
            padding: 0.25rem 0.75rem;
            border-radius: 2rem;
            font-size: 0.8rem;
            font-weight: 600;
          }
          .btn-download {
            background-color: var(--dark-bg);
            color: var(--light-text);
            border: none;
            padding: 0.6rem 1rem;
            border-radius: 0.25rem;
            font-size: 0.9rem;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }
          .btn-download:hover {
            background-color: #495057;
            transform: translateY(-1px);
          }
          .no-certificates {
            text-align: center;
            padding: 3rem 1rem;
          }
          .empty-state {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1rem;
          }
          .empty-icon {
            font-size: 4rem;
            opacity: 0.3;
          }
          .empty-state h4 {
            margin: 0;
            font-size: 1.5rem;
            font-weight: 600;
            color: var(--primary-text);
          }
          .empty-state p {
            margin: 0;
            color: var(--secondary-text);
            font-size: 1rem;
          }
          .btn-browse-articles {
            background-color: var(--accent-green);
            color: var(--light-text);
            border: none;
            padding: 0.75rem 1.5rem;
            border-radius: 0.25rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s ease;
            margin-top: 0.5rem;
          }
          .btn-browse-articles:hover {
            background-color: var(--accent-green-hover);
            transform: translateY(-2px);
          .form-group input.readonly-field,
          .form-group select.readonly-field {
            background-color: #f0f0f0 !important;
            color: #888888 !important;
            cursor: not-allowed !important;
            border-color: #cccccc !important;
            opacity: 0.8 !important;
            font-style: italic !important;
          }
          .certificate-card h4 {
            color: var(--primary-text);
            margin-bottom: 0.5rem;
          }
          .cert-info {
            color: var(--secondary-text);
            margin-bottom: 1rem;
          }
          .certificate-download-btn {
            background: var(--accent-green);
            color: var(--light-text);
            border: none;
            padding: 0.75rem 1.5rem;
            border-radius: var(--border-radius);
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
            font-size: 0.95rem;
            width: 100%;
          }
          .certificate-download-btn:hover {
            background: var(--accent-green-hover);
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(25, 135, 84, 0.2);
          }
        `}</style>
        {/* Upgrade Modal */}
        {showUpgradeModal && (
          <div className="upgrade-modal-overlay" onClick={() => setShowUpgradeModal(false)} style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div className="upgrade-modal" onClick={(e) => e.stopPropagation()} style={{
              background: 'var(--card-bg)',
              borderRadius: 'var(--border-radius)',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: 'var(--card-shadow-hover)',
              border: '1px solid var(--border-color)'
            }}>
              <div className="upgrade-modal-header" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1.5rem 2rem',
                borderBottom: '1px solid var(--border-color)',
                background: 'var(--card-bg)'
              }}>
                <h3 style={{ 
                  margin: 0, 
                  color: 'var(--primary-text)', 
                  fontSize: '1.5rem', 
                  fontWeight: 600 
                }}>Upgrade to Professional</h3>
                <button className="modal-close" onClick={() => setShowUpgradeModal(false)} style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: 'var(--secondary-text)',
                  padding: '0.5rem',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }} onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--primary-bg)';
                  e.target.style.color = 'var(--primary-text)';
                }} onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = 'var(--secondary-text)';
                }}>×</button>
              </div>
              
              <div className="upgrade-modal-content" style={{ padding: '2rem' }}>
                <div className="upgrade-plan-details" style={{ marginBottom: '2rem' }}>
                  <h4 style={{ 
                    margin: '0 0 1rem 0', 
                    color: 'var(--accent-green)', 
                    fontSize: '1.3rem', 
                    fontWeight: 600 
                  }}>Professional Plan - €199.99/year (excl VAT)</h4>
                  <div className="upgrade-benefits">
                    <ul style={{ 
                      listStyle: 'none', 
                      padding: 0, 
                      margin: 0,
                      color: 'var(--primary-text)'
                    }}>
                      <li style={{ padding: '0.5rem 0', fontWeight: 500 }}>✓ Earn official CPD points</li>
                      <li style={{ padding: '0.5rem 0', fontWeight: 500 }}>✓ Download accredited certificates</li>
                      <li style={{ padding: '0.5rem 0', fontWeight: 500 }}>✓ Take AI-generated tests</li>
                      <li style={{ padding: '0.5rem 0', fontWeight: 500 }}>✓ Track progress and certification history</li>
                      <li style={{ padding: '0.5rem 0', fontWeight: 500 }}>✓ Priority support</li>
                    </ul>
                  </div>
                </div>
                
                <div className="discount-section" style={{
                  backgroundColor: 'var(--primary-bg)',
                  padding: '1.5rem',
                  borderRadius: 'var(--border-radius)',
                  marginBottom: '2rem',
                  border: '1px solid var(--border-color)'
                }}>
                  <h4 style={{ 
                    margin: '0 0 1rem 0', 
                    color: 'var(--primary-text)', 
                    fontSize: '1.1rem', 
                    fontWeight: 600 
                  }}>Have a discount code?</h4>
                  <div className="discount-input-group" style={{ 
                    display: 'flex', 
                    gap: '0.75rem', 
                    marginBottom: '1rem' 
                  }}>
                    <input
                      type="text"
                      value={discountCode}
                      onChange={handleDiscountCodeChange}
                      placeholder="Enter discount code"
                      className="discount-input"
                      maxLength="50"
                      style={{
                        flex: 1,
                        padding: '0.875rem',
                        border: '1px solid var(--border-color)',
                        borderRadius: 'var(--border-radius)',
                        fontSize: '1rem',
                        transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                        backgroundColor: 'var(--card-bg)'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = 'var(--primary-text)';
                        e.target.style.boxShadow = '0 0 0 3px rgba(0, 0, 0, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--border-color)';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                    <button
                      type="button"
                      onClick={validateDiscountCode}
                      disabled={!discountCode.trim() || isValidatingDiscount}
                      className="validate-discount-btn"
                      style={{
                        backgroundColor: 'var(--accent-green)',
                        color: 'var(--light-text)',
                        border: 'none',
                        padding: '0.75rem 1.5rem',
                        borderRadius: 'var(--border-radius)',
                        cursor: 'pointer',
                        fontWeight: 600,
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!e.target.disabled) {
                          e.target.style.backgroundColor = 'var(--accent-green-hover)';
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = 'var(--card-shadow-hover)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!e.target.disabled) {
                          e.target.style.backgroundColor = 'var(--accent-green)';
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = 'none';
                        }
                      }}
                    >
                      {isValidatingDiscount ? 'Validating...' : 'Apply'}
                    </button>
                  </div>
                  
                  {error && (
                    <div className="error-message" style={{
                      backgroundColor: '#fee',
                      border: '1px solid #fcc',
                      color: '#c33',
                      padding: '1rem',
                      borderRadius: 'var(--border-radius)',
                      fontSize: '0.95rem',
                      marginTop: '1rem'
                    }}>
                      {error}
                    </div>
                  )}
                  
                  {discountValidation && (
                    <div className="discount-success" style={{
                      backgroundColor: '#d4edda',
                      border: '1px solid #c3e6cb',
                      borderRadius: 'var(--border-radius)',
                      padding: '1rem',
                      marginTop: '1rem'
                    }}>
                      <div className="discount-details" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '1rem'
                      }}>
                        <span className="discount-code" style={{
                          color: '#155724',
                          fontWeight: 600
                        }}>✓ {discountValidation.code}</span>
                        <span className="discount-amount" style={{
                          color: '#155724',
                          fontWeight: 600
                        }}>
                          Save {formatPrice(discountValidation.discount_amount)} 
                          ({discountValidation.discount_percentage.toFixed(1)}% off)
                        </span>
                      </div>
                      <div className="price-breakdown" style={{
                        backgroundColor: 'var(--card-bg)',
                        border: '1px solid #c3e6cb',
                        borderRadius: 'var(--border-radius)',
                        padding: '1rem'
                      }}>
                        <div className="price-row" style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: '0.5rem',
                          color: 'var(--primary-text)'
                        }}>
                          <span>Original Price:</span>
                          <span>{formatPrice(discountValidation.original_amount)}</span>
                        </div>
                        <div className="price-row discount-row" style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: '0.5rem',
                          color: '#155724'
                        }}>
                          <span>Discount ({discountValidation.code}):</span>
                          <span>-{formatPrice(discountValidation.discount_amount)}</span>
                        </div>
                        <div className="price-row final-row" style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontWeight: 600,
                          borderTop: '1px solid #c3e6cb',
                          paddingTop: '0.5rem',
                          marginTop: '0.5rem',
                          color: 'var(--primary-text)'
                        }}>
                          <span>Final Price:</span>
                          <span>{formatPrice(discountValidation.final_amount)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="upgrade-actions" style={{
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: 'flex-end'
                }}>
                  <button 
                    className="btn-upgrade-proceed"
                    onClick={handleUpgradeWithDiscount}
                    disabled={upgradeLoading}
                    style={{
                      backgroundColor: 'var(--accent-green)',
                      color: 'var(--light-text)',
                      border: 'none',
                      padding: '1rem 2rem',
                      borderRadius: 'var(--border-radius)',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontSize: '1rem'
                    }}
                    onMouseEnter={(e) => {
                      if (!e.target.disabled) {
                        e.target.style.backgroundColor = 'var(--accent-green-hover)';
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = 'var(--card-shadow-hover)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!e.target.disabled) {
                        e.target.style.backgroundColor = 'var(--accent-green)';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = 'none';
                      }
                    }}
                  >
                    {upgradeLoading ? 'Processing...' : 'Proceed to Payment'}
                  </button>
                  <button 
                    className="btn-cancel"
                    onClick={() => setShowUpgradeModal(false)}
                    disabled={upgradeLoading}
                    style={{
                      backgroundColor: 'var(--secondary-text)',
                      color: 'var(--light-text)',
                      border: 'none',
                      padding: '1rem 2rem',
                      borderRadius: 'var(--border-radius)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontSize: '1rem'
                    }}
                    onMouseEnter={(e) => {
                      if (!e.target.disabled) {
                        e.target.style.backgroundColor = '#545b62';
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = 'var(--card-shadow-hover)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!e.target.disabled) {
                        e.target.style.backgroundColor = 'var(--secondary-text)';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = 'none';
                      }
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Email Confirmation Component
  const EmailConfirmation = ({ token }) => {
    const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'already' | 'error'
    const [message, setMessage] = useState('');

    useEffect(() => {
      const confirm = async () => {
        try {
          const res = await fetch(`${API_URL}/confirm-email/${token}`);
          const data = await res.json();
          if (res.ok) {
            const msg = data.message || 'Email confirmed successfully!';
            setMessage(msg);
            setStatus(data.confirmed ? 'success' : 'already');
          } else {
            setStatus('error');
            setMessage(data.error || 'Invalid or expired confirmation link');
          }
        } catch (e) {
          setStatus('error');
          setMessage('Network error. Please try again.');
        }
      };

      if (token) confirm();
    }, [token]);

    return (
      <div className="auth-container">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          {status === 'loading' && (
            <>
              <h2>Confirming your email…</h2>
              <p>Please wait a moment.</p>
            </>
          )}

          {status !== 'loading' && (
            <>
              <h2>{status === 'success' ? 'Email Confirmed' : status === 'already' ? 'Already Confirmed' : 'Confirmation Failed'}</h2>
              <p>{message}</p>
              <div style={{ marginTop: '1rem' }}>
                <p>Your email is confirmed. You can now complete your subscription.</p>
                <button className="btn-primary" onClick={() => navigate('/subscription-required')}>Proceed to Payment</button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const SubscriptionSuccess = () => {
    const [isVerifying, setIsVerifying] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const location = useLocation();

    useEffect(() => {
      const verifyPayment = async () => {
        try {
          const urlParams = new URLSearchParams(location.search);
          const sessionId = urlParams.get('session_id');
          
          if (!sessionId) {
            setError('Invalid session. Please try again.');
            setIsVerifying(false);
            return;
          }
          
          // Verify the payment and get access token
          const response = await fetch(`${API_URL}/verify-subscription-payment`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              session_id: sessionId
            }),
          });
          
          const result = await response.json();

          if (response.ok) {
            // Payment verified successfully - authenticate user
            localStorage.setItem('token', result.access_token);
            localStorage.setItem('user', JSON.stringify(result.user));
            setUser(result.user);
            setIsAuthenticated(true);
            
            // Clear session data
            sessionStorage.removeItem('pendingSubscriptionUser');
            sessionStorage.removeItem('stripeSessionId');
            
            setMessage(result.message || 'Subscription activated successfully!');
            
            // Redirect to dashboard after a short delay
            setTimeout(() => {
              navigate('/');
            }, 2000);
          } else {
            setError(result.error || 'Payment verification failed');
          }
        } catch (error) {
          console.error('Payment verification error:', error);
          setError('Network error. Please try again.');
        } finally {
          setIsVerifying(false);
        }
      };
      
      verifyPayment();
    }, [location.search, navigate, setUser, setIsAuthenticated]);
                
    return (
      <div className="subscription-result-container">
        <div className="subscription-result-card">
          {isVerifying ? (
            <div className="verification-loading">
              <div className="loading-spinner"></div>
              <h2>Verifying your payment...</h2>
              <p>Please wait while we activate your subscription.</p>
            </div>
          ) : error ? (
            <div className="verification-error">
              <div className="error-icon">⚠️</div>
              <h2>Payment Verification Failed</h2>
              <p>{error}</p>
              <button onClick={() => navigate('/login')} className="btn-primary">
                Back to Login
                </button>
              </div>
          ) : (
            <div className="verification-success">
              <div className="success-icon">✅</div>
              <h2>Welcome to CPD Pro!</h2>
              <p>{message}</p>
              <p>Redirecting to your dashboard...</p>
            </div>
          )}
        </div>

        <style jsx>{`
          .subscription-result-container {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            padding: 2rem;
            background: #f5f5f5;
          }

          .subscription-result-card {
            background: white;
            border-radius: 12px;
            padding: 3rem;
            text-align: center;
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
            max-width: 500px;
            width: 100%;
          }
          
          .loading-spinner {
            width: 50px;
            height: 50px;
            border: 4px solid #f3f3f3;
            border-top: 4px solid #007bff;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 0 auto 1rem;
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          .success-icon, .error-icon {
            font-size: 4rem;
            margin-bottom: 1rem;
          }
          
          .verification-loading h2,
          .verification-error h2,
          .verification-success h2 {
            color: #333;
            margin-bottom: 1rem;
          }
          
          .verification-loading p,
          .verification-error p,
          .verification-success p {
            color: #666;
            margin-bottom: 0.5rem;
          }
          
          .btn-primary {
            background: #007bff;
            color: white;
            border: none;
            padding: 0.75rem 2rem;
            border-radius: 6px;
            font-size: 1rem;
            cursor: pointer;
            margin-top: 1rem;
          }
          
          .btn-primary:hover {
            background: #0056b3;
          }
        `}</style>
      </div>
    );
  };

  const SubscriptionCancelled = () => {
    const [message, setMessage] = useState('');
    
    useEffect(() => {
      // Clear session data when user cancels
      sessionStorage.removeItem('tempPassword');
      sessionStorage.removeItem('stripeSessionId');
      
      setMessage('Your subscription purchase was cancelled. No payment was charged.');
    }, []);
    
    return (
      <div className="subscription-result-container">
        <div className="subscription-result-card">
          <div className="cancellation-info">
            <div className="cancel-icon">❌</div>
            <h2>Subscription Cancelled</h2>
            <p>{message}</p>
            <p>You can try again anytime by logging in and selecting a subscription plan.</p>
            <div className="action-buttons">
              <button onClick={() => navigate('/login')} className="btn-primary">
                Back to Login
              </button>
              <button onClick={() => navigate('/register')} className="btn-secondary">
                Create New Account
              </button>
            </div>
          </div>
        </div>
        
        <style jsx>{`
          .subscription-result-container {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            padding: 2rem;
            background: #f5f5f5;
          }
          
          .subscription-result-card {
            background: white;
            border-radius: 12px;
            padding: 3rem;
            text-align: center;
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
            max-width: 500px;
            width: 100%;
          }
          
          .cancel-icon {
            font-size: 4rem;
            margin-bottom: 1rem;
          }
          
          .cancellation-info h2 {
            color: #333;
            margin-bottom: 1rem;
          }
          
          .cancellation-info p {
            color: #666;
            margin-bottom: 1rem;
          }
          
          .action-buttons {
            display: flex;
            gap: 1rem;
            justify-content: center;
            margin-top: 2rem;
          }

          .btn-primary {
            background: #007bff;
            color: white;
            border: none;
            padding: 0.75rem 2rem;
            borderRadius: 6px;
            font-size: 1rem;
            cursor: pointer;
          }

          .btn-primary:hover {
            background: #0056b3;
          }

          .btn-secondary {
            background: #6c757d;
            color: white;
            border: none;
            padding: 0.75rem 2rem;
            border-radius: 6px;
            font-size: 1rem;
            cursor: pointer;
          }

          .btn-secondary:hover {
            background: #545b62;
          }
        `}</style>
      </div>
    );
  };

  // Verify Certificate Component (public)
  const VerifyCertificate = () => {
    const params = new URLSearchParams(window.location.search);
    const certId = params.get('certificate_id');
    const [state, setState] = useState({ loading: true, data: null, error: null });

    useEffect(() => {
      const run = async () => {
        try {
          const res = await fetch(`${API_URL}/verify-certificate?certificate_id=${encodeURIComponent(certId || '')}`);
          const data = await res.json();
          if (res.ok) setState({ loading: false, data, error: null });
          else setState({ loading: false, data: null, error: data.error || 'Verification failed' });
        } catch (e) {
          setState({ loading: false, data: null, error: 'Network error' });
        }
      };
      run();
    }, [certId]);

    // Styles — mirrors the issued certificate so a reviewer sees one consistent credential.
    const pageStyle = {
      background: '#08111c',
      minHeight: isAuthenticated ? 'calc(100vh - 64px)' : '100vh',
      padding: '3rem 1rem',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      color: '#f8f4ea'
    };
    const cardStyle = {
      width: '100%',
      maxWidth: 820,
      background: '#ffffff',
      color: '#08111c',
      border: '1px solid #dcd6c8',
      overflow: 'hidden'
    };
    const bandStyle = {
      background: '#08111c',
      color: '#f8f4ea',
      borderBottom: '4px solid #c4a35a',
      padding: '1.5rem 1.75rem'
    };
    const bodyStyle = { padding: '1.75rem' };
    const labelStyle = {
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '.2em',
      textTransform: 'uppercase',
      color: '#8a7340'
    };
    const idStyle = {
      margin: '.5rem 0 0',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      fontSize: 28,
      fontWeight: 700,
      letterSpacing: '.08em',
      color: '#08111c'
    };
    const statusStyle = (ok) => ({
      display: 'inline-block',
      padding: '.4rem .75rem',
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '.16em',
      textTransform: 'uppercase',
      color: ok ? '#c4a35a' : '#f8b4b4',
      border: `1px solid ${ok ? 'rgba(196,163,90,.55)' : 'rgba(248,180,180,.5)'}`
    });
    const grid = { display: 'grid', gridTemplateColumns: '180px 1fr', rowGap: '.85rem', columnGap: '1rem' };
    const keyStyle = { color: '#6b6254', fontSize: 14, fontWeight: 600 };
    const valueStyle = { color: '#08111c', fontSize: 14, fontWeight: 600 };
    const noteStyle = {
      marginTop: '1.75rem',
      paddingTop: '1rem',
      borderTop: '1px solid #e2dccd',
      fontSize: 11,
      lineHeight: 1.65,
      color: '#8b8477'
    };

    const statusText = state.loading
      ? 'Checking'
      : state.error
        ? 'Error'
        : state.data && state.data.valid
          ? 'Verified'
          : 'No match';
    const statusOk = !state.error && !!(state.data && state.data.valid);

    return (
      <div style={pageStyle}>
        <div style={{ width: '100%', maxWidth: 820 }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{ fontSize: 20, fontWeight: 700 }}>
              CPD <span style={{ color: '#c4a35a' }}>Pro</span>
            </div>
            <div style={{
              marginTop: 4,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '.22em',
              textTransform: 'uppercase',
              color: '#98a2ad'
            }}>
              Certificate verification
            </div>
          </div>

          <div style={cardStyle}>
            <div style={bandStyle}>
              <span style={statusStyle(statusOk)}>{statusText}</span>
              <div style={{ marginTop: '.9rem', fontSize: 22, fontWeight: 700, letterSpacing: '-.4px' }}>
                {state.loading && 'Checking the register…'}
                {!state.loading && state.error && 'Verification unavailable'}
                {!state.loading && !state.error && (
                  state.data && state.data.valid
                    ? 'This certificate is authentic'
                    : 'No certificate found'
                )}
              </div>
              <div style={{ marginTop: '.4rem', fontSize: 13, color: '#98a2ad' }}>
                {state.loading && 'Looking up the certificate ID.'}
                {!state.loading && state.error && state.error}
                {!state.loading && !state.error && (
                  state.data && state.data.valid
                    ? 'The details below match the record held by CPD Pro.'
                    : 'Check the certificate ID and try again.'
                )}
              </div>
            </div>

            {!state.loading && !state.error && state.data && (
              <div style={bodyStyle}>
                <div style={labelStyle}>Certificate ID</div>
                <div style={idStyle}>{state.data.certificate_id || certId || '—'}</div>

                {state.data.valid && (
                  <div style={{ marginTop: '1.75rem', display: 'grid', ...grid }}>
                    <div style={keyStyle}>Holder</div>
                    <div style={valueStyle}>{state.data.user_name || '—'}</div>
                    <div style={keyStyle}>Activity</div>
                    <div style={valueStyle}>{state.data.article_title || '—'}</div>
                    <div style={keyStyle}>Date completed</div>
                    <div style={valueStyle}>
                      {state.data.completed_at ? new Date(state.data.completed_at).toLocaleDateString() : '—'}
                    </div>
                    <div style={keyStyle}>CPD points</div>
                    <div style={valueStyle}>{Number(state.data.pe_points || 0).toFixed(2)}</div>
                  </div>
                )}

                <div style={noteStyle}>
                  {state.data.valid
                    ? `This page confirms that a certificate with the ID above was issued by CPD Pro and records a
                       completed self-directed CPD activity, including a passed knowledge test. Acceptance of these
                       points remains subject to the requirements of the holder's professional body.`
                    : `CPD Pro holds no certificate with this ID. Certificate IDs are printed on the certificate and
                       are also encoded in the QR code, so scanning the code avoids transcription errors.`}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  const Help = () => {
    const [extStep, setExtStep] = useState(1);
    const [contactForm, setContactForm] = useState({
      name: user?.first_name + ' ' + user?.last_name || '',
      email: user?.email || '',
      subject: '',
      message: ''
    });
    const [contactLoading, setContactLoading] = useState(false);
    const [contactSuccess, setContactSuccess] = useState('');
    const [contactError, setContactError] = useState('');

    const handleContactSubmit = async (e) => {
      e.preventDefault();
      setContactLoading(true);
      setContactError('');
      setContactSuccess('');

      try {
        const response = await fetch(`${API_URL}/contact`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            ...contactForm,
            user_id: user?.id
          })
        });

        const data = await response.json();

        if (response.ok) {
          setContactSuccess('Your message has been sent successfully! We\'ll get back to you within 24 hours.');
          setContactForm({
            name: user?.first_name + ' ' + user?.last_name || '',
            email: user?.email || '',
            subject: '',
            message: ''
          });
        } else {
          setContactError(data.error || 'Failed to send message. Please try again.');
        }
      } catch (error) {
        console.error('Contact form error:', error);
        setContactError('Network error. Please check your connection and try again.');
      } finally {
        setContactLoading(false);
      }
    };

    return (
      <div className="help-container">
        <div className="help-header">
          <h1>{t('help.title', 'Help & Support Center')}</h1>
          <p>{t('help.subtitle', 'Your comprehensive guide to mastering the CPD Pro platform')}</p>
        </div>

        <div className="help-content">
          <div className="help-section">
            <div className="help-section-head">
              <span className="help-section-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                </svg>
              </span>
              <h2>{t('help.gettingStarted', 'Getting Started')}</h2>
            </div>
            <div className="help-cards">
              <div className="help-card">
                <div className="help-card-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
                </div>
                <h3>{t('help.browse.title', 'Browse Content')}</h3>
                <p>{t('help.browse.body', 'Explore our extensive library...')}</p>
              </div>
              <div className="help-card">
                <div className="help-card-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="8" r="6" /><path d="M15.5 13.5 17 22l-5-3-5 3 1.5-8.5" /></svg>
                </div>
                <h3>{t('help.earn.title', 'Earn CPD Points')}</h3>
                <p>{t('help.earn.body', 'With a Professional subscription...')}</p>
              </div>
              <div className="help-card">
                <div className="help-card-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 3v18h18" /><path d="m7 14 3-3 3 3 5-6" /></svg>
                </div>
                <h3>{t('help.track.title', 'Track Progress')}</h3>
                <p>{t('help.track.body', 'Monitor your learning journey...')}</p>
              </div>
            </div>
          </div>

          <div className="help-section">
            <div className="help-section-head">
              <span className="help-section-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m13 2-3 7h6l-3 7" /><path d="M5 12h.01M19 12h.01" />
                </svg>
              </span>
              <h2>{t('help.keyFeatures', 'Key Features')}</h2>
            </div>
            <div className="features-list">
              {[
                t('help.features.ai', 'AI Assistant: Get instant explanations and clarifications on any article content'),
                t('help.features.multi', 'Multi-Language Support: Access content in multiple languages'),
                t('help.features.categories', 'Professional Categories: Content tailored to your specific profession'),
                t('help.features.progress', 'Progress Tracking: Keep track of your learning journey and achievements'),
                t('help.features.certificates', 'Certificate Downloads: Official certificates for completed courses (Professional plan)')
              ].map((s, idx) => {
                const ci = s.indexOf(':');
                return (
                  <div className="feature-item" key={idx}>
                    <span className="feature-check" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                    </span>
                    <span>{ci > 0 ? (<><strong>{s.slice(0, ci)}</strong>{s.slice(ci)}</>) : s}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="help-section help-section--extension">
            <div className="help-section-head">
              <span className="help-section-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M14 7h2a2 2 0 0 1 2 2v2m0 0a2 2 0 1 1 0 4v2a2 2 0 0 1-2 2h-2a2 2 0 0 0-4 0H8a2 2 0 0 1-2-2v-2a2 2 0 1 1 0-4V9a2 2 0 0 1 2-2h2a2 2 0 0 1 4 0z" />
                </svg>
              </span>
              <div>
                <h2>{t('help.extension.title', 'Browser Extension')}</h2>
                <p className="help-section-sub">{t('help.extension.subtitle', '')}</p>
              </div>
            </div>
            <div className="ext-steps">
              {[1, 2, 3, 4, 5].map((n, idx, arr) => (
                <div
                  className={`ext-step${extStep === n ? ' is-on' : ''}`}
                  key={n}
                  onClick={() => setExtStep(n)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExtStep(n); } }}
                  role="button"
                  tabIndex={0}
                  aria-pressed={extStep === n}
                >
                  <div className="ext-step-marker">
                    <div className="ext-step-num">{n}</div>
                    {idx < arr.length - 1 && <span className="ext-step-line" aria-hidden="true" />}
                  </div>
                  <div className="ext-step-body">
                    <div className="ext-step-title">{t(`help.extension.step${n}Title`)}</div>
                    <div className="ext-step-desc">{t(`help.extension.step${n}Desc`)}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="ext-note">{t('help.extension.note', '')}</div>
            <a className="ext-cta" href="https://chromewebstore.google.com/detail/cpd-pro-%E2%80%93-article-quiz/jahjkbeffflpapclognafdfjpcphcfej" target="_blank" rel="noopener noreferrer">
              {t('help.extension.cta', 'Get the extension')}
            </a>
          </div>

          <div className="help-section">
            <div className="help-section-head">
              <span className="help-section-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 18h6" /><path d="M10 22h4" /><path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.3h6c0-1 .4-1.8 1-2.3A7 7 0 0 0 12 2z" />
                </svg>
              </span>
              <h2>{t('help.tips', 'Tips for Success')}</h2>
            </div>
            <ul className="tips-list">
              {(t('help.tipsList', { returnObjects: true }) || []).map((tip, idx) => (
                <li key={idx}>{tip}</li>
              ))}
            </ul>
          </div>

          <div className="help-section">
            <div className="help-section-head">
              <span className="help-section-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 5L2 7" />
                </svg>
              </span>
              <h2>{t('help.contact', 'Contact Us')}</h2>
            </div>
            <div className="contact-section">
              <div className="contact-info">
                <h3>{t('help.getInTouch', 'Get in Touch')}</h3>
                <p>{t('help.contactIntro', "Need help or have questions? We're here to support your professional development journey.")}</p>
                <div className="contact-details">
                  <p><strong>{t('help.emailLabel', 'Email:')}</strong> <a href="mailto:service@protagon.ai">service@protagon.ai</a></p>
                  <p><strong>{t('help.responseTimeLabel', 'Response Time:')}</strong> {t('help.responseTime', 'Within 24 hours (weekdays)')}</p>
                  <p><strong>{t('help.supportHoursLabel', 'Support Hours:')}</strong> {t('help.supportHours', 'Monday - Friday, 9:00 AM - 6:00 PM CET')}</p>
                </div>
              </div>

              <div className="contact-form">
                <h3>{t('help.sendMessage', 'Send us a Message')}</h3>
                {contactSuccess && <div className="success-message">{contactSuccess}</div>}
                {contactError && <div className="error-message">{contactError}</div>}
                
                <form onSubmit={handleContactSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('help.name', 'Name')}</label>
                      <input
                        type="text"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  
                    <div className="form-group">
                      <label>{t('help.subject', 'Subject')}</label>
                    <input
                      type="text"
                      value={contactForm.subject}
                      onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                      placeholder={t('help.subjectPh', 'What can we help you with?')}
                      required
                    />
                  </div>
                  
                    <div className="form-group">
                    <label>{t('help.message', 'Message')}</label>
                    <textarea
                      value={contactForm.message}
                      onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                      placeholder={t('help.messagePh', 'Please describe your question or issue in detail...')}
                      rows="5"
                      required
                    />
                  </div>
                  
                  <button type="submit" disabled={contactLoading} className="contact-submit-btn">
                    {contactLoading ? t('help.submitting', 'Submitting Request...') : t('help.submit', 'Submit Content Request')}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        <style jsx>{`
          .help-container {
            max-width: 1040px;
            margin: 0 auto;
            padding: 2rem 1.5rem 4rem;
            background: #f4f7fb;
            min-height: 100vh;
          }

          .help-header {
            position: relative;
            text-align: center;
            margin-bottom: 2.5rem;
            padding: 2.75rem 2rem;
            border-radius: 20px;
            background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
            color: #fff;
            box-shadow: 0 18px 40px rgba(37, 99, 235, 0.28);
            overflow: hidden;
          }

          .help-header::after {
            content: "";
            position: absolute;
            top: -40%;
            right: -10%;
            width: 320px;
            height: 320px;
            background: radial-gradient(circle, rgba(255,255,255,0.18), transparent 70%);
            pointer-events: none;
          }

          .help-header h1 {
            font-size: 2.4rem;
            color: #fff;
            margin: 0 0 0.5rem;
            font-weight: 800;
            letter-spacing: -0.02em;
          }

          .help-header p {
            font-size: 1.1rem;
            color: rgba(255, 255, 255, 0.9);
            max-width: 620px;
            margin: 0 auto;
          }

          .help-content {
            display: flex;
            flex-direction: column;
            gap: 1.75rem;
          }

          .help-section {
            background: #fff;
            border: 1px solid rgba(15, 23, 42, 0.08);
            border-radius: 18px;
            padding: 1.9rem 2rem;
            box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
          }

          .help-section-head {
            display: flex;
            align-items: center;
            gap: 0.9rem;
            margin-bottom: 1.5rem;
          }

          .help-section-icon {
            flex-shrink: 0;
            width: 42px;
            height: 42px;
            border-radius: 12px;
            background: linear-gradient(135deg, #2563eb, #1d4ed8);
            color: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 6px 16px rgba(37, 99, 235, 0.25);
          }

          .help-section-icon svg { width: 22px; height: 22px; }

          .help-section h2 {
            font-size: 1.5rem;
            color: #0f172a;
            margin: 0;
            font-weight: 800;
            letter-spacing: -0.01em;
          }

          .help-section-sub {
            margin: 0.25rem 0 0;
            color: #64748b;
            font-size: 0.97rem;
            line-height: 1.5;
            max-width: 760px;
          }

          .help-cards {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
            gap: 1.25rem;
          }

          .help-card {
            background: #fff;
            padding: 1.5rem;
            border-radius: 14px;
            border: 1px solid rgba(15, 23, 42, 0.08);
            transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
          }

          .help-card:hover {
            transform: translateY(-4px);
            border-color: rgba(37, 99, 235, 0.25);
            box-shadow: 0 16px 40px rgba(37, 99, 235, 0.14);
          }

          .help-card-icon {
            width: 38px;
            height: 38px;
            border-radius: 11px;
            background: rgba(37, 99, 235, 0.1);
            color: #1d4ed8;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 0.9rem;
          }

          .help-card-icon svg { width: 19px; height: 19px; }

          .help-card h3 {
            font-size: 1.1rem;
            color: #0f172a;
            margin-bottom: 0.5rem;
            font-weight: 700;
          }

          .help-card p {
            color: #64748b;
            line-height: 1.6;
            margin: 0;
            font-size: 0.95rem;
          }

          .features-list {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
            gap: 0.85rem;
          }

          .feature-item {
            display: flex;
            align-items: flex-start;
            gap: 0.75rem;
            padding: 0.95rem 1.1rem;
            background: #f8fafc;
            border: 1px solid rgba(15, 23, 42, 0.06);
            border-radius: 12px;
            color: #334155;
            line-height: 1.5;
            font-size: 0.95rem;
          }

          .feature-check {
            flex-shrink: 0;
            width: 22px;
            height: 22px;
            border-radius: 50%;
            background: rgba(34, 197, 94, 0.14);
            color: #16a34a;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-top: 1px;
          }

          .feature-check svg { width: 13px; height: 13px; }

          .feature-item strong {
            color: #0f172a;
            font-weight: 700;
          }

          /* Browser extension step-by-step roadmap */
          .help-section--extension {
            background: linear-gradient(180deg, #ffffff 0%, #f7faff 100%);
            border-color: rgba(37, 99, 235, 0.18);
          }

          .ext-steps {
            display: flex;
            flex-direction: column;
            margin-top: 0.5rem;
          }

          .ext-step {
            display: flex;
            gap: 1.1rem;
            align-items: stretch;
          }

          .ext-step-marker {
            position: relative;
            flex-shrink: 0;
            width: 40px;
            display: flex;
            flex-direction: column;
            align-items: center;
          }

          .ext-step-num {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: linear-gradient(135deg, #2563eb, #1d4ed8);
            color: #fff;
            font-weight: 800;
            font-size: 1.05rem;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 3px solid #fff;
            box-shadow: 0 6px 16px rgba(37, 99, 235, 0.28);
            z-index: 2;
          }

          .ext-step-line {
            flex: 1;
            width: 3px;
            min-height: 14px;
            margin: 2px 0;
            background: linear-gradient(180deg, #2563eb, rgba(37, 99, 235, 0.25));
            border-radius: 3px;
          }

          .ext-step-body {
            flex: 1;
            background: #fff;
            border: 1px solid rgba(15, 23, 42, 0.08);
            border-left: 4px solid #2563eb;
            border-radius: 12px;
            padding: 0.95rem 1.2rem;
            margin-bottom: 1rem;
            box-shadow: 0 6px 18px rgba(15, 23, 42, 0.05);
          }

          .ext-step:last-child .ext-step-body { margin-bottom: 0; }

          .ext-step-title {
            font-weight: 800;
            color: #0f172a;
            margin-bottom: 0.25rem;
            font-size: 1.02rem;
          }

          .ext-step-desc {
            color: #64748b;
            font-size: 0.95rem;
            line-height: 1.55;
          }

          .ext-note {
            margin-top: 1.4rem;
            background: rgba(37, 99, 235, 0.07);
            border: 1px solid rgba(37, 99, 235, 0.18);
            border-radius: 12px;
            padding: 0.95rem 1.2rem;
            color: #1e40af;
            font-size: 0.93rem;
            line-height: 1.55;
          }

          .ext-cta {
            display: inline-block;
            margin-top: 1.4rem;
            background: linear-gradient(135deg, #2563eb, #1d4ed8);
            color: #fff;
            text-decoration: none;
            font-weight: 700;
            padding: 0.8rem 1.5rem;
            border-radius: 11px;
            box-shadow: 0 6px 16px rgba(37, 99, 235, 0.28);
            transition: transform 0.2s ease, box-shadow 0.2s ease;
          }

          .ext-cta:hover {
            transform: translateY(-2px);
            box-shadow: 0 12px 28px rgba(37, 99, 235, 0.32);
          }

          .tips-list {
            list-style: none;
            padding: 0;
            margin: 0;
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
            gap: 0.7rem;
          }

          .tips-list li {
            padding: 0.9rem 1.1rem 0.9rem 2.4rem;
            background: #f8fafc;
            border: 1px solid rgba(15, 23, 42, 0.06);
            border-radius: 12px;
            color: #334155;
            line-height: 1.5;
            font-size: 0.95rem;
            position: relative;
          }

          .tips-list li::before {
            content: "";
            position: absolute;
            left: 1rem;
            top: 1.15rem;
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: linear-gradient(135deg, #2563eb, #1d4ed8);
          }

          .contact-section {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 2rem;
            margin-top: 1rem;
          }

          .contact-info h3,
          .contact-form h3 {
            font-size: 1.3rem;
            color: #374151;
            margin-bottom: 1rem;
            font-weight: 600;
          }

          .contact-info p {
            color: #6b7280;
            line-height: 1.6;
            margin-bottom: 1rem;
          }

          .contact-details {
            background: #f0f9ff;
            padding: 1.5rem;
            border-radius: 8px;
            margin-top: 1rem;
          }

          .contact-details p {
            margin-bottom: 0.5rem;
            color: #374151;
          }

          .contact-details a {
            color: #3b82f6;
            text-decoration: none;
          }

          .contact-details a:hover {
            text-decoration: underline;
          }

          .contact-form {
            background: #f8fafc;
            padding: 1.5rem;
            border-radius: 8px;
          }

          .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
            margin-bottom: 1rem;
          }

          .form-group {
            margin-bottom: 1rem;
          }

          .form-group label {
            display: block;
            margin-bottom: 0.5rem;
            font-weight: 500;
            color: #374151;
          }

          .form-group input,
          .form-group textarea {
            width: 100%;
            padding: 0.75rem;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 0.95rem;
            transition: border-color 0.2s ease;
            background: #ffffff; /* ensure light background in dark environments */
            color: #111827;      /* readable text color */
          }

          .form-group input::placeholder,
          .form-group textarea::placeholder {
            color: #6b7280; /* muted placeholder */
          }

          /* Fix autofill dark background in Safari/Chrome */
          .form-group input:-webkit-autofill,
          .form-group textarea:-webkit-autofill {
            -webkit-box-shadow: 0 0 0px 1000px #ffffff inset;
            -webkit-text-fill-color: #111827;
            transition: background-color 5000s ease-in-out 0s;
          }

          .form-group input:focus,
          .form-group textarea:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }

          .contact-submit-btn {
            background: #3b82f6;
            color: white;
            padding: 0.875rem 2rem;
            border: none;
            border-radius: 6px;
            font-weight: 500;
            cursor: pointer;
            transition: background-color 0.2s ease;
            font-size: 1rem;
          }

          .contact-submit-btn:hover {
            background: #2563eb;
          }

          .contact-submit-btn:disabled {
            background: #9ca3af;
            cursor: not-allowed;
          }

          .success-message {
            background: #dcfce7;
            color: #166534;
            padding: 1rem;
            border-radius: 6px;
            margin-bottom: 1rem;
            border-left: 4px solid #16a34a;
          }

          .error-message {
            background: #fef2f2;
            color: #dc2626;
            padding: 1rem;
            border-radius: 6px;
            margin-bottom: 1rem;
            border-left: 4px solid #ef4444;
          }

          @media (max-width: 768px) {
            .help-container {
              padding: 1rem;
            }

            .help-cards {
              grid-template-columns: 1fr;
            }

            .contact-section {
              grid-template-columns: 1fr;
            }

            .form-row {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
      </div>
    );
  };
  const AccountManagementView = React.memo(({ user, setUser, articles, completionStatuses, API_URL }) => {
    // Subcomponent: combine certificates UI
    function CombineCertificates({ certificates, API_URL, downloadCertificateFromList }) {
      const [selectedIds, setSelectedIds] = React.useState([]);
      const [busy, setBusy] = React.useState(false);
      const [msg, setMsg] = React.useState('');
      const token = React.useMemo(() => {
        try { return localStorage.getItem('token'); } catch { return null; }
      }, []);

      const toggle = (id) => {
        setMsg('');
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
      };
      const combine = async () => {
        if (selectedIds.length === 0) return;
        if (selectedIds.length > 10) { setMsg('You can combine at most 10 certificates.'); return; }
        setBusy(true);
        try {
          const res = await fetch(`${API_URL}/combined-certificates`, {
            method: 'POST',
            headers: { 'Content-Type':'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ completion_ids: selectedIds, title: 'Combined Certificate' })
          });
          const data = await res.json();
          if (!res.ok) { setMsg(data.error || 'Failed to create combined certificate'); }
          else {
            setMsg('Combined certificate created.');
            // Fetch combined certificate HTML and generate PDF client-side
            try {
              const res2 = await fetch(`${API_URL}/combined-certificates/${data.id}/download`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              const html2 = await res2.text();
              if (!res2.ok) {
                setMsg('Failed to download combined certificate');
              } else {
                if (typeof renderHtmlToPdf === 'function') {
                  await renderHtmlToPdf(html2, `combined-certificate-${data.id}.pdf`);
                } else {
                  // Minimal fallback: open in new tab
                  const blob = new Blob([html2], { type: 'text/html' });
                  const url = URL.createObjectURL(blob);
                  window.open(url, '_blank');
                }
              }
            } catch (_e) {
              setMsg('Download failed');
            }
            setSelectedIds([]);
          }
        } catch (e) {
          setMsg(e.message);
        } finally {
          setBusy(false);
        }
      };

      return (
        <>
          <div style={{display:'flex', alignItems:'center', gap:'.5rem', margin:'0 0 .5rem'}}>
            <button className="btn-primary" disabled={busy || selectedIds.length===0 || selectedIds.length>10} onClick={combine}>
              {busy ? 'Combining...' : `Combine Selected (${selectedIds.length}/10)`}
            </button>
            {msg && <span className="muted">{msg}</span>}
          </div>
          <div className="certificates-grid">
            {certificates.map((cert, index) => (
              <div key={index} className={`certificate-card ${selectedIds.includes(cert.id) ? 'selected' : ''}`}>
                <label style={{display:'flex', alignItems:'center', gap:'.5rem'}}>
                  <input type="checkbox" checked={selectedIds.includes(cert.id)} onChange={()=>toggle(cert.id)} />
                  <h4 style={{margin:0}}>{cert.article_title}</h4>
                </label>
                <p className="cert-info">
                  {cert.pe_points} PE Points • {new Date(cert.completed_at).toLocaleDateString()}
                </p>
                <div style={{display:'flex', gap:'.5rem', marginTop:'auto'}}>
                  <button 
                    className="certificate-download-btn"
                    onClick={() => downloadCertificateFromList(cert.article_id)}
                  >
                    Download Certificate
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      );
    }
    // Keep input focus stable while editing objective fields
    const titleRefs = useRef({});
    const notesRefs = useRef({});
    const lastFocusRef = useRef({ id: null, field: null, caret: null });

    // Local objectives state to avoid parent re-renders stealing focus while typing
    const parseObjectiveNotes = (raw) => {
      try {
        const obj = JSON.parse(raw);
        if (obj && typeof obj === 'object') {
          return { text: obj.text || '', achieved: !!obj.achieved };
        }
      } catch {}
      return { text: raw || '', achieved: false };
    };

    const [localObjectives, setLocalObjectives] = useState(() => {
      try {
        const arr = JSON.parse(sessionStorage.getItem('objectives') || '[]');
        return (arr || []).map(o => ({ ...o, achieved: !!o.achieved, collapsed: o.collapsed !== false }));
      } catch { return []; }
    });
    const [objSaving, setObjSaving] = useState({}); // id -> 'saving' | 'saved' | 'error'
    const [objError, setObjError] = useState('');
    const [activeTab, setActiveTab] = useState(() => {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get('tab');
      try { const saved = sessionStorage.getItem('accountActiveTab'); if (saved) return saved; } catch {}
      return fromUrl || 'overview';
    });

    // Persist active tab (do not touch URL on each keystroke to avoid focus loss)
    useEffect(() => {
      try { sessionStorage.setItem('accountActiveTab', activeTab); } catch {}
    }, [activeTab]);

    // Persist objectives to sessionStorage with debounce to avoid focus loss during typing
    const debouncedObjectives = useDebounce(localObjectives, 400);
    useEffect(() => {
      try { sessionStorage.setItem('objectives', JSON.stringify(debouncedObjectives)); } catch {}
    }, [debouncedObjectives]);
    // Load objectives from backend on first mount (if logged in)
    useEffect(() => {
      let ignore = false;
      const load = async () => {
        try {
          const token = localStorage.getItem('token');
          if (!token) return;
          const resp = await fetch(`${API_URL}/objectives`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!resp.ok) return;
          const data = await resp.json();
          if (!ignore && Array.isArray(data)) {
            const mapped = data.map(o => {
              const parsed = parseObjectiveNotes(o.notes);
              return {
              id: Date.now() + Math.random(), // local id
              serverId: o.id,
              title: o.title || '',
              notes: parsed.text,
              achieved: parsed.achieved,
              linkedArticleIds: Array.isArray(o.linked_article_ids) ? o.linked_article_ids : (o.linked_article_ids || []),
              collapsed: true
              };
            });
            setLocalObjectives(mapped);
          }
        } catch (e) {
          // ignore errors silently in UI
        }
      };
      load();
      return () => { ignore = true; };
    }, [API_URL]);

    // Save one objective to backend (create or update)
    const saveObjective = async (obj) => {
      try {
        setObjError('');
        setObjSaving(prev => ({ ...prev, [obj.id]: 'saving' }));
        const token = localStorage.getItem('token');
        if (!token) throw new Error('not_authenticated');
        const payload = {
          title: obj.title || '',
          // store notes as JSON to also persist achieved without DB migration
          notes: JSON.stringify({ text: obj.notes || '', achieved: !!obj.achieved }),
          linked_article_ids: obj.linkedArticleIds || []
        };
        let resp;
        if (obj.serverId) {
          resp = await fetch(`${API_URL}/objectives/${obj.serverId}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(payload)
          });
        } else {
          resp = await fetch(`${API_URL}/objectives`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(payload)
          });
        }
        if (!resp.ok) throw new Error('save_failed');
        const saved = await resp.json();
        const serverId = saved?.id || obj.serverId;
        setLocalObjectives(prev => prev.map(o => o.id === obj.id ? { ...o, serverId, collapsed: true } : o));
        setObjSaving(prev => ({ ...prev, [obj.id]: 'saved' }));
        setTimeout(() => setObjSaving(prev => ({ ...prev, [obj.id]: undefined })), 1500);
      } catch (e) {
        setObjSaving(prev => ({ ...prev, [obj.id]: 'error' }));
        setObjError('Failed to save objective');
      }
    };

    // Delete objective on server if it exists
    const deleteObjective = async (obj) => {
      const token = localStorage.getItem('token');
      // Optimistic local removal
      setLocalObjectives(prev => prev.filter(o => o.id !== obj.id));
      if (!obj.serverId || !token) return;
      try {
        await fetch(`${API_URL}/objectives/${obj.serverId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (_) {
        // ignore
      }
    };

    // Article filters for linking
    const [linkFilterCompleted, setLinkFilterCompleted] = useState('all'); // all|completed|not
    const [linkFilterType, setLinkFilterType] = useState('all'); // all|youtube|article
    const isYoutube = useCallback((a) => !!(a?.external_url && (a.external_url.includes('youtube.com') || a.external_url.includes('youtu.be'))), []);
    const filteredArticles = useMemo(() => {
      return (articles || []).filter(a => {
        if (linkFilterType === 'youtube' && !isYoutube(a)) return false;
        if (linkFilterType === 'article' && isYoutube(a)) return false;
        if (linkFilterCompleted !== 'all') {
          const done = !!completionStatuses?.[a.id]?.completed;
          if (linkFilterCompleted === 'completed' && !done) return false;
          if (linkFilterCompleted === 'not' && done) return false;
        }
        return true;
      });
    }, [articles, completionStatuses, linkFilterCompleted, linkFilterType, isYoutube]);

    // After any debounced re-render, restore focus if it was lost
    useEffect(() => {
      const { id, field, caret } = lastFocusRef.current || {};
      if (!id || !field) return;
      const node = field === 'title' ? titleRefs.current[id] : notesRefs.current[id];
      if (node && document.activeElement !== node) {
        try {
          node.focus();
          if (typeof caret === 'number') {
            node.setSelectionRange(caret, caret);
          }
        } catch {}
      }
    });

    // No parent state sync here to prevent re-renders that can steal focus
    const [upgradeLoading, setUpgradeLoading] = useState(false);
    const [activityHistory, setActivityHistory] = useState([]);
    const [activityLoading, setActivityLoading] = useState(false);
    const [certificates, setCertificates] = useState([]);
    const [certificatesLoading, setCertificatesLoading] = useState(false);
    const [profileData, setProfileData] = useState({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      phone_country_code: user?.phone_country_code || '+31',
      profession: user?.profession || '',
      secondary_profession: user?.secondary_profession || '',
      company: user?.company || '',
      job_title: user?.job_title || '',
      country: user?.country || '',
      address: user?.address || '',
      city: user?.city || '',
      state: user?.state || '',
      postal_code: user?.postal_code || ''
    });
    const [profileLoading, setProfileLoading] = useState(false);
    const [profileError, setProfileError] = useState('');
    const [profileSuccess, setProfileSuccess] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    // Upgrade modal state
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [discountCode, setDiscountCode] = useState('');
    const [discountValidation, setDiscountValidation] = useState(null);
    const [isValidatingDiscount, setIsValidatingDiscount] = useState(false);

    // Article request state
    const [requestForm, setRequestForm] = useState({
      language: 'English',
      profession_category: user?.profession || '',
      description: '',
      topic: ''
    });
    const [requestLoading, setRequestLoading] = useState(false);
    const [requestSuccess, setRequestSuccess] = useState('');
    const [requestError, setRequestError] = useState('');

    // Format price helper function
    const formatPrice = (cents) => {
      return `€${(cents / 100).toFixed(2)}`;
    };

    // Fetch activity history when history tab is selected
    const fetchActivityHistory = async () => {
      try {
        setActivityLoading(true);
        const response = await fetch(`${API_URL}/users/${user.id}/activity-history`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setActivityHistory(data.activities || []);
        } else {
          console.error('Failed to fetch activity history');
          setActivityHistory([]);
        }
      } catch (error) {
        console.error('Error fetching activity history:', error);
        setActivityHistory([]);
      } finally {
        setActivityLoading(false);
      }
    };

    // Fetch certificates
    const fetchCertificates = async () => {
      try {
        setCertificatesLoading(true);
        const response = await fetch(`${API_URL}/users/${user.id}/certificates`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setCertificates(data.certificates || []);
        } else {
          console.error('Failed to fetch certificates');
          setCertificates([]);
        }
      } catch (error) {
        console.error('Error fetching certificates:', error);
        setCertificates([]);
      } finally {
        setCertificatesLoading(false);
      }
    };

    // Load data when tabs are activated
    useEffect(() => {
      if (activeTab === 'overview') {
        // Load certificates for dashboard stats on overview tab
        fetchCertificates();
      } else if (activeTab === 'history' && activityHistory.length === 0) {
        fetchActivityHistory();
      } else if (activeTab === 'certificates' && certificates.length === 0) {
        fetchCertificates();
      }
    }, [activeTab]);

    const handleProfileUpdate = async (e) => {
      e.preventDefault();
      setProfileLoading(true);
      setProfileError('');
      setProfileSuccess('');

      try {
        const response = await fetch(`${API_URL}/users/${user.id}/profile`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(profileData)
        });

        const data = await response.json();

        if (response.ok) {
          setProfileSuccess('Profile updated successfully!');
          setUser({ ...user, ...profileData });
        } else {
          setProfileError(data.error || 'Failed to update profile');
        }
      } catch (error) {
        console.error('Error updating profile:', error);
        setProfileError('Network error. Please try again.');
      } finally {
        setProfileLoading(false);
      }
    };

    const downloadCertificateFromList = async (certificateId) => {
      try {
        const token = localStorage.getItem('token') || '';
        const res = await fetch(`${API_URL}/certificate/${certificateId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const html = await res.text();
        if (!res.ok) {
          try {
            const err = JSON.parse(html);
            alert(err.error || 'Failed to load certificate');
          } catch {
            alert('Failed to load certificate');
          }
          return;
        }
        if (typeof renderHtmlToPdf === 'function') {
          await renderHtmlToPdf(html, `certificate-${certificateId}.pdf`);
        } else {
          // Last-resort fallback: open in new tab
          const blob = new Blob([html], { type: 'text/html' });
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank');
        }
      } catch (error) {
        console.error('Error downloading certificate:', error);
        alert('Error downloading certificate');
      }
    };

    const getCurrentPlanInfo = () => {

      const currentPlan = user?.subscription_plan;
      const isActive = hasPlatformAccess(user);

      if (isActive) {
        if (currentPlan === 'Professional' || currentPlan === 'PROFESSIONAL' || currentPlan === 'price_1TrMOMIkwUVtcv9M3cZ3qhGX') {
          return { name: 'Professional', price: '€199.99/year (excl VAT)', features: ['Earn official CPD points worldwide', 'Download accredited certificates', 'Take short tests (5 AI-generated questions)', 'Request favorite articles for CPD points', 'Track progress and certification history', 'Priority support'] };
        } else if (currentPlan === 'Professional Monthly' || currentPlan === 'PROFESSIONAL_MONTHLY' || currentPlan === 'price_1TrMaeIkwUVtcv9MDRqJUJ0N') {
          return { name: 'Professional Monthly', price: '€19.99/month (excl VAT)', features: ['Everything in Professional Insights', 'Earn official CPD points (2 per month allowance, rolls over)', 'Download accredited certificates', 'Take short tests (5 AI-generated questions)', 'Track progress and certification history'] };
        } else if (currentPlan === 'Professional Insights' || currentPlan === 'ProfessionalInsights' || currentPlan === 'PROFESSIONAL_INSIGHTS' || currentPlan === 'price_1RtSANIkwUVtcv9MtC4FqshW') {
          return { name: 'Professional Insights', price: 'Free', features: ['Read unlimited articles worldwide', 'Learn across multiple professions', 'All articles in one global platform', 'Email support'] };
        }
      }
      
      return { name: 'No active subscription', price: '', features: [] };
    };

    const currentPlanInfo = getCurrentPlanInfo();

    // Handle subscription management
    const handleManageSubscription = async () => {
      setUpgradeLoading(true);
      setError('');

      try {
        const response = await fetch(`${API_URL}/stripe/create-portal-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            userId: user.id
          })
        });

        const data = await response.json();
        
        if (response.ok && data.url) {
          window.location.href = data.url;
        } else {
          // Handle specific error types
          if (data.error_type === 'configuration_error') {
            setMessage('The billing portal is temporarily unavailable. Please contact support at service@protagon.ai to manage your subscription.');
          } else if (data.error_type === 'stripe_error') {
            setError('Unable to access billing portal. Please try again in a few minutes.');
          } else {
            setError(data.error || 'Failed to open subscription management. Please try again.');
          }
        }
      } catch (error) {
        console.error('Portal error:', error);
        setError('Network error. Please check your internet connection and try again.');
      } finally {
        setUpgradeLoading(false);
      }
    };

    // Handle discount code validation
    const handleDiscountCodeChange = (e) => {
      setDiscountCode(e.target.value);
      if (discountValidation) {
        setDiscountValidation(null);
      }
      setError('');
    };

    const validateDiscountCode = async () => {
      if (!discountCode.trim()) {
        setDiscountValidation(null);
        return;
      }

      setIsValidatingDiscount(true);
      setError('');
      
      try {
        const response = await fetch(`${API_URL}/validate-discount-code`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            code: discountCode.trim(),
            user_id: user?.id,
            plan_id: 'price_1TrMOMIkwUVtcv9M3cZ3qhGX', // Professional plan
            amount: 19999 // €199.99 in cents
          }),
        });

        const data = await response.json();

        if (response.ok) {
          setDiscountValidation({
            valid: true,
            code: discountCode.trim(),
            discount_amount: data.discount_amount,
            final_amount: data.final_amount,
            original_amount: data.original_amount || 19999,
            discount_percentage: data.discount_percentage
          });
          setError('');
        } else {
          setDiscountValidation(null);
          setError(data.error || 'Invalid discount code');
        }
      } catch (error) {
        console.error('Error validating discount code:', error);
        setDiscountValidation(null);
        setError('Failed to validate discount code. Please try again.');
      } finally {
        setIsValidatingDiscount(false);
      }
    };

    const handleUpgradeWithDiscount = async () => {
      setUpgradeLoading(true);
      setError('');

      try {
        // Prepare request body based on validation state
        const requestBody = {
          userId: user.id,
          priceId: 'price_1TrMOMIkwUVtcv9M3cZ3qhGX', // Professional plan price ID
          planType: 'PROFESSIONAL' // Upgrading from Professional Insights to Professional
        };

        // Add discount code if validated
        if (discountValidation && discountValidation.valid) {
          requestBody.discountCode = discountCode;
        }

        const response = await fetch(`${API_URL}/stripe/create-checkout-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(requestBody)
        });

        const data = await response.json();
        
        if (response.ok && data.url) {
          window.location.href = data.url;
        } else {
          setError(data.error || 'Failed to initiate payment. Please try again.');
        }
      } catch (error) {
        console.error('Error creating checkout session:', error);
        setError('Failed to initiate payment. Please try again.');
      } finally {
        setUpgradeLoading(false);
      }
    };

    // Handle article request submission
    const handleArticleRequest = async (e) => {
      e.preventDefault();
      setRequestLoading(true);
      setRequestError('');
      setRequestSuccess('');

      try {
        const response = await fetch(`${API_URL}/article-requests`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            ...requestForm,
            user_id: user.id
          })
        });

        const data = await response.json();

        if (response.ok) {
          setRequestSuccess('Your article request has been submitted successfully! Our team will review it and consider adding relevant content.');
          setRequestForm({
            language: 'English',
            profession_category: user?.profession || '',
            description: '',
            topic: ''
          });
        } else {
          setRequestError(data.error || 'Failed to submit request. Please try again.');
        }
      } catch (error) {
        console.error('Error submitting article request:', error);
        setRequestError('Network error. Please try again.');
      } finally {
        setRequestLoading(false);
      }
    };
    return (
      <div className="account-management">
        <div className="account-header">
          <h1>{t('account.title', 'Account Management')}</h1>
          <p>{t('account.subtitle', 'Manage your account settings and preferences')}</p>
        </div>

        {showUpgradeModal && (
          <div
            className="upgrade-modal-overlay"
            onClick={() => setShowUpgradeModal(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
          >
            <div
              className="upgrade-modal"
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'var(--card-bg)',
                borderRadius: 'var(--border-radius)',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: 'var(--card-shadow-hover)',
                border: '1px solid var(--border-color)'
              }}
            >
              <div
                className="upgrade-modal-header"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '1.5rem 2rem',
                  borderBottom: '1px solid var(--border-color)',
                  background: 'var(--card-bg)'
                }}
              >
                <h3 style={{ margin: 0, color: 'var(--primary-text)', fontSize: '1.5rem', fontWeight: 600 }}>
                  Upgrade to Professional
                </h3>
                <button
                  className="modal-close"
                  onClick={() => setShowUpgradeModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: 'var(--secondary-text)',
                    padding: '0.5rem',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                >
                  ×
                </button>
              </div>

              <div className="upgrade-modal-content" style={{ padding: '2rem' }}>
                <div className="upgrade-plan-details" style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ margin: '0 0 1rem 0', color: 'var(--accent-green)', fontSize: '1.3rem', fontWeight: 600 }}>
                    Professional Plan - €199.99/year (excl VAT)
                  </h4>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, color: 'var(--primary-text)' }}>
                    <li style={{ padding: '0.5rem 0', fontWeight: 500 }}>✓ Earn official CPD points</li>
                    <li style={{ padding: '0.5rem 0', fontWeight: 500 }}>✓ Download accredited certificates</li>
                    <li style={{ padding: '0.5rem 0', fontWeight: 500 }}>✓ Take AI-generated tests</li>
                    <li style={{ padding: '0.5rem 0', fontWeight: 500 }}>✓ Track progress and certification history</li>
                    <li style={{ padding: '0.5rem 0', fontWeight: 500 }}>✓ Priority support</li>
                  </ul>
                </div>

                <div className="discount-section" style={{
                  backgroundColor: 'var(--primary-bg)',
                  padding: '1.25rem',
                  borderRadius: 'var(--border-radius)',
                  marginBottom: '1.5rem',
                  border: '1px solid var(--border-color)'
                }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', color: 'var(--primary-text)', fontSize: '1.1rem', fontWeight: 600 }}>
                    Have a discount code?
                  </h4>
                  <div className="discount-input-group" style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <input
                      type="text"
                      placeholder="Enter discount code"
                      value={discountCode}
                      onChange={handleDiscountCodeChange}
                      className="discount-input"
                      style={{
                        flex: 1,
                        padding: '0.875rem',
                        border: '1px solid var(--border-color)',
                        borderRadius: 'var(--border-radius)',
                        fontSize: '1rem',
                        backgroundColor: 'var(--card-bg)',
                        color: 'var(--primary-text)'
                      }}
                    />
                    <button
                      onClick={validateDiscountCode}
                      disabled={isValidatingDiscount || !discountCode.trim()}
                      className="validate-discount-btn"
                      style={{
                        padding: '0.875rem 1rem',
                        backgroundColor: 'var(--dark-bg)',
                        color: 'var(--light-text)',
                        border: 'none',
                        borderRadius: 'var(--border-radius)',
                        fontWeight: 600,
                        cursor: isValidatingDiscount || !discountCode.trim() ? 'not-allowed' : 'pointer',
                        opacity: isValidatingDiscount || !discountCode.trim() ? 0.6 : 1
                      }}
                    >
                      {isValidatingDiscount ? 'Validating...' : 'Apply'}
                    </button>
                  </div>

                  {error && <div className="error-message">{error}</div>}
                  {discountValidation && discountValidation.valid && (
                    <div className="discount-success">
                      <div className="discount-details">
                        <strong>✓ {discountValidation.code}</strong>{' '}
                        <span>({discountValidation.discount_percentage.toFixed(1)}% off)</span>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  className="btn-primary"
                  onClick={handleUpgradeWithDiscount}
                  disabled={upgradeLoading}
                >
                  {upgradeLoading ? 'Processing...' : 'Upgrade to Professional'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="account-tabs">
          <button 
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActiveTab('overview'); }}
          >
            {t('account.tabs.overview', 'Overview')}
          </button>
          <button 
            className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActiveTab('profile'); }}
          >
            {t('account.tabs.profile', 'Profile')}
          </button>
          <button 
            className={`tab-button ${activeTab === 'subscription' ? 'active' : ''}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActiveTab('subscription'); }}
          >
            {t('account.tabs.subscription', 'Subscription')}
          </button>
          <button 
            className={`tab-button ${activeTab === 'certificates' ? 'active' : ''}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActiveTab('certificates'); }}
          >
            {t('account.tabs.certificates', 'Certificates')}
          </button>
          <button 
            className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActiveTab('history'); }}
          >
            {t('account.tabs.history', 'Activity History')}
          </button>
          <button 
            className={`tab-button ${activeTab === 'requests' ? 'active' : ''}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActiveTab('requests'); }}
          >
            {t('account.tabs.requests', 'Request Content')}
          </button>
          <button 
            className={`tab-button ${activeTab === 'objectives' ? 'active' : ''}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActiveTab('objectives'); }}
          >
            {t('account.tabs.objectives', 'Objectives')}
          </button>
        </div>

        <div className="account-content">
          {activeTab === 'overview' && (
            <div className="overview-tab">
              <div className="account-stats">
                <div className="stat-card">
                  <h3>{t('account.currentPlan', 'Current Plan')}</h3>
                  <p className="stat-value">{currentPlanInfo.name}</p>
                  <p className="stat-description">{currentPlanInfo.price}</p>
                </div>
                <div className="stat-card">
                  <h3>{t('account.pePointsEarned', 'PE Points Earned')}</h3>
                  <p className="stat-value">{user?.pe_points_earned || 0}</p>
                  <p className="stat-description">{t('account.totalPoints', 'Total points')}</p>
                </div>
                <div className="stat-card">
                  <h3>{t('account.certificates', 'Certificates')}</h3>
                  <p className="stat-value">{certificates.length}</p>
                  <p className="stat-description">{t('account.downloadedCertificates', 'Downloaded certificates')}</p>
                </div>
                <div className="stat-card">
                  <h3>{t('account.status', 'Account Status')}</h3>
                  <p className="stat-value">{hasPlatformAccess(user) ? t('account.active', 'Active') : t('account.inactive', 'Inactive')}</p>
                  <p className="stat-description">
                    {user?.subscription_status !== 'active' && user?.access_until && hasPlatformAccess(user)
                      ? t('account.accessUntil', 'Cancelled — access until {{date}}', { date: new Date(user.access_until).toLocaleDateString() })
                      : t('account.subscriptionStatus', 'Subscription status')}
                  </p>
                </div>
              </div>
              
              <div className="welcome-section">
                <h2>{t('account.welcomeBack', 'Welcome back, {{name}}!', { name: user?.first_name })}</h2>
                <p>{t('account.welcomeSub', 'Manage your account settings, view your progress, and download your certificates all in one place.')}</p>
              </div>

              
            </div>
          )}

          {activeTab === 'profile' && (
            <div className="profile-tab">
              <h2>{t('account.profileInfo', 'Profile Information')}</h2>
              {profileError && <div className="error-message">{profileError}</div>}
              {profileSuccess && <div className="success-message">{profileSuccess}</div>}
              
              <form onSubmit={handleProfileUpdate} className="profile-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('account.firstName', 'First Name')}</label>
                    <input
                      type="text"
                      value={profileData.first_name}
                      readOnly
                      className="readonly-field"
                      title="This field cannot be edited. Contact support if changes are needed."
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('account.lastName', 'Last Name')}</label>
                    <input
                      type="text"
                      value={profileData.last_name}
                      readOnly
                      className="readonly-field"
                      title="This field cannot be edited. Contact support if changes are needed."
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('account.email', 'Email')}</label>
                    <input
                      type="email"
                      value={profileData.email}
                      readOnly
                      className="readonly-field"
                      title="Email cannot be changed. Contact support if needed."
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('account.profession', 'Profession')}</label>
                    <select
                      value={profileData.profession}
                      onChange={(e) => setProfileData({...profileData, profession: e.target.value})}
                      required
                    >
                      <option value="">{t('account.selectProfession', 'Select Profession')}</option>
                      {PROFESSION_CATEGORIES.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('account.secondaryProfession', 'Secondary Profession (optional)')}</label>
                    <select
                      value={profileData.secondary_profession || ''}
                      onChange={(e) => setProfileData({...profileData, secondary_profession: e.target.value})}
                    >
                      <option value="">{t('none', 'None')}</option>
                      {PROFESSION_CATEGORIES.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>


                <div className="form-row">
                  <div className="form-group">
                    <label>{t('account.phone', 'Phone Number')}</label>
                    <input
                      type="tel"
                      name="phone"
                      value={profileData.phone}
                      readOnly
                      className="readonly-field"
                      placeholder={t('account.phonePlaceholder', 'Your phone number')}
                      title="Phone number cannot be changed. Contact support if needed."
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('account.company', 'Company')}</label>
                    <input
                      type="text"
                      value={profileData.company}
                      onChange={(e) => setProfileData({...profileData, company: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('account.jobTitle', 'Job Title')}</label>
                    <input
                      type="text"
                      value={profileData.job_title}
                      onChange={(e) => setProfileData({...profileData, job_title: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('account.country', 'Country')}</label>
                    <input
                      type="text"
                      value={profileData.country}
                      readOnly
                      className="readonly-field"
                      title="Country cannot be changed. Contact support if needed."
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('account.address', 'Address')}</label>
                    <input
                      type="text"
                      value={profileData.address}
                      onChange={(e) => setProfileData({...profileData, address: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('account.city', 'City')}</label>
                    <input
                      type="text"
                      value={profileData.city}
                      readOnly
                      className="readonly-field"
                      title="City cannot be changed. Contact support if needed."
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('account.state', 'State/Province')}</label>
                    <input
                      type="text"
                      value={profileData.state}
                      readOnly
                      className="readonly-field"
                      title="State/Province cannot be changed. Contact support if needed."
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('account.postalCode', 'Postal Code')}</label>
                    <input
                      type="text"
                      value={profileData.postal_code}
                      readOnly
                      className="readonly-field"
                      title="Postal Code cannot be changed. Contact support if needed."
                    />
                  </div>
                </div>

                  <button type="submit" className="btn-primary" disabled={profileLoading}>
                    {profileLoading ? t('updating', 'Updating...') : t('account.updateProfile', 'Update Profile Information')}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'objectives' && (
            (hasPlatformAccess(user) && ['Professional','PROFESSIONAL','Professional Monthly','PROFESSIONAL_MONTHLY','price_1TrMOMIkwUVtcv9M3cZ3qhGX','price_1TrMaeIkwUVtcv9MDRqJUJ0N','prod_SewzWTOox1xOug'].includes(user?.subscription_plan)) ? (
            <div className="objectives-tab pop">
              <div className="pop-head">
                <div className="pop-head-text">
                  <h2>{t('account.objectives.title', 'Objectives')}</h2>
                  <p className="muted">{t('account.objectives.subtitle', 'Your Personal Development Plan (POP): set focused learning objectives and link the articles or videos you complete to track your growth.')}</p>
                </div>
                <button className="pop-add-btn" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLocalObjectives([...localObjectives, { id: Date.now(), title: '', notes: '', achieved: false, linkedArticleIds: [], collapsed: false }]); }}>
                  <span className="pop-add-plus">+</span> {t('account.objectives.add', 'Add objective')}
                </button>
              </div>

              {localObjectives.length > 0 && (() => {
                const total = localObjectives.length;
                const achieved = localObjectives.filter(o => o.achieved).length;
                const pct = total ? Math.round((achieved / total) * 100) : 0;
                const linkedTotal = localObjectives.reduce((s, o) => s + (o.linkedArticleIds?.length || 0), 0);
                const linkedDone = localObjectives.reduce((s, o) => s + (o.linkedArticleIds || []).filter(id => completionStatuses?.[id]?.completed).length, 0);
                return (
                  <div className="pop-overview">
                    <div className="pop-overview-head">
                      <span className="pop-overview-title">{t('account.objectives.planProgress', 'Plan progress')}</span>
                      <span className="pop-overview-pct">{pct}%</span>
                    </div>
                    <div className="pop-progress"><div className="pop-progress-fill" style={{ width: `${pct}%` }} /></div>
                    <div className="pop-overview-stats">
                      <div className="pop-stat"><span className="pop-stat-num">{total}</span><span className="pop-stat-label">{t('account.objectives.statObjectives', 'Objectives')}</span></div>
                      <div className="pop-stat"><span className="pop-stat-num">{achieved}</span><span className="pop-stat-label">{t('account.objectives.statAchieved', 'Achieved')}</span></div>
                      <div className="pop-stat"><span className="pop-stat-num">{linkedDone}/{linkedTotal}</span><span className="pop-stat-label">{t('account.objectives.statContent', 'Content completed')}</span></div>
                    </div>
                  </div>
                );
              })()}

              {objError && <div className="error-text" style={{color:'#b91c1c', background:'#fee2e2', border:'1px solid #fecaca', padding:'.5rem .75rem', borderRadius:'var(--border-radius)', margin:'0 0 1rem'}}>{t('saveFailed','Failed to save objective')}</div>}
              <div className="objectives-list">
                {localObjectives.length === 0 && <p className="muted">{t('account.objectives.empty', 'No objectives yet.')}</p>}
                {localObjectives.filter(o => !o.collapsed).map(obj => (
                  <div key={obj.id} className="objective-card" style={{
                    border:'1px solid var(--border-color)',
                    borderLeft:'4px solid #2563eb',
                    borderRadius:'var(--border-radius)',
                    padding:'1rem',
                    background:'var(--card-bg)',
                    boxShadow:'var(--card-shadow)'
                  }}>
                    <div className="objective-header">
                      <input
                        type="text"
                        value={obj.title}
                        ref={(el) => { if (el) titleRefs.current[obj.id] = el; }}
                        onChange={(e) => {
                          const caret = e.target.selectionStart;
                          const value = e.target.value;
                          lastFocusRef.current = { id: obj.id, field: 'title', caret };
                          setLocalObjectives(localObjectives.map(o => o.id === obj.id ? { ...o, title: value } : o));
                          requestAnimationFrame(() => {
                            const node = titleRefs.current[obj.id];
                            if (node) {
                              try { node.focus(); node.setSelectionRange(caret, caret); } catch {}
                            }
                          });
                        }}
                        placeholder={t('account.objectives.titlePh', 'Objective title')}
                        className="input"
                        style={{flex:1, padding:'.6rem', border:'1px solid var(--border-color)', borderRadius:'var(--border-radius)'}}
                      />
                      <div style={{ display:'flex', gap: '.5rem', alignItems:'center' }}>
                        <button
                          type="button"
                          className={`pop-achieve-toggle ${obj.achieved ? 'is-achieved' : ''}`}
                          aria-pressed={!!obj.achieved}
                          onClick={(e)=>{
                            e.preventDefault(); e.stopPropagation();
                            lastFocusRef.current = { id: obj.id, field: 'title', caret: (titleRefs.current[obj.id]?.selectionStart)||0 };
                            setLocalObjectives(localObjectives.map(o => o.id === obj.id ? { ...o, achieved: !o.achieved } : o));
                          }}
                        >
                          <span className="pop-achieve-check">{obj.achieved ? '✓' : '＋'}</span>
                          {obj.achieved ? t('achieved','Achieved') : t('markAchieved','Mark as achieved')}
                        </button>
                        <button className="btn small" onClick={(e) => { e.preventDefault(); e.stopPropagation(); saveObjective(obj); }} disabled={objSaving[obj.id]==='saving'} style={{background:'var(--accent-green)', color:'var(--light-text)'}}>
                          {objSaving[obj.id]==='saving' ? t('saving','Saving...') : obj.serverId ? t('save','Save') : t('create','Create')}
                        </button>
                        <button className="btn small" onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteObjective(obj); }} style={{background:'#6b7280', color:'#fff'}}>{t('account.objectives.remove', 'Remove')}</button>
                      </div>
                    </div>
                    <textarea
                      value={obj.notes}
                      ref={(el) => { if (el) notesRefs.current[obj.id] = el; }}
                      onChange={(e) => {
                        const caret = e.target.selectionStart;
                        const value = e.target.value;
                        lastFocusRef.current = { id: obj.id, field: 'notes', caret };
                        setLocalObjectives(localObjectives.map(o => o.id === obj.id ? { ...o, notes: value } : o));
                        requestAnimationFrame(() => {
                          const node = notesRefs.current[obj.id];
                          if (node) {
                            try { node.focus(); node.setSelectionRange(caret, caret); } catch {}
                          }
                        });
                      }}
                      placeholder={t('account.objectives.notesPh', 'Notes / why this matters')}
                      rows={3}
                      className="textarea"
                      style={{width:'100%', border:'1px solid var(--border-color)', borderRadius:'var(--border-radius)', padding:'.6rem', margin:'.25rem 0 .75rem'}}
                    />
                    <div className="objective-links">
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:'.75rem', borderTop:'1px solid var(--border-color)', paddingTop:'.5rem' }}>
                        <h4 style={{ margin:0 }}>{t('account.objectives.linkArticles', 'Link articles')}</h4>
                        <div className="link-filters" style={{ display:'flex', gap:'.5rem' }}>
                          <select value={linkFilterCompleted} onChange={(e)=>setLinkFilterCompleted(e.target.value)} className="select" style={{padding:'.4rem', border:'1px solid var(--border-color)', borderRadius:'var(--border-radius)'}}>
                            <option value="all">{t('all','All')}</option>
                            <option value="completed">{t('completed','Completed')}</option>
                            <option value="not">{t('notCompleted','Not completed')}</option>
                          </select>
                          <select value={linkFilterType} onChange={(e)=>setLinkFilterType(e.target.value)} className="select" style={{padding:'.4rem', border:'1px solid var(--border-color)', borderRadius:'var(--border-radius)'}}>
                            <option value="all">{t('all','All')}</option>
                            <option value="article">{t('articles','Articles')}</option>
                            <option value="youtube">YouTube</option>
                          </select>
                        </div>
                      </div>
                      <div className="articles-scroll">
                        {filteredArticles.map(a => (
                          <label key={a.id} className="article-link-item">
                            <input
                              type="checkbox"
                              checked={obj.linkedArticleIds.includes(a.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                const exists = obj.linkedArticleIds.includes(a.id);
                                const updated = localObjectives.map(o => o.id === obj.id ? { ...o, linkedArticleIds: exists ? o.linkedArticleIds.filter(id => id !== a.id) : [...o.linkedArticleIds, a.id] } : o);
                                setLocalObjectives(updated);
                              }}
                            />
                            <span>{a.title}</span>
                            {completionStatuses[a.id]?.completed && <span className="badge completed">{t('completed', 'Completed')}</span>}
                          </label>
                        ))}
                      </div>
                      {obj.linkedArticleIds.length > 0 && (
                        <div className="linked-preview">
                          <h5>{t('account.objectives.linked', 'Linked')}</h5>
                          <ul>
                            {obj.linkedArticleIds.map(id => {
                              const art = articles.find(x => x.id === id);
                              return art ? <li key={id}>{art.title}</li> : null;
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Saved objectives presentation */}
              {localObjectives.filter(o => o.collapsed).length > 0 && (
                <div className="pop-list">
                  <h3 className="pop-list-title">{t('account.objectives.listTitle', 'Your objectives')}</h3>
                  {localObjectives.filter(o => o.collapsed).map(o => {
                    const total = o.linkedArticleIds?.length || 0;
                    const done = (o.linkedArticleIds||[]).filter(id => completionStatuses?.[id]?.completed).length;
                    const cpct = total ? Math.round((done/total)*100) : 0;
                    return (
                      <div key={`present-${o.id}`} className={`pop-card ${o.achieved ? 'achieved' : ''}`}>
                        <div className="pop-card-head">
                          <div className="pop-card-title-wrap">
                            <span className="pop-card-title">{o.title || t('untitled','Untitled objective')}</span>
                            <span className={`pop-badge ${o.achieved ? 'achieved' : 'active'}`}>
                              {o.achieved ? t('achieved','Achieved') : t('active','Active')}
                            </span>
                          </div>
                          <button className="pop-edit-btn" onClick={(e)=>{ e.preventDefault(); e.stopPropagation();
                            setLocalObjectives(prev => prev.map(x => x.id === o.id ? { ...x, collapsed: false } : x));
                            setTimeout(()=>{
                              const node = titleRefs.current[o.id] || notesRefs.current[o.id];
                              if (node) { try { node.scrollIntoView({ behavior:'smooth', block:'center' }); node.focus(); } catch {} }
                            }, 0);
                          }}>{t('edit','Edit')}</button>
                        </div>
                        {o.notes && (<p className="pop-card-notes">{o.notes}</p>)}
                        {total > 0 && (
                          <div className="pop-card-progress-wrap">
                            <div className="pop-card-progress-head">
                              <span>{t('linked','Linked')}: {done}/{total} {t('completed','Completed')}</span>
                              <span className="pop-card-progress-pct">{cpct}%</span>
                            </div>
                            <div className="pop-progress sm"><div className="pop-progress-fill" style={{ width: `${cpct}%` }} /></div>
                            <ul className="pop-linked-list">
                              {(o.linkedArticleIds||[]).slice(0,5).map(id => {
                                const a = articles.find(x => x.id === id);
                                if (!a) return null;
                                const completed = !!completionStatuses?.[id]?.completed;
                                return <li key={`sum-${o.id}-${id}`} className={completed ? 'done' : ''}>{a.title}</li>;
                              })}
                              {total > 5 && <li className="pop-linked-more">+{total - 5} {t('more','more')}</li>}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="objectives-tab">
              <div className="section-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.5rem' }}>
                <h2 style={{ margin:0, display:'flex', alignItems:'center', gap:'.5rem' }}>
                  {t('account.objectives.title', 'Objectives')}
                  <span className="badge" style={{ background:'#10b981', color:'#fff', borderRadius:'999px', padding:'0.15rem 0.5rem', fontSize:'.75rem' }}>PRO</span>
                </h2>
                <button className="btn small" onClick={() => { window.location.href = '/subscription-required'; }} style={{ background:'var(--accent-green)', color:'var(--light-text)', border:'none', padding:'.35rem .8rem', borderRadius:'6px' }}>{t('upgrade','Upgrade')}</button>
              </div>
              <p className="muted" style={{ marginTop:0 }}>{t('dashboard.aiAssistant.upgradeCopy','Upgrade to Professional to create and track learning objectives and link content to your Personal Development Plan.')}</p>
            </div>
          )
          )}

          {activeTab === 'subscription' && (
            <div className="subscription-tab">
              <h2>{t('account.subscriptionInfo', 'Subscription Information')}</h2>
              
              <div className="current-plan">
                <h3>{t('account.currentPlan', 'Current Plan')}: {currentPlanInfo.name}</h3>
                {currentPlanInfo.price && <p className="plan-price">{currentPlanInfo.price}</p>}
                
                {currentPlanInfo.features.length > 0 && (
                  <div className="plan-features">
                    <h4>{t('account.planFeatures', 'Plan Features:')}</h4>
                    <ul>
                      {currentPlanInfo.features.map((feature, index) => (
                        <li key={index}>✓ {feature}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Manage Subscription Button for Active Subscribers */}
                {hasPlatformAccess(user) && (
                  <div className="manage-subscription-section">
                    <h4>{t('account.manageYourSubscription', 'Manage Your Subscription')}</h4>
                    <p>{t('account.managePortalCopy', 'Access the Stripe customer portal to manage your billing, payment methods, and subscription settings.')}</p>
                    <button 
                      className="btn-primary"
                      onClick={handleManageSubscription}
                      disabled={upgradeLoading}
                    >
                      {upgradeLoading ? t('loading', 'Loading...') : t('account.manageSubscription', 'Manage Subscription')}
                    </button>
                  </div>
                )}

                {/* Upgrade Section for Monthly Users */}
                {hasPlatformAccess(user) &&
                 (
                   user?.subscription_plan === 'Professional Monthly' ||
                   user?.subscription_plan === 'PROFESSIONAL_MONTHLY' ||
                   user?.subscription_plan === 'price_1TrMaeIkwUVtcv9MDRqJUJ0N'
                 ) && (
                  <div className="upgrade-section">
                    <h4>Upgrade to Professional</h4>
                    <p>Get access to CPD points, certificates, and tests by upgrading to our Professional plan!</p>
                    
                    <div className="upgrade-benefits">
                      <h5>What you'll get:</h5>
                      <ul className="upgrade-features">
                        <li>✓ Earn official CPD points</li>
                        <li>✓ Download accredited certificates</li>
                        <li>✓ Take AI-generated tests</li>
                        <li>✓ Track progress and certification history</li>
                        <li>✓ Priority support</li>
                      </ul>
                    </div>

                    <div className="discount-section">
                      <h5>Have a discount code?</h5>
                      <div className="discount-input-group">
                        <input
                          type="text"
                          placeholder="Enter discount code"
                          value={discountCode}
                          onChange={handleDiscountCodeChange}
                          className="discount-input"
                        />
                        <button 
                          onClick={validateDiscountCode}
                          disabled={isValidatingDiscount || !discountCode.trim()}
                          className="validate-discount-btn"
                        >
                          {isValidatingDiscount ? 'Validating...' : 'Apply'}
                        </button>
                      </div>
                      
                      {error && <div className="error-message">{error}</div>}
                      
                      {discountValidation && discountValidation.valid && (
                        <div className="discount-success">
                          <div className="discount-details">
                            <span className="discount-code" style={{
                              color: '#155724',
                              fontWeight: 600
                            }}>✓ {discountValidation.code}</span>
                            <span className="discount-amount" style={{
                              color: '#155724',
                              fontWeight: 600
                            }}>
                              Save {formatPrice(discountValidation.discount_amount)} 
                              ({discountValidation.discount_percentage.toFixed(1)}% off)
                            </span>
                          </div>
                          <div className="price-breakdown" style={{
                            backgroundColor: 'var(--card-bg)',
                            border: '1px solid #c3e6cb',
                            borderRadius: 'var(--border-radius)',
                            padding: '1rem'
                          }}>
                            <div className="price-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              marginBottom: '0.5rem',
                              color: 'var(--primary-text)'
                            }}>
                              <span>Original Price:</span>
                              <span>{formatPrice(discountValidation.original_amount)}</span>
                            </div>
                            <div className="price-row discount-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              marginBottom: '0.5rem',
                              color: '#155724'
                            }}>
                              <span>Discount ({discountValidation.code}):</span>
                              <span>-{formatPrice(discountValidation.discount_amount)}</span>
                            </div>
                            <div className="price-row final-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              fontWeight: 600,
                              borderTop: '1px solid #c3e6cb',
                              paddingTop: '0.5rem',
                              marginTop: '0.5rem',
                              color: 'var(--primary-text)'
                            }}>
                              <span>Final Price:</span>
                              <span>{formatPrice(discountValidation.final_amount)}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <button 
                      className="btn-primary"
                      onClick={handleUpgradeWithDiscount}
                      disabled={upgradeLoading}
                    >
                      {upgradeLoading ? 'Processing...' : 'Upgrade to Professional'}
                    </button>
                  </div>
                )}

                {/* General Upgrade Section for Non-Active Subscribers */}
                {!hasPlatformAccess(user) && (
                  <div className="upgrade-section">
                    <h4>Upgrade Your Account</h4>
                    <p>Get access to premium features and start earning CPD points!</p>
                    <button 
                      className="btn-primary"
                      onClick={() => setShowUpgradeModal(true)}
                    >
                      Upgrade Now
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'certificates' && (
            <div className="certificates-tab">
              <h2>{t('account.yourCertificates', 'Your Certificates')}</h2>
              
              {certificatesLoading ? (
                <div className="loading">Loading certificates...</div>
              ) : certificates.length === 0 ? (
                <div className="no-certificates">
                  <p>You haven't earned any certificates yet.</p>
                  <p>Complete articles and pass tests to earn your first certificate!</p>
                </div>
              ) : (
                <CombineCertificates certificates={certificates} API_URL={API_URL} downloadCertificateFromList={downloadCertificateFromList} />
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="history-tab">
              <h2>{t('account.tabs.history', 'Activity History')}</h2>
              
              {activityLoading ? (
                <div className="loading">{t('account.loadingActivity', 'Loading activity history...')}</div>
              ) : (() => {
                // Filter out invalid/meaningless entries
                const validActivities = activityHistory.filter(activity => {
                  // Must have a valid date
                  const hasValidDate = activity.date && 
                    !isNaN(new Date(activity.date).getTime()) && 
                    new Date(activity.date).getFullYear() > 1970;
                  
                  // Must have an article title
                  const hasArticleTitle = activity.article_title && 
                    activity.article_title.trim() !== '' && 
                    activity.article_title !== 'Invalid Date';
                  
                  // Must be a meaningful entry (not just failed without context)
                  const isMeaningful = hasValidDate && hasArticleTitle;
                  
                  return isMeaningful;
                });
                
                return validActivities.length === 0 ? (
                  <div className="no-history">
                    <p>{t('account.noActivity', 'No activity history found.')}</p>
                    <p>Start reading articles and taking tests to build your history!</p>
                  </div>
                ) : (
                  <div className="activity-list">
                    {validActivities.map((activity, index) => (
                      <div key={index} className="activity-item">
                        <div className="activity-info">
                          <h4>{activity.article_title}</h4>
                          <p className="activity-date">
                            {new Date(activity.date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                        <div className="activity-result">
                          <span className={`status ${activity.passed ? 'passed' : 'failed'}`}>
                            {activity.passed ? t('activity.passed', 'Passed') : t('activity.failed', 'Failed')}
                          </span>
                          {activity.passed && activity.pe_points && (
                        <span className="points">{activity.pe_points === 1 ? `${activity.pe_points} ${t('pePoint','PE Point')}` : `+${activity.pe_points} ${t('pePoints','PE Points')}`}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          {activeTab === 'requests' && (
            <div className="requests-tab">
              <h2>{t('account.tabs.requests', 'Request Content')}</h2>
              <p>{t('request.intro', "Can't find the content you need? Request articles or YouTube videos you'd like to see added to our platform.")}</p>
              
              {requestSuccess && <div className="success-message">{requestSuccess}</div>}
              {requestError && <div className="error-message">{requestError}</div>}
              
              <form onSubmit={handleArticleRequest} className="request-form">
                <div className="form-row">
                  <div className="form-group">
                  <label>{t('request.preferredLanguage', 'Preferred Language *')}</label>
                    <select
                      value={requestForm.language}
                      onChange={(e) => setRequestForm({...requestForm, language: e.target.value})}
                      required
                    >
                      {LANGUAGES.map(language => (
                        <option key={language} value={language}>{language}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                  <label>{t('request.professionCategory', 'Profession Category *')}</label>
                    <select
                      value={requestForm.profession_category}
                      onChange={(e) => setRequestForm({...requestForm, profession_category: e.target.value})}
                      required
                    >
                      <option value="">Select Category</option>
                      {PROFESSION_CATEGORIES.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('request.topic', 'Topic/Subject *')}</label>
                  <input
                    type="text"
                    value={requestForm.topic}
                    onChange={(e) => setRequestForm({...requestForm, topic: e.target.value})}
                    placeholder={t('request.placeholderTopic', 'e.g., Risk Management, AI in Finance, Sustainable Architecture')}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>{t('request.description', 'Detailed Description')}</label>
                  <textarea
                    value={requestForm.description}
                    onChange={(e) => setRequestForm({...requestForm, description: e.target.value})}
                    placeholder={t('request.placeholderDescription', "Please provide more details about what specific articles, sources, or content you'd like to see. Include any specific subtopics, formats, or requirements.")}
                    rows={4}
                  />
                </div>

                <button type="submit" className="submit-request-btn" disabled={requestLoading}>
                  {requestLoading ? t('request.submitting', 'Submitting Request...') : t('request.submit', 'Submit Content Request')}
                </button>
              </form>

              <div className="request-info">
                <h3>{t('request.howItWorks', 'How it works')}</h3>
                <div className="process-steps">
                  <div className="process-step">
                    <div className="step-number">1</div>
                    <div className="step-content">
                      <h4>{t('request.steps.submit', 'Submit Request')}</h4>
                      <p>{t('request.desc.submit', 'Submit your request with specific topics and details')}</p>
                    </div>
                  </div>
                  <div className="process-step">
                    <div className="step-number">2</div>
                    <div className="step-content">
                      <h4>{t('request.steps.review', 'Review Process')}</h4>
                      <p>{t('request.desc.review', 'Our content team reviews all requests carefully')}</p>
                    </div>
                  </div>
                  <div className="process-step">
                    <div className="step-number">3</div>
                    <div className="step-content">
                      <h4>{t('request.steps.prioritization', 'Prioritization')}</h4>
                      <p>{t('request.desc.prioritization', 'We prioritize requests based on user demand and relevance')}</p>
                    </div>
                  </div>
                  <div className="process-step">
                    <div className="step-number">4</div>
                    <div className="step-content">
                      <h4>{t('request.steps.addition', 'Content Addition')}</h4>
                      <p>{t('request.desc.addition', 'New articles matching requests are added regularly')}</p>
                    </div>
                  </div>
                  <div className="process-step">
                    <div className="step-number">5</div>
                    <div className="step-content">
                      <h4>{t('request.steps.notification', 'Notification')}</h4>
                      <p>{t('request.desc.notification', "You'll be notified when relevant content is available")}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <style jsx>{`
          .account-management {
            max-width: 1200px;
            margin: 0 auto;
            padding: 2rem;
            background: var(--primary-bg);
            min-height: 100vh;
          }
          .account-header {
            position: relative;
            text-align: center;
            margin-bottom: 2rem;
            padding: 2.75rem 2rem;
            border-radius: 20px;
            background:
              radial-gradient(circle at 88% -30%, rgba(255,255,255,0.18), transparent 45%),
              linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
            color: #fff;
            box-shadow: 0 18px 40px rgba(37, 99, 235, 0.28);
            overflow: hidden;
          }
          .account-header h1 {
            color: #fff;
            font-size: 2.4rem;
            font-weight: 800;
            letter-spacing: -0.02em;
            margin: 0 0 0.5rem 0;
          }
          .account-header p {
            color: rgba(255,255,255,0.9);
            font-size: 1.05rem;
            margin: 0 auto;
            max-width: 620px;
          }
          .account-tabs {
            display: flex;
            justify-content: center;
            margin-bottom: 2rem;
            border-bottom: 1px solid var(--border-color);
          }
          .tab-button {
            background: none;
            border: none;
            padding: 1rem 2rem;
            cursor: pointer;
            color: var(--secondary-text);
            font-size: 1rem;
            border-bottom: 2px solid transparent;
            transition: all 0.3s ease;
          }
          .tab-button:hover {
            color: var(--primary-text);
          }
          .tab-button.active {
            color: var(--accent-green);
            border-bottom-color: var(--accent-green);
          }
          .account-content {
            background: var(--card-bg);
            border-radius: var(--border-radius);
            padding: 2rem;
            box-shadow: var(--shadow-sm);
          }
          .muted { color: var(--secondary-text); }
          .objectives-actions { margin: 0 0 1rem; }
          .objectives-actions .btn { background: var(--accent-green); color: var(--light-text); border:none; padding:.6rem 1rem; border-radius:.25rem; cursor:pointer; }
          .objectives-actions .btn:hover { background: var(--accent-green-hover); }
          .objectives-list { display: grid; gap: 1rem; }
          .objective-card { border:1px solid var(--border-color); border-left:4px solid #2563eb; border-radius: 14px; padding:1.25rem; background: var(--primary-bg); box-shadow:0 4px 14px rgba(15,23,42,.06); }
          .objective-header { display:flex; gap:.5rem; align-items:center; margin:0 0 .5rem; }
          .objective-header input { flex:1; padding:.6rem; border:1px solid var(--border-color); border-radius:8px; }
          .objective-header input:focus, .objective-card textarea:focus { outline:none; box-shadow:0 0 0 3px rgba(37,99,235,.18); border-color:#2563eb; }
          .objective-header .btn.small { background:#6b7280; color:white; border:none; padding:.4rem .6rem; border-radius:6px; cursor:pointer; }
          .objective-header .btn.small:hover { background:#555b63; }
          .objective-card textarea { width:100%; border:1px solid var(--border-color); border-radius:8px; padding:.6rem; margin:.25rem 0 .75rem; }
          .articles-scroll { max-height:180px; overflow:auto; border:1px solid var(--border-color); border-radius:8px; padding:.5rem; background:white; }
          .article-link-item { display:flex; align-items:center; gap:.5rem; padding:.35rem .25rem; border-radius:6px; }
          .article-link-item:hover { background:#f1f5f9; }
          .badge.completed { background:#22c55e; color:white; border-radius:999px; font-size:.75rem; padding:.1rem .5rem; margin-left:auto; }

          /* POP (Personal Development Plan) */
          .pop-head { display:flex; justify-content:space-between; align-items:flex-start; gap:1rem; flex-wrap:wrap; margin-bottom:1.25rem; }
          .pop-head-text h2 { margin:0 0 .25rem; }
          .pop-head-text .muted { margin:0; max-width:640px; }
          .pop-add-btn { display:inline-flex; align-items:center; gap:.4rem; background:linear-gradient(135deg,#2563eb 0%,#1d4ed8 100%); color:#fff; border:none; padding:.6rem 1.1rem; border-radius:999px; font-weight:600; font-size:.9rem; cursor:pointer; box-shadow:0 8px 20px rgba(37,99,235,.25); transition:all .2s ease; white-space:nowrap; }
          .pop-add-btn:hover { transform:translateY(-1px); box-shadow:0 12px 26px rgba(37,99,235,.32); }
          .pop-add-plus { font-size:1.15rem; line-height:1; }

          .pop-overview { background:linear-gradient(135deg,#eff4ff 0%,#f8fafc 100%); border:1px solid #dbe4ff; border-radius:16px; padding:1.25rem 1.5rem; margin-bottom:1.5rem; }
          .pop-overview-head { display:flex; justify-content:space-between; align-items:baseline; margin-bottom:.6rem; }
          .pop-overview-title { font-weight:700; color:#1e293b; }
          .pop-overview-pct { font-weight:800; font-size:1.4rem; color:#2563eb; }
          .pop-overview-stats { display:flex; gap:2rem; margin-top:1rem; flex-wrap:wrap; }
          .pop-stat { display:flex; flex-direction:column; }
          .pop-stat-num { font-size:1.5rem; font-weight:800; color:#1e293b; line-height:1.1; }
          .pop-stat-label { font-size:.8rem; color:#64748b; text-transform:uppercase; letter-spacing:.03em; }

          .pop-progress { height:10px; border-radius:999px; background:#e2e8f0; overflow:hidden; }
          .pop-progress.sm { height:7px; }
          .pop-progress-fill { height:100%; border-radius:999px; background:linear-gradient(90deg,#2563eb,#3b82f6); transition:width .4s ease; }

          .pop-list { display:grid; gap:1rem; margin-top:1.5rem; }
          .pop-list-title { margin:0 0 .25rem; font-size:1.05rem; color:#334155; }
          .pop-card { border:1px solid var(--border-color); border-left:4px solid #2563eb; border-radius:14px; padding:1.1rem 1.25rem; background:var(--card-bg, #fff); box-shadow:0 2px 10px rgba(15,23,42,.05); transition:transform .18s ease, box-shadow .18s ease; }
          .pop-card:hover { transform:translateY(-2px); box-shadow:0 10px 24px rgba(15,23,42,.09); }
          .pop-card.achieved { border-left-color:#16a34a; }
          .pop-card-head { display:flex; justify-content:space-between; align-items:center; gap:.75rem; }
          .pop-card-title-wrap { display:flex; align-items:center; gap:.6rem; flex-wrap:wrap; }
          .pop-card-title { font-weight:700; font-size:1.05rem; color:#0f172a; }
          .pop-badge { padding:.2rem .6rem; border-radius:999px; font-size:.72rem; font-weight:700; text-transform:uppercase; letter-spacing:.03em; }
          .pop-badge.active { background:#dbeafe; color:#1d4ed8; }
          .pop-badge.achieved { background:#dcfce7; color:#15803d; }
          .pop-edit-btn { background:#eff6ff; color:#2563eb; border:1px solid #bfdbfe; padding:.35rem .8rem; border-radius:8px; font-weight:600; font-size:.85rem; cursor:pointer; transition:all .18s ease; white-space:nowrap; }
          .pop-edit-btn:hover { background:#2563eb; color:#fff; border-color:#2563eb; }
          .pop-card-notes { margin:.6rem 0 0; color:var(--secondary-text); }
          .pop-card-progress-wrap { margin-top:.9rem; }
          .pop-card-progress-head { display:flex; justify-content:space-between; font-size:.82rem; color:#64748b; margin-bottom:.35rem; }
          .pop-card-progress-pct { font-weight:700; color:#2563eb; }
          .pop-linked-list { list-style:none; margin:.6rem 0 0; padding:0; display:grid; gap:.3rem; }
          .pop-linked-list li { position:relative; padding-left:1.4rem; font-size:.9rem; color:#475569; }
          .pop-linked-list li::before { content:'○'; position:absolute; left:0; color:#94a3b8; }
          .pop-linked-list li.done { color:#15803d; }
          .pop-linked-list li.done::before { content:'✓'; color:#16a34a; font-weight:700; }
          .pop-linked-more { color:#94a3b8 !important; }
          .pop-linked-more::before { content:'' !important; }

          .pop-achieve-toggle { display:inline-flex; align-items:center; gap:.4rem; padding:.45rem .85rem; border-radius:999px; border:1.5px solid #cbd5e1; background:#fff; color:#475569; font-weight:600; font-size:.85rem; cursor:pointer; transition:all .18s ease; white-space:nowrap; }
          .pop-achieve-toggle:hover { border-color:#16a34a; color:#15803d; background:#f0fdf4; }
          .pop-achieve-toggle .pop-achieve-check { display:inline-flex; align-items:center; justify-content:center; width:18px; height:18px; border-radius:50%; background:#e2e8f0; color:#64748b; font-size:.7rem; font-weight:800; line-height:1; transition:all .18s ease; }
          .pop-achieve-toggle:hover .pop-achieve-check { background:#bbf7d0; color:#15803d; }
          .pop-achieve-toggle.is-achieved { border-color:#16a34a; background:linear-gradient(135deg,#16a34a 0%,#15803d 100%); color:#fff; box-shadow:0 6px 16px rgba(22,163,74,.28); }
          .pop-achieve-toggle.is-achieved .pop-achieve-check { background:rgba(255,255,255,.25); color:#fff; }
          .pop-achieve-toggle.is-achieved:hover { color:#fff; }
          .account-stats {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 1.5rem;
            margin-bottom: 2rem;
          }
          .stat-card {
            background: var(--primary-bg);
            padding: 1.5rem;
            border-radius: var(--border-radius);
            text-align: center;
            border: 1px solid var(--border-color);
          }
          .stat-card h3 {
            color: var(--secondary-text);
            font-size: 0.9rem;
            margin-bottom: 0.5rem;
            text-transform: uppercase;
          }
          .stat-value {
            color: var(--accent-green);
            font-size: 2rem;
            font-weight: bold;
            margin-bottom: 0.5rem;
          }
          .stat-description {
            color: var(--secondary-text);
            font-size: 0.9rem;
          }
          .welcome-section {
            text-align: center;
            padding: 2rem;
            background: var(--primary-bg);
            border-radius: var(--border-radius);
          }
          .welcome-section h2 {
            color: var(--primary-text);
            margin-bottom: 1rem;
          }
          .profile-form {
            max-width: 800px;
          }
          .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
            margin-bottom: 1rem;
          }
          .form-group {
            display: flex;
            flex-direction: column;
          }
          .form-group label {
            color: var(--primary-text);
            margin-bottom: 0.5rem;
            font-weight: 500;
          }
          .form-group input,
          .form-group select {
            padding: 0.75rem;
            border: 1px solid var(--border-color);
            border-radius: var(--border-radius);
            font-size: 1rem;
            background: var(--primary-bg);
            color: var(--primary-text);
          }
          .certificates-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 1.5rem;
          }
          .certificate-card {
            background: var(--primary-bg);
            padding: 1.5rem;
            border-radius: var(--border-radius);
            border: 1px solid var(--border-color);
            display: flex;
            flex-direction: column;
            height: 100%;
          }
          .certificate-card h4 {
            color: var(--primary-text);
            margin-bottom: 0.5rem;
          }
          .cert-info {
            color: var(--secondary-text);
            margin-bottom: 1rem;
          }
          .certificate-download-btn {
            background: var(--accent-green);
            color: var(--light-text);
            border: none;
            padding: 0.75rem 1.5rem;
            border-radius: var(--border-radius);
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
            font-size: 0.95rem;
            width: 100%;
          }
          .certificate-download-btn:hover {
            background: var(--accent-green-hover);
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(25, 135, 84, 0.2);
          }
          .activity-list {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }
          .activity-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1rem;
            background: var(--primary-bg);
            border-radius: var(--border-radius);
            border: 1px solid var(--border-color);
          }
          .activity-info h4 {
            color: var(--primary-text);
            margin-bottom: 0.25rem;
          }
          .activity-date {
            color: var(--secondary-text);
            font-size: 0.9rem;
          }
          .activity-result {
            display: flex;
            align-items: center;
            gap: 1rem;
          }
          .status {
            padding: 0.25rem 0.75rem;
            border-radius: 1rem;
            font-size: 0.8rem;
            font-weight: bold;
          }
          .status.passed {
            background: rgba(76, 175, 80, 0.1);
            color: var(--accent-green);
          }
          .status.failed {
            background: rgba(244, 67, 54, 0.1);
            color: #f44336;
          }
          .points {
            color: var(--accent-green);
            font-weight: bold;
          }
          .loading, .no-certificates, .no-history {
            text-align: center;
            padding: 2rem;
            color: var(--secondary-text);
          }
          .current-plan {
            background: var(--primary-bg);
            padding: 2rem;
            border-radius: var(--border-radius);
            border: 1px solid var(--border-color);
          }
          .plan-price {
            color: var(--accent-green);
            font-size: 1.2rem;
            font-weight: bold;
            margin-bottom: 1rem;
          }
          .plan-features ul {
            list-style: none;
            padding: 0;
          }
          .plan-features li {
            color: var(--primary-text);
            margin-bottom: 0.5rem;
          }
          .upgrade-section {
            margin-top: 2rem;
            padding-top: 2rem;
            border-top: 1px solid var(--border-color);
          }
          .manage-subscription-section {
            margin-top: 2rem;
            padding: 2rem;
            background: var(--primary-bg);
            border-radius: var(--border-radius);
            border: 1px solid var(--border-color);
          }
          .manage-subscription-section h4 {
            margin: 0 0 1rem 0;
            color: var(--primary-text);
            font-weight: 600;
          }
          .manage-subscription-section p {
            margin: 0 0 1.5rem 0;
            color: var(--secondary-text);
          }
          .upgrade-benefits {
            margin: 1.5rem 0;
          }
          .upgrade-benefits h5 {
            margin: 0 0 1rem 0;
            color: var(--primary-text);
            font-weight: 600;
          }
          .upgrade-features {
            list-style: none;
            margin: 0;
            padding: 0;
          }
          .upgrade-features li {
            color: var(--secondary-text);
            margin-bottom: 0.5rem;
            padding-left: 1rem;
          }
          .discount-section {
            margin: 2rem 0;
            padding: 1.5rem;
            background: var(--card-bg);
            border-radius: var(--border-radius);
            border: 1px solid var(--border-color);
          }
          .discount-section h5 {
            margin: 0 0 1rem 0;
            color: var(--primary-text);
            font-weight: 600;
          }
          .discount-input-group {
            display: flex;
            gap: 0.5rem;
            margin-bottom: 1rem;
          }
          .discount-input {
            flex: 1;
            padding: 0.75rem;
            border: 1px solid var(--border-color);
            border-radius: var(--border-radius);
            font-size: 1rem;
            background: var(--primary-bg);
            color: var(--primary-text);
          }
          .validate-discount-btn {
            padding: 0.75rem 1.5rem;
            background: var(--accent-green);
            color: var(--light-text);
            border: none;
            border-radius: var(--border-radius);
            cursor: pointer;
            font-weight: 600;
            transition: all 0.2s;
          }
          .validate-discount-btn:hover:not(:disabled) {
            background: var(--accent-green-hover);
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(25, 135, 84, 0.2);
          }
          .validate-discount-btn:disabled {
            background: #9ca3af;
            cursor: not-allowed;
          }
          .discount-success {
            background: var(--card-bg);
            border: 1px solid var(--accent-green);
            border-radius: var(--border-radius);
            padding: 1rem;
            margin-top: 1rem;
          }
          .discount-details {
            display: flex;
            justifyContent: space-between;
            margin-bottom: 1rem;
          }
          .discount-code {
            color: var(--accent-green);
            font-weight: 600;
          }
          .discount-amount {
            color: var(--accent-green);
            font-weight: 600;
          }
          .price-breakdown {
            border-top: 1px solid var(--border-color);
            padding-top: 1rem;
          }
          .price-row {
            display: flex;
            justifyContent: space-between;
            margin-bottom: 0.5rem;
            color: var(--primary-text);
          }
          .discount-row {
            color: var(--accent-green);
          }
          .final-row {
            font-weight: 600;
            font-size: 1.1rem;
            border-top: 1px solid var(--border-color);
            padding-top: 0.5rem;
            margin-top: 0.5rem;
          }
          .error-message {
            background: rgba(244, 67, 54, 0.1);
            border: 1px solid #f44336;
            color: #f44336;
            padding: 0.75rem;
            border-radius: var(--border-radius);
            margin-top: 0.5rem;
          }
          .success-message {
            background: rgba(76, 175, 80, 0.1);
            border: 1px solid var(--accent-green);
            color: var(--accent-green);
            padding: 0.75rem;
            border-radius: var(--border-radius);
            margin-bottom: 1.5rem;
          }
          .request-form {
            max-width: 800px;
            margin-bottom: 2rem;
          }
          .form-group textarea {
            padding: 0.75rem;
            border: 1px solid var(--border-color);
            border-radius: var(--border-radius);
            font-size: 1rem;
            background: var(--primary-bg);
            color: var(--primary-text);
            resize: vertical;
            font-family: inherit;
            min-height: 100px;
          }
          .request-info {
            background: var(--primary-bg);
            padding: 2rem;
            border-radius: var(--border-radius);
            border: 1px solid var(--border-color);
          }
          .request-info h3 {
            color: var(--primary-text);
            margin-bottom: 1rem;
            font-size: 1.3rem;
          }
          .submit-request-btn {
            background: var(--accent-green);
            color: var(--light-text);
            border: none;
            padding: 0.75rem 1.5rem;
            border-radius: var(--border-radius);
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
            font-size: 1rem;
          }
          .submit-request-btn:hover:not(:disabled) {
            background: var(--accent-green-hover);
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(25, 135, 84, 0.2);
          }
          .submit-request-btn:disabled {
            background: #9ca3af;
            cursor: not-allowed;
          }
          .process-steps {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }
          .process-step {
            display: flex;
            align-items: flex-start;
            gap: 1rem;
            padding: 1rem;
            background: var(--card-bg);
            border-radius: var(--border-radius);
            border: 1px solid var(--border-color);
          }
          .step-number {
            background: var(--accent-green);
            color: var(--light-text);
            width: 32px;
            height: 32px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 600;
            font-size: 0.9rem;
            flex-shrink: 0;
          }
          .step-content h4 {
            color: var(--primary-text);
            margin: 0 0 0.5rem 0;
            font-size: 1rem;
            font-weight: 600;
          }
          .step-content p {
            color: var(--secondary-text);
            margin: 0;
            line-height: 1.4;
            font-size: 0.95rem;
          }
          .request-info ul {
            list-style: none;
            padding: 0;
            margin: 0;
          }
          .request-info li {
            color: var(--secondary-text);
            margin-bottom: 0.75rem;
            padding-left: 0.5rem;
          }
          
          .btn-primary {
            background: var(--accent-green);
            color: var(--light-text);
            border: none;
            padding: 0.75rem 1.5rem;
            border-radius: var(--border-radius);
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
            font-size: 1rem;
            text-align: center;
            display: inline-block;
            text-decoration: none;
          }
          .btn-primary:hover:not(:disabled) {
            background: var(--accent-green-hover);
            transform: translateY(-2px);
            box-shadow: 0 6px 15px rgba(25, 135, 84, 0.25);
          }
          .btn-primary:disabled {
            background-color: #9ca3af;
            cursor: not-allowed;
            transform: none;
            box-shadow: none;
          }
          
          @media (max-width: 768px) {
            .form-row {
              grid-template-columns: 1fr;
            }
            .account-tabs {
              flex-wrap: wrap;
            }
            .tab-button {
              padding: 0.75rem 1rem;
            }
            .discount-input-group {
              flex-direction: column;
            }
            .manage-subscription-section {
              padding: 1rem;
            }
          }
        `}</style>
      </div>
    );
  });

  // Check if current URL has a confirmation token
  useEffect(() => {
    const path = window.location.pathname;
    // Add null check to prevent "Cannot read properties of undefined (reading 'match')" error
    if (path) {
    const confirmEmailMatch = path.match(/\/confirm-email\/(.+)/);
    
    if (confirmEmailMatch) {
      const token = confirmEmailMatch[1];
      navigate(`/confirm/${token}`);
      }
    }
  }, []);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="App">
        <div className="loading-container">
          <div className="loading">Checking authentication...</div>
            </div>
      </div>
    );
  }

  // Navbar for unauthenticated users (login/register pages)
  const UnauthenticatedNavbar = () => (
    <nav className="navbar">
      <div className="nav-brand" 
        onClick={() => {
          // Only allow navigation if user has subscription/admin access, or if not on subscription page
          if (hasPlatformAccess(user) || location.pathname !== '/subscription-required') {
            navigate('/');
          }
        }} 
        style={{ 
          cursor: (hasPlatformAccess(user) || location.pathname !== '/subscription-required') ? 'pointer' : 'default',
          opacity: location.pathname === '/subscription-required' && !hasPlatformAccess(user) ? 0.6 : 1
        }}
      >
        <h2>CPD Pro <span style={{fontWeight:400, color:'#ffffff', fontSize:'0.9rem', opacity:0.85, marginLeft: 8}}>by Protagon</span></h2>
      </div>
      <div className="nav-content">
        {(['/', '/home', '/abonnementen', '/waarom-cpd-pro', '/accreditatie', '/faq', '/hoe-werkt-cpd-pro'].includes(location.pathname)) && (
          <div className="landing-nav-tabs" role="navigation" aria-label={t('landingNav.ariaLabel', 'Landing sections')}>
            <button className={`landing-nav-tab ${location.pathname === '/abonnementen' ? 'active' : ''}`} onClick={() => navigate('/abonnementen')}>{t('landingNav.subscriptions', 'Subscriptions')}</button>
            <button className={`landing-nav-tab ${location.pathname === '/waarom-cpd-pro' ? 'active' : ''}`} onClick={() => navigate('/waarom-cpd-pro')}>{t('landingNav.why', 'Why CPD Pro?')}</button>
            <button className={`landing-nav-tab ${location.pathname === '/hoe-werkt-cpd-pro' ? 'active' : ''}`} onClick={() => navigate('/hoe-werkt-cpd-pro')}>{t('landingNav.howItWorks', 'How it works')}</button>
            <button className={`landing-nav-tab ${location.pathname === '/accreditatie' ? 'active' : ''}`} onClick={() => navigate('/accreditatie')}>{t('landingNav.accreditation', 'Accreditation')}</button>
            <button className={`landing-nav-tab ${location.pathname === '/faq' ? 'active' : ''}`} onClick={() => navigate('/faq')}>{t('landingNav.faq', 'FAQ')}</button>
          </div>
        )}
        <div className="nav-user-actions" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div className="lang-switcher">
            <button onClick={() => i18n.changeLanguage('en')} className={`lang-btn ${i18n.language === 'en' ? 'active' : ''}`} aria-label="Switch to English">🇬🇧</button>
            <button onClick={() => i18n.changeLanguage('nl')} className={`lang-btn ${i18n.language === 'nl' ? 'active' : ''}`} aria-label="Wissel naar Nederlands">🇳🇱</button>
          </div>
          <div className="auth-nav-actions">
            <button className="nav-btn" onClick={() => navigate('/login')}>{t('lp.cta.signIn', 'Sign in')}</button>
            <button className="nav-btn primary" onClick={() => navigate(`/register?lang=${i18n.language}`)}>{t('auth.register', 'Register')}</button>
          </div>
        </div>
      </div>
    </nav>
  );

  // Show login if not authenticated
  if (!isAuthenticated) {
    return (
      <div className={`App ${ (location.pathname === '/login' || location.pathname === '/register') ? 'auth-dark' : '' }`}>
        {(location.pathname === '/login' || location.pathname === '/register' || ['/', '/home', '/abonnementen', '/waarom-cpd-pro', '/hoe-werkt-cpd-pro', '/accreditatie', '/faq'].includes(location.pathname)) && <UnauthenticatedNavbar />}

        <main className="main-content">
          {location.pathname === '/login' && <LoginForm />}
          {location.pathname === '/register' && <RegisterForm />}
          {(['/', '/home', '/abonnementen', '/waarom-cpd-pro', '/hoe-werkt-cpd-pro', '/accreditatie', '/faq'].includes(location.pathname)) && (isAuthenticated ? <UserDashboard /> : <CpdLandingPage />)}
          {location.pathname.startsWith('/confirm/') && <EmailConfirmation token={params.token} />}
          {location.pathname.startsWith('/reset-password/') && <PasswordReset />}
          {/* Certificate QR codes are scanned by reviewers who have no account. */}
          {location.pathname === '/verify' && <VerifyCertificate />}
          {location.pathname === '/subscription-required' && <SubscriptionRequired />}
          {location.pathname === '/subscription-success' && <SubscriptionSuccess />}
          {location.pathname === '/subscription-cancelled' && <SubscriptionCancelled />}
        </main>
      </div>
    );
  }
  // Return authenticated view
  return (
    <div className={`App ${ (location.pathname === '/login' || location.pathname === '/register') ? 'auth-dark' : '' }`}>
      {/* Only show navbar for authenticated users or login/register pages */}
      {(isAuthenticated || location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/home') && (
        <nav className="navbar">
          <div className="nav-brand" 
            onClick={() => {
              // Only allow navigation if user has subscription/admin access, or if not on subscription page
              if (hasPlatformAccess(user) || location.pathname !== '/subscription-required') {
                navigate('/');
              }
            }} 
            style={{ 
              cursor: (hasPlatformAccess(user) || location.pathname !== '/subscription-required') ? 'pointer' : 'default',
              opacity: location.pathname === '/subscription-required' && !hasPlatformAccess(user) ? 0.6 : 1
            }}
          >
            <h2>CPD Pro <span style={{fontWeight:400, color:'#ffffff', fontSize:'0.9rem', opacity:0.85, marginLeft: 8}}>by Protagon</span></h2>
          </div>
          {isAuthenticated && (
            <div className="nav-content">
              {/* Only show navigation tabs if user has active subscription or is admin AND not on subscription-required page */}
              {hasPlatformAccess(user) && location.pathname !== '/subscription-required' && location.pathname !== '/home' && (
              <div className="nav-tabs">
                {/* Accredited tab removed per request */}
                <button 
                  className={`nav-tab ${location.pathname === '/articles' || location.pathname.startsWith('/article') ? 'active' : ''}`}
                  onClick={() => navigate('/articles')}
                >
                  {t('nav.library', 'Library')}
                </button>
                <button 
                  className={`nav-tab ${location.pathname === '/news' ? 'active' : ''}`}
                  onClick={() => navigate('/news')}
                >
                  {t('nav.news', 'News')}
                </button>
                <button 
                  className={`nav-tab ${location.pathname === '/help' ? 'active' : ''}`}
                  onClick={() => navigate('/help')}
                >
                  {t('nav.help', 'Help Center')}
                </button>
              </div>
              )}
              <div className="nav-user-actions" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div className="lang-switcher">
                  <button onClick={() => i18n.changeLanguage('en')} className={`lang-btn ${i18n.language === 'en' ? 'active' : ''}`} aria-label="Switch to English" title="English">🇬🇧</button>
                  <button onClick={() => i18n.changeLanguage('nl')} className={`lang-btn ${i18n.language === 'nl' ? 'active' : ''}`} aria-label="Wissel naar Nederlands" title="Nederlands">🇳🇱</button>
                </div>
                <div className="user-greeting" 
                  onClick={() => {
                    // Only allow navigation if user has subscription/admin access, or if not on subscription page
                    if (hasPlatformAccess(user) || location.pathname !== '/subscription-required') {
                      navigate('/account');
                    }
                  }}
                  style={{
                    cursor: (hasPlatformAccess(user) || location.pathname !== '/subscription-required') ? 'pointer' : 'default',
                    opacity: location.pathname === '/subscription-required' && !hasPlatformAccess(user) ? 0.6 : 1
                  }}
                >
                    <span className="user-avatar">{user?.first_name?.charAt(0)}</span>
                    <span className="user-name">{user?.first_name}</span>
                    {/* Debug: log user data */}
                    {console.log('Displaying user in navbar:', user)}
                </div>
                <button className="btn-logout" onClick={handleLogout}>
                  {t('auth.logout', 'Logout')}
                </button>
              </div>
            </div>
          )}
          {!isAuthenticated && (location.pathname === '/login' || location.pathname === '/register') && (
            <div className="nav-content">
              {/* Removed auth-nav-actions with Login and Sign Up buttons */}
            </div>
          )}
        </nav>
      )}

      <main className="main-content">
        {location.pathname === '/' && (
          isAuthenticated ? <UserDashboard /> : null // Show nothing or a loader, redirection is handled in useEffect
        )}
        {location.pathname === '/login' && <LoginForm />}
        {location.pathname === '/register' && <RegisterForm />}
        
        {/* Protected routes - only accessible with active subscription or admin access */}
        {location.pathname === '/articles' && (
          hasPlatformAccess(user) ? (
            <>
              {console.log('🏠 Rendering Articles component with:', articles?.length, 'articles, loading:', loading)}
              <Articles 
            articles={articles}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
            completionStatuses={completionStatuses}
            handleReadArticle={handleReadArticle}
            startTest={startTest}
            testLoading={testLoading}
            loading={loading}
            LANGUAGES={LANGUAGES}
            selectedSource={selectedSource}
            setSelectedSource={setSelectedSource}
            selectedSort={selectedSort}
            setSelectedSort={setSelectedSort}
              />
            </>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <h2>Subscription Required</h2>
              <p>You need an active subscription to access articles.</p>
              <button onClick={() => navigate('/subscription-required')} className="btn-primary">
                Choose a Plan
              </button>
            </div>
          )
        )}
        
        {location.pathname === '/news' && (
          hasPlatformAccess(user) ? (
            <NewsUpdatesPage 
          newsItems={newsItems}
          goBack={() => navigate('/')}
            />
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <h2>Subscription Required</h2>
              <p>You need an active subscription to access news.</p>
              <button onClick={() => navigate('/subscription-required')} className="btn-primary">
                Choose a Plan
              </button>
            </div>
          )
        )}
        
        {location.pathname.startsWith('/article/') && (
          hasPlatformAccess(user) ? (
            selectedArticle && <ArticleReader 
          selectedArticle={selectedArticle}
          goBack={goBack}
          completionStatuses={completionStatuses}
          startTest={startTest}
          testLoading={testLoading}
          downloadCertificateFromList={downloadCertificateFromList}
              user={user}
              canUserTakeTests={canUserTakeTests}
              monthlyAllowance={monthlyAllowance}
            />
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <h2>Subscription Required</h2>
              <p>You need an active subscription to read articles.</p>
              <button onClick={() => navigate('/subscription-required')} className="btn-primary">
                Choose a Plan
              </button>
            </div>
          )
        )}
        
        {location.pathname === '/account' && (
          <AccountManagementView
            user={user}
            setUser={setUser}
            articles={articles}
            completionStatuses={completionStatuses}
            API_URL={API_URL}
            objectives={objectives}
            setObjectives={setObjectives}
          />
        )}
        

        {location.pathname === '/help' && (
          hasPlatformAccess(user) ? (
            <Help />
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <h2>Subscription Required</h2>
              <p>You need an active subscription to access the help center.</p>
              <button onClick={() => navigate('/subscription-required')} className="btn-primary">
                Choose a Plan
              </button>
            </div>
          )
        )}

        {/* Accredited page removed per request */}
        
        {location.pathname.startsWith('/test/') && (
          hasPlatformAccess(user) ? (
            testQuestions.length > 0 && (
          <TestInterface
            selectedArticle={selectedArticle}
            testQuestions={testQuestions}
            currentQuestionIndex={currentQuestionIndex}
            setCurrentQuestionIndex={setCurrentQuestionIndex}
            selectedAnswers={selectedAnswers}
            setSelectedAnswers={setSelectedAnswers}
            testResults={testResults}
            setTestResults={setTestResults}
            goBack={goBack}
            downloadCertificate={downloadCertificate}
            fetchCompletionData={fetchCompletionData}
            user={user}
                API_BASE_URL={API_BASE_URL}
              />
            )
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <h2>Subscription Required</h2>
              <p>You need an active subscription to take tests.</p>
              <button onClick={() => navigate('/subscription-required')} className="btn-primary">
                Choose a Plan
              </button>
            </div>
          )
        )}
        
        {location.pathname.startsWith('/confirm/') && <EmailConfirmation token={params.token} />}
        {location.pathname === '/subscription-required' && <SubscriptionRequired />}
        {location.pathname === '/subscription-success' && <SubscriptionSuccess />}
        {location.pathname === '/subscription-cancelled' && <SubscriptionCancelled />}
        {location.pathname.startsWith('/reset-password/') && <PasswordReset />}
        {(['/home', '/abonnementen', '/waarom-cpd-pro', '/hoe-werkt-cpd-pro', '/accreditatie', '/faq'].includes(location.pathname)) && <CpdLandingPage />}
        {location.pathname === '/verify' && <VerifyCertificate />}
      </main>
      {/* Accredited footer: show only on dashboard page */}
      {location.pathname === '/' && (
        <div style={{ width: '100%', padding: '1rem 0', textAlign: 'center' }}>
          <div style={{ color: 'var(--secondary-text)', marginBottom: '0.25rem' }}>Accredited by</div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem' }}>
            <a href="https://www.nirv.nl" target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center' }}>
              <img src="/nirv-logo.png" alt="NiRV" style={{ height: 28, width: 'auto' }} />
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
// Main App with Router
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<MainApp />} />
        <Route path="/login" element={<MainApp />} />
        <Route path="/register" element={<MainApp />} />
        <Route path="/articles" element={<MainApp />} />
        <Route path="/article/:id" element={<MainApp />} />
        <Route path="/test/:id" element={<MainApp />} />
        <Route path="/account" element={<MainApp />} />
        <Route path="/help" element={<MainApp />} />
        <Route path="/confirm/:token" element={<MainApp />} />
        <Route path="/confirm-email/:token" element={<MainApp />} />
        <Route path="/news" element={<MainApp />} />
        <Route path="/subscription-required" element={<MainApp />} />
        <Route path="/subscription-success" element={<MainApp />} />
        <Route path="/subscription-cancelled" element={<MainApp />} />
        <Route path="/reset-password/:token" element={<MainApp />} />
        <Route path="/pricing" element={<MainApp />} />
        <Route path="/home" element={<MainApp />} />
        <Route path="/abonnementen" element={<MainApp />} />
        <Route path="/waarom-cpd-pro" element={<MainApp />} />
        <Route path="/hoe-werkt-cpd-pro" element={<MainApp />} />
        <Route path="/accreditatie" element={<MainApp />} />
        <Route path="/faq" element={<MainApp />} />
        <Route path="/verify" element={<MainApp />} />
      </Routes>
    </Router>
  );
}

export default App;